import { createContext, useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
const API_BASE = process.env.REACT_APP_API_URL || "https://api.jaynith.store";
const AUTH_KEY = "loantrack_token";
const REFRESH_KEY = "loantrack_refresh_token";
const USER_KEY = "loantrack_user";
const TAMIL_KEY = "loantrack_tamil_subtitles";
const BRAND_LOGO = `${process.env.PUBLIC_URL}/jayfin-logo.svg`;

//  HELPERS 
const fmt      = n => `Rs ${Number(n).toLocaleString("en-IN")}`;
const initials = n => String(n || "").split(" ").filter(Boolean).map(w=>w[0]).join("").slice(0,2).toUpperCase();
const PIE_COLORS = ["#2ecc71","#f5a623","#e74c3c"];
const upper = v => String(v || "").toUpperCase();
const isAdminRole = role => upper(role) === "ADMIN";
const isCollectorRole = role => upper(role) === "COLLECTOR";

// API GET caching (for offline read)
const API_CACHE_PREFIX = "LT:apiCache:v1:";
const apiCacheKey = (method, url) => {
  let key = "";
  try {
    const u = new URL(url, window.location.origin);
    key = `${u.pathname}${u.search}`;
  } catch (_) {
    key = String(url || "");
  }
  return `${API_CACHE_PREFIX}${method}:${key}`;
};
const readApiCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};
const writeApiCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data })); } catch (_) {}
};
const isNetworkishError = (err) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const msg = String(err?.message || "");
  return err?.name === "TypeError" || /failed to fetch|networkerror|load failed|fetch/i.test(msg);
};

// Voice -> text helper (tries native/plugin where available; falls back to manual input)
const startVoiceCapture = ({
  lang = "ta-IN",
  onText,
  onError,
  onEnd,
  fallbackLabel = "Type name",
  fallbackWhenOffline = true,
  fallbackTimeoutMs = 9000,
} = {}) => {
  let ended = false;
  let didFallback = false;
  let timeoutId = null;

  const finish = () => {
    if (ended) return;
    ended = true;
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    onEnd?.();
  };

  const cleanText = (value) => String(value || "")
    .replace(/[^0-9a-zA-Z\u0B80-\u0BFF\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const promptFallback = () => {
    if (didFallback) return;
    didFallback = true;
    try {
      const typed = window.prompt(fallbackLabel, "");
      const cleaned = cleanText(typed);
      if (cleaned) onText?.(cleaned);
    } catch (e) {
      onError?.(e);
    } finally {
      finish();
    }
  };

  const offline = typeof navigator !== "undefined" && navigator.onLine === false;

  // Capacitor-native SpeechRecognition plugin (if present in native build)
  const capPlugin = window?.Capacitor?.Plugins?.SpeechRecognition;
  if (capPlugin && typeof capPlugin.start === "function") {
    let stopped = false;
    (async () => {
      try {
        const hasPerm = typeof capPlugin.hasPermission === "function" ? await capPlugin.hasPermission() : { permission: true };
        const ok = hasPerm?.permission !== false;
        if (!ok && typeof capPlugin.requestPermission === "function") await capPlugin.requestPermission();

        const res = await capPlugin.start({ language: lang, partialResults: false, maxResults: 1 });
        if (stopped) return;
        const match = Array.isArray(res?.matches) ? res.matches[0] : (res?.match || res?.value || "");
        const cleaned = cleanText(match);
        if (cleaned) onText?.(cleaned);
      } catch (e) {
        if (offline && fallbackWhenOffline) promptFallback();
        else { onError?.(e); finish(); }
        return;
      }
      finish();
    })();
    return { stop: () => { stopped = true; try { capPlugin.stop?.(); } catch (_) {} finish(); } };
  }

  // Web Speech API is typically network-backed; if offline, fall back immediately.
  if (offline && fallbackWhenOffline) {
    promptFallback();
    return { stop: () => {} };
  }

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    promptFallback();
    return { stop: () => {} };
  }

  const rec = new SR();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = false;
  rec.maxAlternatives = 1;

  let gotResult = false;
  rec.onresult = (e) => {
    gotResult = true;
    const transcript = Array.from(e.results || [])
      .map(r => r?.[0]?.transcript || "")
      .join(" ")
      .trim();
    const cleaned = cleanText(transcript);
    if (cleaned) onText?.(cleaned);
  };
  rec.onerror = (e) => {
    const errCode = String(e?.error || "");
    const fallbackCodes = new Set(["network", "aborted", "no-speech", "audio-capture", "not-allowed", "service-not-allowed"]);
    const shouldFallback = (offline || fallbackCodes.has(errCode)) && fallbackWhenOffline;
    if (shouldFallback) promptFallback();
    else onError?.(e);
  };
  rec.onend = () => finish();

  if ((offline && fallbackWhenOffline) && fallbackTimeoutMs > 0) {
    timeoutId = setTimeout(() => {
      if (!gotResult) {
        try { rec.stop(); } catch (_) {}
        promptFallback();
      }
    }, fallbackTimeoutMs);
  }

  try {
    rec.start();
  } catch (e) {
    if (offline && fallbackWhenOffline) promptFallback();
    else { onError?.(e); finish(); }
  }
  return { stop: () => { try { rec.stop(); } catch (_) {} finish(); } };
};

//  FUZZY NAME MATCHING (used for mic filters)
const normSearch = (value) => {
  const s = String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return s.replace(/[^0-9a-z\u0B80-\u0BFF\s]/g, " ").replace(/\s+/g, " ").trim();
};

const levenshteinBounded = (aRaw, bRaw, maxDist) => {
  const a = String(aRaw || "");
  const b = String(bRaw || "");
  const al = a.length;
  const bl = b.length;
  if (!al) return bl <= maxDist ? bl : null;
  if (!bl) return al <= maxDist ? al : null;
  if (Math.abs(al - bl) > maxDist) return null;

  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;

  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= bl; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      const v = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > maxDist) return null;
    const tmp = prev; prev = curr; curr = tmp;
  }
  return prev[bl] <= maxDist ? prev[bl] : null;
};

const nameMatchScore = (query, fullName) => {
  const q = normSearch(query);
  if (!q) return { ok: true, score: 0, exact: true };

  const qTokens = q.split(" ").filter(Boolean);
  const raw = String(fullName || "");
  const bn = splitBilingual(raw);
  const hay = normSearch(`${raw} ${bn.en || ""} ${bn.ta || ""}`);
  const candTokens = hay.split(" ").filter(Boolean);

  if (!candTokens.length) return { ok: false, score: Infinity, exact: false };
  if (hay.includes(q)) return { ok: true, score: 0, exact: true };

  let total = 0;
  for (const qt of qTokens) {
    if (!qt) continue;
    const short = qt.length <= 2;
    let best = Infinity;

    for (const ct of candTokens) {
      if (!ct) continue;
      if (ct.includes(qt)) { best = 0; break; }
      if (short && ct.startsWith(qt)) { best = 1; break; }
      if (!short) {
        const maxDist = qt.length <= 4 ? 1 : 2;
        const d = levenshteinBounded(qt, ct, maxDist);
        if (d !== null && d < best) best = d;
      }
    }

    const allowed = short ? 1 : (qt.length <= 4 ? 1 : 2);
    if (best === Infinity || best > allowed) return { ok: false, score: Infinity, exact: false };
    total += best;
  }

  return { ok: true, score: total, exact: false };
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(!!e.matches);
    setMatches(mql.matches);
    try {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    } catch (_) {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [query]);

  return matches;
};

//  SORTING (type-aware)
const sortCompare = (a, b, type = "text") => {
  const isEmpty = v => v == null || (typeof v === "string" && v.trim() === "") || (typeof v === "number" && Number.isNaN(v));

  const normNumber = v => {
    if (typeof v === "number") return v;
    if (typeof v === "bigint") return Number(v);
    if (typeof v !== "string") return Number(v);
    const cleaned = v.replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    return Number.isNaN(n) ? NaN : n;
  };

  const normDate = v => {
    if (v instanceof Date) return v.getTime();
    if (typeof v === "number") return v;
    const t = Date.parse(String(v || ""));
    return Number.isNaN(t) ? NaN : t;
  };

  const normTime = v => {
    if (typeof v === "number") return v;
    const s = String(v || "");
    const m = s.match(/^\s*(\d{1,2}):(\d{2})/);
    if (!m) return NaN;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    return hh * 60 + mm;
  };

  const normText = v => String(v ?? "");

  let av = a;
  let bv = b;
  if (type === "number") {
    av = normNumber(a);
    bv = normNumber(b);
  } else if (type === "date") {
    av = normDate(a);
    bv = normDate(b);
  } else if (type === "time") {
    av = normTime(a);
    bv = normTime(b);
  } else if (type === "bool") {
    av = a ? 1 : 0;
    bv = b ? 1 : 0;
  } else {
    av = normText(a);
    bv = normText(b);
  }

  const aEmpty = isEmpty(av);
  const bEmpty = isEmpty(bv);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (type === "text") {
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
  }
  if (type === "number" || type === "date" || type === "time" || type === "bool") {
    return av === bv ? 0 : av < bv ? -1 : 1;
  }
  return 0;
};

const stableSort = (items, cmp) =>
  items
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const c = cmp(a.item, b.item);
      return c !== 0 ? c : a.idx - b.idx;
    })
    .map(x => x.item);

const sortRows = (items, sort, getValue, typeByKey = {}) => {
  if (!sort?.key) return items;
  const { key, dir } = sort;
  const factor = dir === "desc" ? -1 : 1;
  const type = typeByKey[key] || "text";
  return stableSort(items, (a, b) => factor * sortCompare(getValue(a, key), getValue(b, key), type));
};

const useSortableHeader = ({ tableRef, columns, sort, setSort }) => {
  const onTableClick = useCallback(
    (e) => {
      const th = e?.target?.closest?.("th");
      if (!th) return;
      if (!th.closest("thead")) return;
      const idx = th.cellIndex;
      const col = columns[idx];
      if (!col?.key) return;
      setSort(s => ({
        key: col.key,
        dir: s?.key === col.key && s?.dir === "asc" ? "desc" : "asc",
      }));
    },
    [columns, setSort]
  );

  useEffect(() => {
    const table = tableRef?.current;
    if (!table) return;
    const ths = table.querySelectorAll("thead th");
    ths.forEach((th, idx) => {
      const col = columns[idx];
      if (!col?.key) {
        th.classList.remove("srt-th");
        th.removeAttribute("data-sort-active");
        return;
      }
      th.classList.add("srt-th");
      const active = sort?.key === col.key ? sort?.dir : "";
      if (active) th.setAttribute("data-sort-active", active);
      else th.removeAttribute("data-sort-active");
    });
  }, [columns, sort, tableRef]);

  return onTableClick;
};
const normalizeMessage = v => {
  if (v instanceof Error) return v.message || "Error";
  if (typeof v === "string") return v;
  if (v == null) return "";
  if (Array.isArray(v)) {
    const first = v[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object" && typeof first.msg === "string") return first.msg;
    try { return JSON.stringify(first); } catch (_) { return String(first); }
  }
  if (typeof v === "object") {
    if (typeof v.detail === "string") return v.detail;
    if (Array.isArray(v.detail)) return normalizeMessage(v.detail);
    if (typeof v.message === "string") return v.message;
    try { return JSON.stringify(v); } catch (_) { return String(v); }
  }
  return String(v);
};

//  UI LANGUAGE (Tamil subtitles)
const UIContext = createContext({ showTamil: false, setShowTamil: () => {} });
const useUI = () => useContext(UIContext);
const TA2 = {   Overview: "\u0BAE\u0BC7\u0BB2\u0BCB\u0B9F\u0BCD\u0B9F\u0BAE\u0BCD",   Management: "\u0BA8\u0BBF\u0BB0\u0BCD\u0BB5\u0BBE\u0B95\u0BAE\u0BCD",   Reports: "\u0B85\u0BB1\u0BBF\u0B95\u0BCD\u0B95\u0BC8\u0B95\u0BB3\u0BCD",   Mobile: "\u0BAE\u0BCA\u0BAA\u0BC8\u0BB2\u0BCD",   Dashboard: "\u0B9F\u0BBE\u0BB7\u0BCD\u0BAA\u0BCB\u0BB0\u0BCD\u0B9F\u0BC1",   Collectors: "\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BCD\u0B95\u0BB3\u0BCD",   Borrowers: "\u0B95\u0B9F\u0BA9\u0BCD\u0020\u0BAA\u0BC6\u0BB1\u0BC1\u0BB5\u0BCB\u0BB0\u0BCD",   Loans: "\u0B95\u0B9F\u0BA9\u0BCD\u0B95\u0BB3\u0BCD",   "Collector App": "\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BCD\u0020\u0B9A\u0BC6\u0BAF\u0BB2\u0BBF",   "Sign out": "\u0BB5\u0BC6\u0BB3\u0BBF\u0BAF\u0BC7\u0BB1\u0BC1",   "MOBILE PREVIEW": "\u0BAE\u0BCA\u0BAA\u0BC8\u0BB2\u0BCD\u0020\u0BAE\u0BC1\u0BA9\u0BCD\u0BA9\u0BCB\u0B9F\u0BCD\u0B9F\u0BAE\u0BCD",   "TODAY'S ROUTE": "\u0B87\u0BA9\u0BCD\u0BB1\u0BC8\u0BAF\u0020\u0BAA\u0BBE\u0BA4\u0BC8",   "ADD COLLECTOR": "\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BC8\u0020\u0B9A\u0BC7\u0BB0\u0BCD\u0B95\u0BCD\u0B95",   "ADD BORROWER": "\u0B95\u0B9F\u0BA9\u0BCD\u0020\u0BAA\u0BC6\u0BB1\u0BC1\u0BB5\u0BCB\u0BB0\u0BCD\u0020\u0B9A\u0BC7\u0BB0\u0BCD\u0B95\u0BCD\u0B95",   "CREATE LOAN": "\u0B95\u0B9F\u0BA9\u0BCD\u0020\u0B89\u0BB0\u0BC1\u0BB5\u0BBE\u0B95\u0BCD\u0B95\u0BC1",   "PAYMENTS & REPORTS": "\u0BAA\u0BA3\u0BAE\u0BCD\u0020\u0026\u0020\u0B85\u0BB1\u0BBF\u0B95\u0BCD\u0B95\u0BC8\u0B95\u0BB3\u0BCD",   "Today's Collection": "\u0B87\u0BA9\u0BCD\u0BB1\u0BC8\u0BAF\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BCD",   "Active Loans": "\u0B9A\u0BC6\u0BAF\u0BB2\u0BBF\u0BB2\u0BCD\u0020\u0B89\u0BB3\u0BCD\u0BB3\u0020\u0B95\u0B9F\u0BA9\u0BCD\u0B95\u0BB3\u0BCD",   "Collectors Active": "\u0B9A\u0BC6\u0BAF\u0BB2\u0BBF\u0BB2\u0BCD\u0020\u0B89\u0BB3\u0BCD\u0BB3\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BCD\u0B95\u0BB3\u0BCD",   "Overdue Accounts": "\u0BA4\u0BBE\u0BAE\u0BA4\u0020\u0B95\u0BA3\u0B95\u0BCD\u0B95\u0BC1\u0B95\u0BB3\u0BCD",   "Weekly Collections": "\u0BB5\u0BBE\u0BB0\u0BBE\u0BA8\u0BCD\u0BA4\u0BBF\u0BB0\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BCD\u0B95\u0BB3\u0BCD",   "Today's Status": "\u0B87\u0BA9\u0BCD\u0BB1\u0BC8\u0BAF\u0020\u0BA8\u0BBF\u0BB2\u0BC8",   "Total Collected": "\u0BAE\u0BCA\u0BA4\u0BCD\u0BA4\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BCD",   "Paid / Partial / Unpaid": "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BAA\u0BCD\u0BAA\u0B9F\u0BCD\u0B9F\u0BA4\u0BC1\u0020\u002F\u0020\u0BAA\u0B95\u0BC1\u0BA4\u0BBF\u0020\u002F\u0020\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8",   Paid: "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BAA\u0BCD\u0BAA\u0B9F\u0BCD\u0B9F\u0BA4\u0BC1",   Partial: "\u0BAA\u0B95\u0BC1\u0BA4\u0BBF",   Unpaid: "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8",   PAID: "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BAA\u0BCD\u0BAA\u0B9F\u0BCD\u0B9F\u0BA4\u0BC1",   PARTIAL: "\u0BAA\u0B95\u0BC1\u0BA4\u0BBF",   UNPAID: "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8",   "NOT PAID": "\u0B9A\u0BC6\u0BB2\u0BC1\u0BA4\u0BCD\u0BA4\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8",   "Filter by address": "\u0BAE\u0BC1\u0B95\u0BB5\u0BB0\u0BBF\u0020\u0BAE\u0BC2\u0BB2\u0BAE\u0BCD\u0020\u0BB5\u0B9F\u0BBF\u0B95\u0B9F\u0BCD\u0B9F\u0BC1",   "All addresses": "\u0B85\u0BA9\u0BC8\u0BA4\u0BCD\u0BA4\u0BC1\u0020\u0BAE\u0BC1\u0B95\u0BB5\u0BB0\u0BBF\u0B95\u0BB3\u0BCD",   Address: "\u0BAE\u0BC1\u0B95\u0BB5\u0BB0\u0BBF",   Home: "\u0BAE\u0BC1\u0B95\u0BAA\u0BCD\u0BAA\u0BC1",   Export: "\u0B8F\u0BB1\u0BCD\u0BB1\u0BC1\u0BAE\u0BA4\u0BBF",   "Export Excel": "\u0B8E\u0B95\u0BCD\u0B9A\u0BC6\u0BB2\u0BCD\u0020\u0B8F\u0BB1\u0BCD\u0BB1\u0BC1\u0BAE\u0BA4\u0BBF",   "Exporting...": "\u0B8F\u0BB1\u0BCD\u0BB1\u0BC1\u0BAE\u0BA4\u0BBF\u002E\u002E\u002E",   "Voice Entry": "\u0B95\u0BC1\u0BB0\u0BB2\u0BCD\u0020\u0BAA\u0BA4\u0BBF\u0BB5\u0BC1",   "Scan Image": "\u0BAA\u0B9F\u0BAE\u0BCD\u0020\u0BB8\u0BCD\u0B95\u0BC7\u0BA9\u0BCD",   "FIELD AGENTS": "\u0B95\u0BB3\u0020\u0BAA\u0BA3\u0BBF\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD\u0B95\u0BB3\u0BCD",   Cancel: "\u0BB0\u0BA4\u0BCD\u0BA4\u0BC1",   "Collector Comparison": "\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BCD\u0020\u0B92\u0BAA\u0BCD\u0BAA\u0BC0\u0B9F\u0BC1",   "All Collectors": "\u0B85\u0BA9\u0BC8\u0BA4\u0BCD\u0BA4\u0BC1\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BBE\u0BB3\u0BB0\u0BCD\u0B95\u0BB3\u0BCD",   Total: "\u0BAE\u0BCA\u0BA4\u0BCD\u0BA4\u0BAE\u0BCD",   Active: "\u0B9A\u0BC6\u0BAF\u0BB2\u0BBF\u0BB2\u0BCD",   Inactive: "\u0B9A\u0BC6\u0BAF\u0BB2\u0BB1\u0BCD\u0BB1",   "All Borrowers": "\u0B85\u0BA9\u0BC8\u0BA4\u0BCD\u0BA4\u0BC1\u0020\u0B95\u0B9F\u0BA9\u0BCD\u0020\u0BAA\u0BC6\u0BB1\u0BC1\u0BB5\u0BCB\u0BB0\u0BCD",   "Recovery Progress (Stacked)": "\u0BAE\u0BC0\u0B9F\u0BCD\u0BAA\u0BC1\u0020\u0BAE\u0BC1\u0BA9\u0BCD\u0BA9\u0BC7\u0BB1\u0BCD\u0BB1\u0BAE\u0BCD\u0020\u0028\u0B85\u0B9F\u0BC1\u0B95\u0BCD\u0B95\u0BC1\u0B95\u0BB3\u0BCD\u0029",   Principal: "\u0BAE\u0BC2\u0BB2\u0BA4\u0BCD\u0020\u0BA4\u0BCA\u0B95\u0BC8",   Outstanding: "\u0BAA\u0BBE\u0B95\u0BCD\u0B95\u0BBF",   "Collection Trend (Last 30 Days)": "\u0BB5\u0B9A\u0BC2\u0BB2\u0BCD\u0020\u0BAA\u0BCB\u0B95\u0BCD\u0B95\u0BC1\u0020\u0028\u0B95\u0B9F\u0BC8\u0B9A\u0BBF\u0020\u0033\u0030\u0020\u0BA8\u0BBE\u0B9F\u0BCD\u0B95\u0BB3\u0BCD\u0029",   "Summary Today": "\u0B87\u0BA9\u0BCD\u0BB1\u0BC8\u0BAF\u0020\u0B9A\u0BC1\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BAE\u0BCD",   Edit: "\u0BA4\u0BBF\u0BB0\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1",   "Full Collection Log": "\u0BAE\u0BC1\u0BB4\u0BC1\u0020\u0BB5\u0B9A\u0BC2\u0BB2\u0BCD\u0020\u0BAA\u0BA4\u0BBF\u0BB5\u0BC1",   "All Loans": "\u0B85\u0BA9\u0BC8\u0BA4\u0BCD\u0BA4\u0BC1\u0020\u0B95\u0B9F\u0BA9\u0BCD\u0B95\u0BB3\u0BCD",   Statement: "\u0B95\u0BA3\u0B95\u0BCD\u0B95\u0BC1\u0020\u0B85\u0BB1\u0BBF\u0B95\u0BCD\u0B95\u0BC8", };

function Bi({ en, ta, className, style }) {
  const { showTamil } = useUI();
  const fixedTa = repairUtf8Mojibake(typeof ta === "string" ? ta : "");
  const taOk = fixedTa && /[\u0B80-\u0BFF]/.test(fixedTa) ? fixedTa : null;
  const tamil = taOk ?? TA2[en];
  if (!showTamil || !tamil) return <span className={className} style={style}>{en}</span>;
  return (
    <span className={`bi ${className || ""}`} style={style}>
      <span>{en}</span>
      <span className="ta">{tamil}</span>
    </span>
  );
}

function StatusIcon({ type }) {
  const common = { viewBox: "0 0 24 24", "aria-hidden": true, focusable: false };
  if (type === "paid") {
    return (
      <svg {...common}>
        <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "partial") {
    return (
      <svg {...common}>
        <path d="M6 8h12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        <path d="M6 16h7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M6 12h12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

function repairUtf8Mojibake(value) {
  let s = String(value || "");
  if (!s) return s;

  // Try up to 2 passes because some strings are double-mojibaked (UTF-8 -> latin1 -> UTF-8 -> latin1).
  for (let pass = 0; pass < 2; pass += 1) {
    if (/[\u0B80-\u0BFF]/.test(s)) return s;
    if (!/[ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¾Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â®]/.test(s)) return s;
    try {
      const bytes = Uint8Array.from(s, ch => ch.charCodeAt(0) & 0xff);
      s = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
      continue;
    } catch (_) {
      try {
        s = decodeURIComponent(escape(s));
        continue;
      } catch (_) {}
    }
    break;
  }
  return s;
}

const splitBilingual = (value) => {
  const raw = repairUtf8Mojibake(String(value || "")).trim();
  if (!raw) return { en: "", ta: "" };
  if (raw.includes("|")) {
    const [en, ta] = raw.split("|").map(s => s.trim());
    return { en: en || raw, ta: repairUtf8Mojibake(ta || "") };
  }
  if (raw.includes("\n")) {
    const [en, ta] = raw.split("\n").map(s => s.trim());
    return { en: en || raw, ta: repairUtf8Mojibake(ta || "") };
  }
  const hasTamil = /[\u0B80-\u0BFF]/.test(raw);
  if (hasTamil) {
    const ta = (raw.match(/[\u0B80-\u0BFF][\u0B80-\u0BFF\s.'-]*/g) || []).join(" ").trim();
    const en = raw.replace(/[\u0B80-\u0BFF][\u0B80-\u0BFF\s.'-]*/g, "").replace(/\s{2,}/g, " ").trim();
    return { en: en || raw, ta: ta || "" };
  }
  return { en: raw, ta: "" };
};

const TAMIL_PULLI = "\u0BCD";

const LATIN_VOWELS = ["aa", "ii", "uu", "ee", "oo", "ai", "au", "a", "i", "u", "e", "o"];
const LATIN_VOWEL_TO_TAMIL = {
  a: "\u0B85",
  aa: "\u0B86",
  i: "\u0B87",
  ii: "\u0B88",
  u: "\u0B89",
  uu: "\u0B8A",
  e: "\u0B8E",
  ee: "\u0B8F",
  ai: "\u0B90",
  o: "\u0B92",
  oo: "\u0B93",
  au: "\u0B94",
};
const LATIN_VOWEL_SIGN = {
  a: "",
  aa: "\u0BBE",
  i: "\u0BBF",
  ii: "\u0BC0",
  u: "\u0BC1",
  uu: "\u0BC2",
  e: "\u0BC6",
  ee: "\u0BC7",
  ai: "\u0BC8",
  o: "\u0BCA",
  oo: "\u0BCB",
  au: "\u0BCC",
};

const LATIN_CONS = [
  "ksh",
  "n2",
  "nn",
  "mm",
  "nd",
  "nt",
  "ng",
  "nj",
  "ch",
  "sh",
  "zh",
  "th",
  "dh",
  "ph",
  "bh",
  "kh",
  "gh",
  "rr",
  "ll",
  "k",
  "g",
  "c",
  "j",
  "t",
  "d",
  "n",
  "p",
  "b",
  "m",
  "y",
  "r",
  "l",
  "v",
  "w",
  "s",
  "h",
  "f",
  "q",
  "x",
  "z",
];
const LATIN_CONS_TO_TAMIL = {
  ksh: "\u0B95\u0BCD\u0BB7",
  n2: "\u0BA9",
  nn: "\u0BA3\u0BCD\u0BA3",
  mm: "\u0BAE\u0BCD\u0BAE",
  nd: "\u0BA3\u0BCD\u0B9F",
  nt: "\u0BA3\u0BCD\u0B9F",
  ng: "\u0B99",
  nj: "\u0B9E",
  ch: "\u0B9A",
  c: "\u0B9A",
  sh: "\u0BB7",
  s: "\u0B9A",
  z: "\u0BB8",
  zh: "\u0BB4",
  th: "\u0BA4",
  dh: "\u0BA4",
  t: "\u0B9F",
  d: "\u0B9F",
  n: "\u0BA8",
  ph: "\u0BAA",
  bh: "\u0BAA",
  p: "\u0BAA",
  b: "\u0BAA",
  f: "\u0BAA",
  m: "\u0BAE",
  y: "\u0BAF",
  r: "\u0BB0",
  rr: "\u0BB1",
  l: "\u0BB2",
  ll: "\u0BB3",
  v: "\u0BB5",
  w: "\u0BB5",
  h: "\u0BB9",
  j: "\u0B9C",
  q: "\u0B95",
  k: "\u0B95",
  g: "\u0B95",
  kh: "\u0B95",
  gh: "\u0B95",
  x: "\u0B95\u0BCD\u0BB8",
};

const latinToTamil = (value) => {
  const input = String(value || "");
  if (!input) return "";
  let out = "";
  const s = input.toLowerCase();
  for (let i = 0; i < s.length; ) {
    const ch = s[i];
    if (!/[a-z]/.test(ch)) {
      out += input[i];
      i += 1;
      continue;
    }

    const cons = LATIN_CONS.find((c) => s.startsWith(c, i));
    if (cons) {
      const base = LATIN_CONS_TO_TAMIL[cons] || cons;
      i += cons.length;
      const vow = LATIN_VOWELS.find((v) => s.startsWith(v, i));
      if (vow) {
        out += base + (LATIN_VOWEL_SIGN[vow] ?? "");
        i += vow.length;
      } else {
        out += base + TAMIL_PULLI;
      }
      continue;
    }

    const vow = LATIN_VOWELS.find((v) => s.startsWith(v, i));
    if (vow) {
      out += LATIN_VOWEL_TO_TAMIL[vow] || vow;
      i += vow.length;
      continue;
    }

    out += input[i];
    i += 1;
  }
  return out;
};

// Name-focused transliteration tweaks (more natural Tamil names)
// Example: transliteration tweaks
const applyLatinTamilHeuristics = (value, { forPlace = false } = {}) => {
  const input = String(value || "");
  if (!input) return "";
  let fixed = input;

  // Places: normalization tweaks
// (example removed)
  if (forPlace) {
    fixed = fixed.replace(/\bt(?!h)/gi, "th");
// (example removed)
    fixed = fixed.replace(/\btha(?=m)/gi, "thaa");
  }

  // Example: "Pandi" -> "paandi" (heuristic: "a" before "nd/nt" => "aa")
  fixed = fixed.replace(/\b([bcdfghjklmnpqrstvwxyz]{1,3})a(?=(?:nd|nt))/gi, (_, p1) => `${p1}aa`);

  // Example: "annamalai" -> "annaamalai" (nna + consonant/end => nnaa)
  fixed = fixed.replace(/nna(?=(?:[bcdfghjklmnpqrstvwxyz]|$))/gi, "nnaa");

// (example removed)
  fixed = fixed.replace(/([aeiou])n(?=[aeiou])/gi, "$1n2");

// (example removed)
  fixed = fixed.replace(/([bpkv])a(?=l\b)/gi, (_, p1) => `${p1}aa`);

  return fixed;
};

// Name/place-focused transliteration tweaks (simple, offline-friendly)
const latinToTamilName = (value) => latinToTamil(applyLatinTamilHeuristics(value, { forPlace: false }));
const latinToTamilPlace = (value) => latinToTamil(applyLatinTamilHeuristics(value, { forPlace: true }));

const TAMIL_INDEP_VOWEL = {
  "\u0B85": "a",
  "\u0B86": "aa",
  "\u0B87": "i",
  "\u0B88": "ii",
  "\u0B89": "u",
  "\u0B8A": "uu",
  "\u0B8E": "e",
  "\u0B8F": "ee",
  "\u0B90": "ai",
  "\u0B92": "o",
  "\u0B93": "oo",
  "\u0B94": "au",
};
const TAMIL_VOWEL_SIGN = {
  "\u0BBE": "aa",
  "\u0BBF": "i",
  "\u0BC0": "ii",
  "\u0BC1": "u",
  "\u0BC2": "uu",
  "\u0BC6": "e",
  "\u0BC7": "ee",
  "\u0BC8": "ai",
  "\u0BCA": "o",
  "\u0BCB": "oo",
  "\u0BCC": "au",
};
const TAMIL_CONS_TO_LATIN = {
  "\u0B95": "k",
  "\u0B99": "ng",
  "\u0B9A": "s",
  "\u0B9C": "j",
  "\u0B9E": "nj",
  "\u0B9F": "t",
  "\u0BA3": "n",
  "\u0BA4": "th",
  "\u0BA8": "n",
  "\u0BA9": "n",
  "\u0BAA": "p",
  "\u0BAE": "m",
  "\u0BAF": "y",
  "\u0BB0": "r",
  "\u0BB2": "l",
  "\u0BB5": "v",
  "\u0BB4": "zh",
  "\u0BB3": "l",
  "\u0BB1": "r",
  "\u0BB7": "sh",
  "\u0BB8": "s",
  "\u0BB9": "h",
  "\u0B83": "",
};

const tamilToLatin = (value) => {
  const input = String(value || "");
  if (!input) return "";
  let out = "";
  for (let i = 0; i < input.length; ) {
    const ch = input[i];

// Handle special cases
    if (ch === "\u0B95" && input[i + 1] === TAMIL_PULLI && input[i + 2] === "\u0BB7") {
      const after = input[i + 3];
      if (after === TAMIL_PULLI) {
        out += "ksh";
        i += 4;
        continue;
      }
      if (after && TAMIL_VOWEL_SIGN[after]) {
        out += "ksh" + TAMIL_VOWEL_SIGN[after];
        i += 4;
        continue;
      }
      out += "ksha";
      i += 3;
      continue;
    }

    if (TAMIL_INDEP_VOWEL[ch]) {
      out += TAMIL_INDEP_VOWEL[ch];
      i += 1;
      continue;
    }

    if (TAMIL_CONS_TO_LATIN[ch]) {
      const base = TAMIL_CONS_TO_LATIN[ch];
      const next = input[i + 1];
      if (next === TAMIL_PULLI) {
        out += base;
        i += 2;
        continue;
      }
      if (next && TAMIL_VOWEL_SIGN[next]) {
        out += base + TAMIL_VOWEL_SIGN[next];
        i += 2;
        continue;
      }
      out += base + "a";
      i += 1;
      continue;
    }

    out += ch;
    i += 1;
  }
  return out.replace(/\s{2,}/g, " ").trim();
};
function BiName({ value, className, style }) {
  const { en, ta } = splitBilingual(value);
  return <Bi en={en} ta={ta} className={className} style={style} />;
}
const parsePaymentTranscript = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return null;
  const lower = text.toLowerCase();

  let paymentType = null;
  if (/(not\s+paid|unpaid|no\s+payment|didn'?t\s+pay|did\s+not\s+pay)/.test(lower)) paymentType = "unpaid";
  else if (/\bpartial\b|\bpartly\b|\bsome\b/.test(lower)) paymentType = "partial";
  else if (/\bpaid\b|\bpay\b|\bcollected\b|\breceived\b/.test(lower)) paymentType = "paid";

  const amountMatch = lower.match(/(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)/);
  const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, "")) : null;

  let borrowerName = null;
  const fromMatch = text.match(/\bfrom\s+([a-z][a-z .'-]{1,60})/i);
  if (fromMatch) borrowerName = fromMatch[1].trim();
  if (!borrowerName) {
    const startMatch = text.match(/^([a-z][a-z .'-]{1,60}?)\s+(paid|pay|partial|partly|unpaid|not\s+paid|didn'?t\s+pay|did\s+not\s+pay)\b/i);
    if (startMatch) borrowerName = startMatch[1].trim();
  }

  if (!borrowerName || !paymentType) return null;
  if ((paymentType === "paid" || paymentType === "partial") && (!amount || amount <= 0)) return null;

  const summary =
    paymentType === "unpaid"
      ? `${borrowerName} not paid today`
      : paymentType === "partial"
        ? `${borrowerName} partial Rs ${amount}`
        : `${borrowerName} paid Rs ${amount}`;

  return {
    borrowerName,
    amount: paymentType === "unpaid" ? 0 : amount,
    paymentType,
    date: "today",
    notes: "",
    confidence: "medium",
    summary,
  };
};
const localISODate = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const localTimeHHMM = (d = new Date()) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const emitAuthChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent("lt:authChanged"));
  } catch (_) {}
};

const clearStoredAuth = () => {
  try { localStorage.removeItem(AUTH_KEY); } catch (_) {}
  try { localStorage.removeItem(REFRESH_KEY); } catch (_) {}
  try { localStorage.removeItem(USER_KEY); } catch (_) {}
};

const refreshSession = async (apiBase) => {
  let refreshToken = null;
  try { refreshToken = localStorage.getItem(REFRESH_KEY); } catch (_) {}
  if (!refreshToken) return null;

  const res = await fetch(`${apiBase}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return null;

  if (data.access_token) {
    try { localStorage.setItem(AUTH_KEY, data.access_token); } catch (_) {}
  }
  if (data.refresh_token) {
    try { localStorage.setItem(REFRESH_KEY, data.refresh_token); } catch (_) {}
  }
  if (data.user_id && data.company_id) {
    try {
      localStorage.setItem(
        USER_KEY,
        JSON.stringify({ id: data.user_id, company_id: data.company_id, name: data.name, role: data.role })
      );
    } catch (_) {}
  }
  emitAuthChanged();
  return data.access_token || null;
};

const apiJson = async (url, { token, method = "GET", body, headers } = {}) => {
  const base = (() => {
    try { return new URL(url).origin; } catch (_) { return API_BASE; }
  })();
  const cacheKey = method === "GET" ? apiCacheKey(method, url) : null;

  const doFetch = async (accessToken) => {
    const res = await fetch(url, {
      method,
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  let res, data;
  try {
    ({ res, data } = await doFetch(token));
  } catch (e) {
    if (cacheKey && isNetworkishError(e)) {
      const cached = readApiCache(cacheKey);
      if (cached) return cached.data;
    }
    throw e;
  }

  if (res.status === 401) {
    const newToken = await refreshSession(base);
    if (newToken) {
      try {
        ({ res, data } = await doFetch(newToken));
      } catch (e) {
        if (cacheKey && isNetworkishError(e)) {
          const cached = readApiCache(cacheKey);
          if (cached) return cached.data;
        }
        throw e;
      }
    } else {
      clearStoredAuth();
      emitAuthChanged();
    }
  }

  if (!res.ok) {
    const msg = normalizeMessage(data.detail ?? data.message ?? `Request failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  if (cacheKey) writeApiCache(cacheKey, data);
  return data;
};

const emitDataChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent("lt:dataChanged"));
  } catch (_) {}
};

// Tamil suggestion mode: "offline" (built-in transliteration) or "google" (server-side Google Translate proxy)
const TAMIL_SUGGEST_MODE = String(process.env.REACT_APP_TAMIL_TRANSLATOR || "offline").toLowerCase();

const useTamilSuggestor = ({ apiBase, token } = {}) => {
  const timersRef = useRef(new Map());
  const seqRef = useRef(new Map());

  useEffect(() => {
    const timers = timersRef.current;
    const seq = seqRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      seq.clear();
    };
  }, []);

  return useCallback((key, text, { forPlace = false, apply } = {}) => {
    const v = String(text || "");
    const fallback = () => (forPlace ? latinToTamilPlace(v) : latinToTamilName(v));
    const doApply = (ta) => {
      try { apply && apply(String(ta || "")); } catch (_) {}
    };

    if (!v) {
      doApply("");
      return;
    }

    const nextSeq = (seqRef.current.get(key) || 0) + 1;
    seqRef.current.set(key, nextSeq);

    const prev = timersRef.current.get(key);
    if (prev) clearTimeout(prev);

    const delay = TAMIL_SUGGEST_MODE === "google" ? 350 : 0;
    const timer = setTimeout(async () => {
      if (seqRef.current.get(key) !== nextSeq) return;
      if (TAMIL_SUGGEST_MODE !== "google" || !apiBase || !token) {
        doApply(fallback());
        return;
      }
      try {
        const out = await apiJson(`${apiBase}/api/translate`, {
          token,
          method: "POST",
          body: { text: v, source: "en", target: "ta" },
        });
        const ta = out?.translated_text || out?.translatedText || "";
        doApply(ta || fallback());
      } catch (_) {
        doApply(fallback());
      }
    }, delay);

    timersRef.current.set(key, timer);
  }, [apiBase, token]);
};

//  OFFLINE QUEUE (store writes locally, sync when online)
const OFFLINE_QUEUE_KEY = "LT:offlineQueue:v1";
const OFFLINE_ROUTE_CACHE_PREFIX = "LT:routeCache:v1:";
const OFFLINE_COLLECTORS_CACHE_KEY = "LT:collectorsCache:v1:activeOnly";

const readJsonSafe = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
};

const writeJsonSafe = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
};

const readOfflineQueue = () => {
  const q = readJsonSafe(OFFLINE_QUEUE_KEY, []);
  return Array.isArray(q) ? q : [];
};

const writeOfflineQueue = (q) => writeJsonSafe(OFFLINE_QUEUE_KEY, Array.isArray(q) ? q : []);

const enqueueOfflineAction = (action) => {
  const q = readOfflineQueue();
  q.push(action);
  writeOfflineQueue(q);
  return q.length;
};

const dequeueOfflineAction = () => {
  const q = readOfflineQueue();
  const item = q.shift();
  writeOfflineQueue(q);
  return item || null;
};

const peekOfflineAction = () => {
  const q = readOfflineQueue();
  return q[0] || null;
};

const isLikelyNetworkError = (err) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!err) return false;
  const msg = String(err.message || "");
  return err.name === "TypeError" || /failed to fetch|networkerror|load failed|fetch/i.test(msg);
};

const cacheRoute = (collectorId, dateISO, route) => {
  if (!collectorId || !dateISO) return;
  const key = `${OFFLINE_ROUTE_CACHE_PREFIX}${collectorId}:${dateISO}`;
  writeJsonSafe(key, { savedAt: Date.now(), route: Array.isArray(route) ? route : [] });
};

const readCachedRoute = (collectorId, dateISO) => {
  if (!collectorId || !dateISO) return null;
  const key = `${OFFLINE_ROUTE_CACHE_PREFIX}${collectorId}:${dateISO}`;
  const data = readJsonSafe(key, null);
  if (!data || !Array.isArray(data.route)) return null;
  return data.route;
};

const syncOfflineQueue = async ({ token, apiBase, addToast }) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { synced: 0, remaining: readOfflineQueue().length };
  let synced = 0;

  // Process sequentially; stop on first network error to avoid spinning.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const item = peekOfflineAction();
    if (!item) break;
    try {
      const fullUrl = item.url.startsWith("http") ? item.url : `${apiBase}${item.url}`;
      await apiJson(fullUrl, { token, method: item.method, body: item.body, headers: item.headers });
      dequeueOfflineAction();
      synced += 1;
    } catch (e) {
      if (isLikelyNetworkError(e)) break;
      // For non-network errors, drop the item (prevents permanent stuck queue).
      dequeueOfflineAction();
      addToast?.(e.message || "Offline sync failed for an item", "error");
    }
  }

  const remaining = readOfflineQueue().length;
  return { synced, remaining };
};

const apiBlob = async (url, { token } = {}) => {
  const base = (() => {
    try { return new URL(url).origin; } catch (_) { return API_BASE; }
  })();

  const doFetch = async (accessToken) => {
    return fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    });
  };

  let res = await doFetch(token);
  if (res.status === 401) {
    const newToken = await refreshSession(base);
    if (newToken) res = await doFetch(newToken);
    else {
      clearStoredAuth();
      emitAuthChanged();
    }
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.message || `Request failed (${res.status})`);
  }
  return res.blob();
};

//  STYLES 
const S = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=Bebas+Neue&family=Noto+Sans+Tamil:wght@400;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#f4f6fb;
  --bg2:#ffffff;
  --bg3:#edf1fb;
  --bg4:#dfe7ff;
  --bd:#dde2f0;
  --bd2:#ccd4ea;
  --t:#1b2559;
  --t2:#4b5a88;
  --t3:#9aa3c2;
  --am:#4f7cff;
  --am2:#355dff;
  --or:#f5a623;
  --or2:#d97c00;
  --gr:#2ecc71;
  --re:#e74c3c;
  --bl:#23b3ff;
  --mono:'IBM Plex Mono',monospace;
  --sans:'IBM Plex Sans',sans-serif;
  --disp:'Bebas Neue',cursive;
  --tamil:'Noto Sans Tamil','Nirmala UI','Latha','Vijaya','Tamil Sangam MN',system-ui,sans-serif;
  --r:8px;
  --r2:18px;
  --sh:0 18px 45px rgba(15,40,120,.18);
}
body{background:var(--bg);color:var(--t);font-family:var(--sans);overflow:hidden}
::-webkit-scrollbar{width:3px;height:3px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:var(--bd2);border-radius:2px}

/* Layout */
.app{display:flex;height:100vh;width:100vw;overflow:hidden}
.sidebar{width:230px;min-width:230px;background:var(--bg2);border-right:1px solid var(--bd);display:flex;flex-direction:column;z-index:10;box-shadow:4px 0 18px rgba(15,40,120,.06)}
.main{flex:1;overflow-y:auto;background:var(--bg);padding-right:8px}

/* Sidebar */
.sb-brand{padding:18px 18px 14px;border-bottom:1px solid var(--bd);display:flex;flex-direction:column;gap:4px}
.sb-title{font-family:var(--disp);font-size:26px;letter-spacing:4px;color:var(--am);line-height:1}
.sb-sub{font-family:var(--mono);font-size:9px;color:var(--t3);letter-spacing:3px;text-transform:uppercase}
.sb-nav{flex:1;padding:5px 0;overflow-y:auto}
.nav-sec{padding:13px 18px 4px;font-family:var(--mono);font-size:8px;color:var(--t3);letter-spacing:3px;text-transform:uppercase}
.nav-item{display:flex;align-items:center;gap:10px;padding:10px 18px;cursor:pointer;font-size:13px;font-weight:500;color:var(--t2);transition:all .14s;border-radius:999px 0 0 999px;margin-right:10px}
.nav-item:hover{background:rgba(79,124,255,.06);color:var(--am)}
.nav-item.active{background:linear-gradient(90deg,rgba(79,124,255,.18),rgba(79,124,255,.04));color:var(--am);box-shadow:0 10px 25px rgba(79,124,255,.18)}
.nav-ico{font-size:13px;width:17px;text-align:center}
.sb-foot{padding:12px 16px 14px;border-top:1px solid var(--bd);background:linear-gradient(135deg,#eff3ff,#ffffff)}
.u-pill{display:flex;align-items:center;gap:9px}
.av{width:32px;height:32px;border-radius:16px;background:linear-gradient(135deg,#4f7cff,#23b3ff);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;flex-shrink:0;box-shadow:0 10px 25px rgba(79,124,255,.45)}
.u-name{font-size:12px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--t)}
.u-role{font-family:var(--mono);font-size:9px;color:var(--t3);text-transform:uppercase;letter-spacing:1px}

/* Page */
.page{padding:22px 24px}
.ph{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px}
.pt{font-family:var(--sans);font-size:26px;font-weight:600;letter-spacing:.4px;line-height:1.2;color:var(--t)}
.ps{font-family:var(--mono);font-size:9px;color:var(--t3);margin-top:4px;letter-spacing:2px;text-transform:uppercase}

/* Cards */
.card{background:var(--bg2);border:1px solid rgba(221,226,240,.9);border-radius:22px;box-shadow:var(--sh);overflow:hidden}
.ch{padding:10px 16px;border-bottom:1px solid var(--bd);display:flex;align-items:center;justify-content:space-between}
.ct{font-family:var(--mono);font-size:9px;font-weight:600;color:var(--t2);letter-spacing:2px;text-transform:uppercase}
.cb{padding:16px}

/* Statement: Customer details (light) */
.stmt-cust{background:linear-gradient(180deg,#ffffff,#f8f9ff);border:1px solid rgba(221,226,240,.9);box-shadow:var(--sh)}
.stmt-cust .ch{border-bottom:1px solid var(--bd);background:transparent}
.stmt-cust .ct{color:var(--t2)}
.stmt-cust .cb{color:var(--t)}
.stmt-top{display:flex;align-items:center;gap:14px;padding:10px 0 10px}
.stmt-av{width:56px;height:56px;border-radius:999px;background:linear-gradient(135deg,var(--am),var(--am2));display:flex;align-items:center;justify-content:center;font-family:var(--mono);font-weight:900;color:#fff;box-shadow:0 14px 26px rgba(15,40,120,.18)}
.stmt-name .en{font-family:var(--sans);font-size:22px;font-weight:900;color:var(--t);line-height:1.05}
.stmt-name .ta{font-family:var(--tamil);font-size:12px;font-weight:800;color:var(--t2);margin-top:5px}
.stmt-sep{height:1px;background:var(--bd);margin:12px 0}
.stmt-row{display:flex;align-items:flex-start;gap:10px;margin-top:10px}
.stmt-row svg{width:18px;height:18px;stroke:var(--t3);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;margin-top:2px;flex-shrink:0}
.stmt-row-main{font-family:var(--sans);font-size:16px;font-weight:800;color:var(--t);line-height:1.15;word-break:break-word}
.stmt-row-sub{font-family:var(--tamil);font-size:11px;font-weight:800;color:var(--t2);margin-top:3px}
.stmt-row-k{color:var(--t2);font-weight:800}
.stmt-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;margin-top:16px}
.stmt-stat{position:relative;background:#ffffff;border:1px solid var(--bd);border-radius:14px;padding:12px;box-shadow:0 12px 26px rgba(15,40,120,.08)}
.stmt-stat-l{font-family:var(--mono);font-size:10px;letter-spacing:1.8px;text-transform:uppercase;color:var(--t3)}
.stmt-stat-ta{font-family:var(--tamil);font-size:9px;font-weight:800;color:var(--t3);margin-top:3px}
.stmt-stat-v{font-family:var(--sans);font-size:20px;font-weight:900;color:var(--t);margin-top:8px;white-space:nowrap}
.stmt-stat-v2{font-family:var(--tamil);font-size:10px;font-weight:800;color:var(--t2);margin-top:4px}
.stmt-arrow{position:absolute;right:10px;bottom:10px;width:34px;height:34px;border-radius:999px;border:1px solid var(--bd2);background:#ffffff;display:flex;align-items:center;justify-content:center}
.stmt-arrow svg{width:18px;height:18px;stroke:var(--t3);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
@media (max-width: 520px){
  .stmt-stat{padding:10px}
  .stmt-stat-v{font-size:16px}
  .stmt-name .en{font-size:18px}
}

/* Stats */
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px}
.sg.sg-5{grid-template-columns:repeat(5,1fr)}
.sc{background:linear-gradient(145deg,#ffffff,#eff3ff);border:1px solid rgba(221,226,240,.9);border-radius:22px;padding:14px 16px;position:relative;overflow:hidden;box-shadow:var(--sh)}
.sc::before{content:'';position:absolute;top:-40px;right:-40px;width:120px;height:120px;border-radius:50%;background:radial-gradient(circle at 30% 30%,rgba(79,124,255,.2),transparent 60%)}
.sl{font-family:var(--mono);font-size:8px;color:var(--t3);letter-spacing:2px;text-transform:uppercase;margin-bottom:6px}
.sv{font-family:var(--sans);font-size:26px;font-weight:700;color:var(--t);letter-spacing:.5px;line-height:1.1}
.ss{font-family:var(--mono);font-size:8px;color:var(--t3);margin-top:3px}
.schange{font-family:var(--mono);font-size:8px;margin-top:3px}
.schange.up{color:var(--gr)}.schange.down{color:var(--re)}

/* Table */
.tw{overflow-x:auto}
table{width:100%;border-collapse:collapse;font-size:12px}
thead tr{border-bottom:1px solid var(--bd2);background:#f8f9ff}
th{padding:9px 10px;font-family:var(--mono);font-size:8px;color:var(--t3);letter-spacing:2px;text-transform:uppercase;text-align:left;font-weight:500;white-space:nowrap}
td{padding:10px 10px;border-bottom:1px solid var(--bd);color:var(--t2);vertical-align:middle;background:#ffffff}
tr:last-child td{border-bottom:none}
tr:hover td{background:#f3f6ff;color:var(--t)}
.mono{font-family:var(--mono);font-size:11px}
.sort-table thead th.srt-th{cursor:pointer;position:relative;padding-right:22px}
.sort-table thead th.srt-th:hover{color:var(--t2)}
.sort-table thead th.srt-th::before{content:'';position:absolute;right:8px;top:9px;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-bottom:6px solid var(--t3);opacity:.25}
.sort-table thead th.srt-th::after{content:'';position:absolute;right:8px;top:17px;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:6px solid var(--t3);opacity:.25}
.sort-table thead th.srt-th[data-sort-active='asc']::before{opacity:1;border-bottom-color:var(--am2)}
.sort-table thead th.srt-th[data-sort-active='desc']::after{opacity:1;border-top-color:var(--am2)}

/* Borrowers: hide any accidental UUID line under the name (mobile/legacy) */
.borrowers-table td:first-child .mono.mu{display:none}

/* Mobile shell */
.mtop{display:none}
.mnav{display:none}
  @media (max-width: 900px){
    body{overflow:hidden}
    .sidebar{display:none}
    .main{padding-right:0}
    .main.mob{padding-bottom:0}
    .mtop{display:grid;grid-template-columns:44px 1fr 44px;gap:8px;position:sticky;top:0;z-index:15;align-items:center;padding:10px 12px;border-bottom:1px solid var(--bd);background:linear-gradient(135deg,#f8f9ff,#ffffff)}
    .mtop .brandLogo{width:140px;margin:0 auto}
    .mtop-btn{background:#f3f5ff;border:1px solid var(--bd2);border-radius:999px;padding:5px 8px;display:flex;align-items:center;gap:8px;cursor:pointer}
    .mtop-btn:active{transform:translateY(1px)}
    .mtop-btn .av{width:28px;height:28px;border-radius:14px;box-shadow:none}
    .mtop-btn .mtop-t{font-family:var(--mono);font-size:9px;color:var(--t3);letter-spacing:1px;white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis}
    .mtop-back{background:#ffffff;border:1px solid var(--bd2);border-radius:14px;width:40px;height:40px;display:flex;align-items:center;justify-content:center;cursor:pointer}
    .mtop-back:active{transform:translateY(1px)}
    .mtop-back svg{width:22px;height:22px;fill:#111}
    .mtop-back:disabled{opacity:.4;cursor:not-allowed}

    /* pages */
  .page{padding:14px 14px 0}
  .ph{flex-direction:column;gap:10px;align-items:flex-start}
  .pt{font-size:22px}
  .sg{grid-template-columns:repeat(2,1fr);gap:10px}
  .sg.sg-5{grid-template-columns:repeat(2,1fr)}
  .g2{grid-template-columns:1fr}
  .g3{grid-template-columns:1fr}

  /* home grid */
  .homegrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:2px}
  .htile{border:none;background:linear-gradient(145deg,#ffffff,#eff3ff);border:1px solid rgba(221,226,240,.9);border-radius:22px;padding:14px 10px;cursor:pointer;box-shadow:0 18px 45px rgba(15,40,120,.12);display:flex;flex-direction:column;align-items:center;gap:8px}
  .htile:active{transform:translateY(1px)}
  .ht-ico{width:44px;height:44px;border-radius:18px;background:linear-gradient(135deg,var(--am),var(--am2));display:flex;align-items:center;justify-content:center;font-family:var(--mono);font-weight:800;color:#fff;letter-spacing:1px}
  .ht-l{font-family:var(--sans);font-size:12px;font-weight:700;color:var(--t);text-align:center;line-height:1.1}
  .ht-s{font-family:var(--tamil);font-size:10px;color:var(--t3);text-align:center;line-height:1.1}
  @media (max-width: 420px){.homegrid{grid-template-columns:repeat(2,1fr)}}
}

/* Badges */
.badge{display:inline-flex;align-items:center;padding:2px 6px;border-radius:2px;font-family:var(--mono);font-size:7px;font-weight:600;letter-spacing:1px;text-transform:uppercase}
.badge .bi{line-height:1.05}
.badge .ta{font-size:7px;color:var(--t3);font-weight:700;margin-top:1px}
.bg{background:rgba(46,204,113,.12);color:var(--gr);border:1px solid rgba(46,204,113,.25)}
.br{background:rgba(231,76,60,.12);color:var(--re);border:1px solid rgba(231,76,60,.25)}
.ba{background:rgba(79,124,255,.12);color:var(--am2);border:1px solid rgba(79,124,255,.25)}
.bo{background:rgba(245,166,35,.12);color:var(--or2);border:1px solid rgba(245,166,35,.25)}
.bb{background:rgba(35,179,255,.12);color:var(--bl);border:1px solid rgba(35,179,255,.25)}
.bgr{background:#eef1fb;color:var(--t3);border:1px solid var(--bd)}

/* Buttons */
.btn{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;border-radius:var(--r);font-family:var(--sans);font-size:11px;font-weight:600;cursor:pointer;border:none;transition:all .12s;white-space:nowrap}
.btn-p{background:linear-gradient(135deg,var(--am),var(--am2));color:#fff;box-shadow:0 10px 20px rgba(79,124,255,.45)}
.btn-p:hover{filter:brightness(1.05);transform:translateY(-1px)}
.btn-g{background:#f3f5ff;color:var(--t2);border:1px solid var(--bd2)}
.btn-g:hover{background:#e4e8ff;color:var(--t)}
.btn-pu{background:rgba(155,89,182,.15);color:#b97fe0;border:1px solid rgba(155,89,182,.3)}
.btn-su{background:rgba(46,204,113,.12);color:var(--gr);border:1px solid rgba(46,204,113,.2)}
  .btn-sm{padding:3px 8px;font-size:10px}
  .btn:disabled{opacity:.4;cursor:not-allowed}
  .call-btn{gap:6px}
  .call-ico{width:18px;height:18px;border-radius:6px;background:#ff4d86;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 10px 22px rgba(255,77,134,.35)}
  .call-ico svg{width:12px;height:12px;fill:#fff}

  /* Forms */
  .fg{margin-bottom:11px}
  .fl{display:block;font-family:var(--mono);font-size:8px;color:var(--t3);letter-spacing:2px;text-transform:uppercase;margin-bottom:4px}
  .fi{width:100%;background:#f6f7ff;border:1px solid var(--bd2);border-radius:999px;padding:9px 14px;font-family:var(--sans);font-size:12px;color:var(--t);outline:none;transition:border-color .12s,box-shadow .12s,background .12s}
.fi:focus{border-color:var(--am);box-shadow:0 0 0 1px rgba(79,124,255,.35);background:#ffffff}
.fi::placeholder{color:var(--t3)}
select.fi option{background:var(--bg2)}
.fgrid{display:grid;grid-template-columns:1fr 1fr;gap:9px}
.fgrid-3{grid-template-columns:2fr 1fr 1fr}

/* Upload / preview */
.udrop{height:220px;border:1px dashed var(--bd2);background:linear-gradient(180deg,#ffffff,#f6f7ff);border-radius:18px;display:flex;align-items:center;justify-content:center;text-align:center;cursor:pointer;overflow:hidden}
.uprev{max-width:100%;max-height:220px;object-fit:contain;display:block}

/* Modal */
.mo{position:fixed;inset:0;background:rgba(9,20,64,.16);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;z-index:1000;animation:fi .12s}
.md{background:var(--bg2);border:1px solid var(--bd2);border-radius:24px;width:480px;max-width:93vw;max-height:88vh;overflow-y:auto;box-shadow:var(--sh);animation:su .18s}
.md-lg{width:620px}
.mh{padding:13px 17px;border-bottom:1px solid var(--bd);display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:var(--bg2);z-index:2}
.mt2{font-family:var(--sans);font-size:17px;letter-spacing:1px}
.mb2{padding:17px}
.mf{padding:11px 17px;border-top:1px solid var(--bd);display:flex;justify-content:flex-end;gap:7px}

/* Progress */
.prog{height:3px;background:var(--bg3);border-radius:2px;overflow:hidden}
.pf{height:100%;border-radius:2px;transition:width .5s}
.prog-sm{height:5px}

  /* Mobile shell */
  .mob-w{display:flex;justify-content:center;padding:18px;min-height:100%;background:linear-gradient(145deg,#f4f6fb,#e1ebff)}
  .mob-f{width:355px;min-height:660px;background:var(--bg2);border:1px solid var(--bd2);border-radius:30px;overflow:hidden;box-shadow:0 28px 60px rgba(15,40,120,.25);display:flex;flex-direction:column}
  .mob-st{padding:10px 16px 8px;border-bottom:1px solid var(--bd);display:flex;align-items:center;justify-content:space-between;font-family:var(--mono);font-size:11px;color:var(--t3);letter-spacing:1px;background:linear-gradient(135deg,#f8f9ff,#ffffff)}
  .mob-tb{padding:14px 16px 12px;border-bottom:1px solid var(--bd);background:linear-gradient(135deg,#f8f9ff,#ffffff)}
  .mob-ct{flex:1;overflow-y:auto;padding:8px 0 18px;background:linear-gradient(180deg,#ffffff,#f8faff)}
  .mob-nav{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:0 12px;margin-top:6px}
  .mob-nav .mob-step{font-family:var(--mono);font-size:11px;color:var(--t3);letter-spacing:1px}
  .mob-nav .btn{padding:5px 10px}
  .rt-en{font-family:var(--disp);font-size:19px;letter-spacing:1px}
  .rt-ta{font-family:var(--tamil);font-size:28px;font-weight:900;color:var(--t);margin-top:4px;line-height:1.05}
  .rt-name{font-family:var(--sans);font-size:16px;color:var(--t);font-weight:850;margin-top:10px;line-height:1.1}
  .rt-meta{font-family:var(--mono);font-size:8px;color:var(--t3);margin-top:4px;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
  .rt-meta .rt-ta-sm{font-family:var(--tamil);font-size:10px;color:var(--t2);font-weight:800}
  .rc{margin:10px 12px;border:1px solid var(--bd);border-radius:18px;background:#fff;overflow:hidden;box-shadow:0 10px 25px rgba(15,40,120,.08)}
  .rch{padding:14px 14px 10px;display:flex;align-items:center;gap:12px}
  .bav{width:44px;height:44px;border-radius:14px;background:linear-gradient(135deg,#5b6bff,#6f59ff);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:900;color:#fff;flex-shrink:0;box-shadow:none}
  .rn{font-weight:900;font-size:20px;color:var(--t);line-height:1.05}
  .rn .bi>span:first-child{font-family:var(--sans);font-size:20px;font-weight:900;color:var(--t)}
  .rn .bi .ta{font-size:10px;color:var(--t);font-weight:800;margin-top:4}
  .ra{font-family:var(--mono);font-size:11px;color:var(--t3);margin-top:3px;line-height:1.2}
  .ract{display:flex;flex-direction:column;gap:14px;padding:16px 14px 18px;border-top:none;background:#ffffff}
  .act{--accent:var(--t);--accentBg:transparent;--accentBorder:var(--bd2);--accentSolid:var(--t);cursor:pointer;user-select:none;border:2px solid var(--accentBorder);background:var(--accentBg);border-radius:18px;padding:18px 18px;display:flex;align-items:center;gap:16px;text-align:left;box-shadow:none;color:var(--accent);width:100%}
  .act:hover{filter:brightness(1.01)}
  .act:active{transform:translateY(1px)}
  .act:focus-visible{outline:2px solid rgba(79,124,255,.25);outline-offset:2px}
  .act-ico{width:56px;height:56px;border-radius:999px;display:flex;align-items:center;justify-content:center;font-size:32px;font-weight:900;flex-shrink:0;background:var(--accentSolid);color:#fff}
  .act-ico[aria-hidden="true"]{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;line-height:1}
  .act-ico svg{width:30px;height:30px}
  .act-paid{--accent:var(--gr);--accentBg:rgba(46,204,113,.10);--accentBorder:var(--gr);--accentSolid:var(--gr)}
  .act-partial{--accent:var(--or2);--accentBg:rgba(245,166,35,.10);--accentBorder:var(--or);--accentSolid:var(--or)}
  .act-unpaid{--accent:var(--re);--accentBg:rgba(231,76,60,.10);--accentBorder:var(--re);--accentSolid:var(--re)}
  .act-bi{display:flex;flex-direction:column;line-height:1.05;flex:1;min-width:0}
  .act-bi>span:first-child{font-family:var(--mono);font-size:12px;letter-spacing:2px;font-weight:800;text-transform:uppercase;color:var(--accent)}
  .act-bi .ta{font-size:clamp(10px,4.6vw,16px);font-weight:900;color:var(--accent);margin-top:4px;letter-spacing:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .rd{padding:14px 14px 16px;border-top:1px solid rgba(221,226,240,.9);display:flex;align-items:center;justify-content:space-between;gap:12px;background:#ffffff}
  .rd-left{display:flex;flex-direction:column;gap:6px;min-width:0}
  .rd-amt{display:flex;align-items:baseline;gap:10px;min-width:0}
  .rd-st{font-family:var(--sans);font-weight:950;font-size:26px;letter-spacing:.4px;line-height:1;color:var(--rdAccent,var(--t))}
  .rd-val{font-family:var(--sans);font-weight:950;font-size:22px;line-height:1;color:var(--t);white-space:nowrap}
  .rd-sub{display:flex;align-items:center;gap:14px;min-width:0}
  .rd-ta{font-family:var(--tamil);font-size:11px;font-weight:800;color:var(--t3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .rd-time{display:inline-flex;align-items:center;gap:6px;font-family:var(--mono);font-size:12px;color:var(--t3);white-space:nowrap}
  .rd-time svg{width:16px;height:16px;stroke:var(--t3);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
  .edit-pill{display:inline-flex;align-items:center;justify-content:center;padding:10px 16px;border-radius:16px;border:none;background:linear-gradient(135deg,#2ecc71,#20c05a);color:#fff;font-family:var(--sans);font-weight:900;font-size:14px;cursor:pointer;min-width:92px}
  .edit-pill:active{transform:translateY(1px)}
  .edit-pill .bi>span:first-child{color:#fff}
  .edit-pill .ta{color:rgba(255,255,255,.95);font-size:12px;margin-top:3px}

  .call-pill{display:inline-flex;align-items:center;gap:10px;padding:10px 18px;border-radius:999px;background:linear-gradient(135deg,#2ecc71,#20c05a);color:#ffffff;font-family:var(--sans);font-weight:900;font-size:18px;letter-spacing:.2px;text-decoration:none;border:none;box-shadow:none;flex-shrink:0}
  .call-pill:hover{filter:brightness(1.02)}
  .call-pill:active{transform:translateY(1px)}
  .call-pill svg{width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round}

  .mic-btn{width:34px;height:34px;border-radius:999px;border:1px solid var(--bd2);background:#ffffff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}
  .mic-btn:active{transform:translateY(1px)}
  .mic-btn svg{width:18px;height:18px;fill:none;stroke:var(--t2);stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round}
  .mic-btn.on{border-color:rgba(46,204,113,.55);box-shadow:0 0 0 3px rgba(46,204,113,.16)}
  .mic-btn.on svg{stroke:var(--gr)}

  .vchip{display:flex;align-items:center;gap:8px;margin-top:10px}
  .vtag{flex:1;min-width:0;background:#ffffff;border:1px dashed var(--bd2);border-radius:999px;padding:7px 10px;font-family:var(--mono);font-size:10px;color:var(--t2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

/* Login */
.lp{display:flex;align-items:center;justify-content:center;min-height:100vh;background:linear-gradient(135deg,#f4f6fb,#e1ebff)}
.lc{width:360px;background:var(--bg2);border:1px solid var(--bd);border-radius:26px;padding:32px 28px;box-shadow:0 24px 60px rgba(15,40,120,.25)}
.lb{text-align:center;margin-bottom:24px}
.brandLogo{width:220px;max-width:100%;height:auto;display:block;margin:0 auto 10px}
.lt{font-family:var(--sans);font-size:24px;letter-spacing:.5px;color:var(--t);font-weight:600}
.ls{font-family:var(--mono);font-size:9px;color:var(--t3);letter-spacing:2px;margin-top:3px;text-transform:uppercase}
.rtabs{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--bd);border-radius:var(--r);overflow:hidden;margin-bottom:18px}
.rtab{padding:8px;text-align:center;font-size:10px;font-weight:600;cursor:pointer;background:var(--bg3);color:var(--t3);font-family:var(--mono);letter-spacing:1px;transition:all .12s}
.rtab.on{background:linear-gradient(135deg,var(--am),var(--am2));color:#fff}

/* Utils */
.g2{display:grid;grid-template-columns:1fr 1fr;gap:11px}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:11px}
.row{display:flex;align-items:center;gap:6px}
.bw{display:flex;align-items:center;justify-content:space-between}
.mt8{margin-top:8px}.mt12{margin-top:12px}.mt14{margin-top:14px}.mt16{margin-top:16px}
.mb8{margin-bottom:8px}.mb12{margin-bottom:12px}
.am{color:var(--am)}.gr{color:var(--gr)}.re{color:var(--re)}.mu{color:var(--t3)}

/* Toast */
.tw2{position:fixed;bottom:18px;right:18px;z-index:9999;display:flex;flex-direction:column;gap:6px;pointer-events:none}
.toast{background:var(--bg2);border:1px solid var(--bd2);border-radius:var(--r2);padding:9px 13px;font-size:11px;box-shadow:var(--sh);animation:su .18s;display:flex;align-items:center;gap:6px;min-width:210px}
.t-ok{border-left:3px solid var(--gr)}.t-er{border-left:3px solid var(--re)}.t-in{border-left:3px solid var(--am)}

/* Tamil subtitles */
.bi{display:inline-flex;flex-direction:column;line-height:1.05}
.ta{font-family:var(--tamil);font-size:10px;color:var(--t2);margin-top:2px;font-weight:700}
.ta-input{font-family:var(--tamil)}

/* Recharts */
.recharts-tooltip-cursor{fill:rgba(15,40,120,.04) !important}

/* Animations */
@keyframes fi{from{opacity:0}to{opacity:1}}
@keyframes su{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin 1s linear infinite;display:inline-block}
@media(max-width:960px){.sg{grid-template-columns:repeat(2,1fr)}.fgrid{grid-template-columns:1fr}.fgrid-3{grid-template-columns:1fr}}
`;

//  CUSTOM CHART TOOLTIP 
const CT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:"var(--bg2)",border:"1px solid var(--bd2)",borderRadius:6,padding:"7px 11px",fontFamily:"var(--mono)",fontSize:10}}>
      <div style={{color:"var(--t3)",marginBottom:3,fontSize:8,letterSpacing:1}}>{label}</div>
      {payload.map(p=>(
        <div key={p.name} style={{color:p.color,display:"flex",gap:8,justifyContent:"space-between"}}>
          <span>{p.name}</span>
          <span style={{fontWeight:600}}>{typeof p.value==="number"&&p.value>999?fmt(p.value):p.value}</span>
        </div>
      ))}
    </div>
  );
};

//  TOAST 
function Toast({ toasts }) {
  return (
    <div className="tw2">
      {toasts.map(t=>(
        <div key={t.id} className={`toast ${t.type==="success"?"t-ok":t.type==="error"?"t-er":"t-in"}`}>
          <span>{t.type==="success"?"OK":t.type==="error"?"X":"i"}</span>{t.msg}
        </div>
      ))}
    </div>
  );
}

//  MODAL 
function Modal({ title, onClose, children, footer, large }) {
  return (
    <div className="mo" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className={`md${large?" md-lg":""}`}>
        <div className="mh">
          {typeof title === "string"
            ? <Bi en={title} className="mt2" />
            : <span className="mt2">{title}</span>}
          <button className="btn btn-g btn-sm" style={{padding:"3px 6px"}} onClick={onClose}>X</button>
        </div>
        <div className="mb2">{children}</div>
        {footer&&<div className="mf">{footer}</div>}
      </div>
    </div>
  );
}

//  IMAGE SCAN MODAL 
function ImageScanModal({ onClose, addToast, token, apiBase }) {
  const [imgData, setImgData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [saved,   setSaved]   = useState(false);
  const fileRef = useRef();

  const handleFile = file => {
    if (!file) return;
    setResult(null); setSaved(false);
    const r = new FileReader();
    r.onload = e => setImgData(e.target.result);
    r.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!imgData) return;
    setLoading(true); setResult(null);
    try {
      const parsed = await apiJson(`${apiBase}/api/ai/image-scan`, {
        token,
        method: "POST",
        body: { image_data_url: imgData },
      });
      setResult(parsed);
    } catch (e) {
      setResult({error:true,notes:normalizeMessage(e),confidence:"low",action:"Retry with better image"});
    }
    setLoading(false);
  };

  const save = async () => {
    if (!result || result.error) return;
    setLoading(true);
    try {
      const savedRec = await apiJson(`${apiBase}/api/ai/image-scan/save`, {
        token,
        method: "POST",
        body: { image_data_url: imgData, extracted: result },
      });
      setSaved(true);
      const bits = [];
      if (savedRec?.borrower_created) bits.push("Borrower created");
      if (savedRec?.borrower_id && !savedRec?.borrower_created) bits.push("Borrower linked");
      if (savedRec?.loan_created) bits.push("Loan created");
      if (savedRec?.payment_created) bits.push("Payment recorded");
      bits.push(`Scan saved ${savedRec?.id || ""}`.trim());
      const warn = Array.isArray(savedRec?.warnings) && savedRec.warnings.length ? ` (${savedRec.warnings[0]})` : "";
      addToast((bits.filter(Boolean).join(" â€¢ ") || "Saved") + warn, "success");
      emitDataChanged();
    } catch (e) {
      addToast(e, "error");
    } finally {
      setLoading(false);
    }
  };
  const cc   = {high:"var(--gr)",medium:"var(--am)",low:"var(--re)"};

  return (
    <Modal title="  IMAGE SCAN  DATABASE" onClose={onClose} large
      footer={<>
        <button className="btn btn-g" onClick={onClose}>Close</button>
        {result&&!result.error&&!saved&&<button className="btn btn-p" onClick={save} disabled={loading}> Save to Database</button>}
        {saved&&<span className="badge bg" style={{padding:"5px 10px",fontSize:9}}> SAVED</span>}
      </>}>
      <div className="g2" style={{gap:14}}>
        <div>
          <div className="udrop" onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0])}} onClick={()=>fileRef.current?.click()}>
            {imgData
              ? <img src={imgData} className="uprev" alt="preview"/>
              : <><div style={{fontSize:28,marginBottom:7}}></div><div style={{fontWeight:600,fontSize:12,marginBottom:3}}>Drop image here</div><div style={{fontSize:10,color:"var(--t3)"}}>Receipts - Slips - ID Cards</div></>
            }
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
          <button className="btn btn-p mt12" style={{width:"100%",justifyContent:"center"}} onClick={analyze} disabled={!imgData||loading}>
            {loading?<><span className="spin"></span> Analyzing...</>:"Analyze with AI"}
          </button>
          <div style={{textAlign:"center",marginTop:6,fontFamily:"var(--mono)",fontSize:8,color:"var(--t3)",letterSpacing:1}}>GROQ VISION</div>
        </div>
        <div>
          <div className="fl mb8">EXTRACTED DATA</div>
          {!result&&!loading&&<div className="ai-p ai-empty"><div style={{fontSize:26,marginBottom:7}}></div><div style={{fontSize:11,color:"var(--t3)"}}>Upload an image to extract payment data automatically</div></div>}
          {loading&&<div className="ai-p ai-empty"><div className="spin" style={{fontSize:22}}></div><div style={{fontSize:11,color:"var(--t3)",marginTop:7}}>Reading document...</div></div>}
          {result&&!result.error&&(
            <div className="ai-p">
              <div className="bw mb8">
                <span className="fl" style={{margin:0}}>CONFIDENCE</span>
                <span style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:600,color:cc[result.confidence]||"var(--am)"}}>- {(result.confidence||"").toUpperCase()}</span>
              </div>
              {[["Doc Type",result.documentType],["Borrower",result.borrowerName],["Amount",result.amount?fmt(result.amount):null],["Date",result.date],["Loan ID",result.loanId],["Phone",result.phone]].map(([l,v])=>v?(
                <div className="ai-fld" key={l}><span className="ai-fl">{l}</span><span className="ai-fv">{v}</span></div>
              ):null)}
              {result.notes&&<div style={{marginTop:7,padding:"5px 7px",background:"var(--bg)",borderRadius:3,fontSize:11,color:"var(--t3)"}}>{result.notes}</div>}
              {result.action&&<div className="row mt8"><span className="badge ba">ACTION</span><span style={{fontSize:11}}>{result.action}</span></div>}
            </div>
          )}
          {result?.error&&<div className="ai-p" style={{borderColor:"rgba(231,76,60,.3)"}}><div style={{color:"var(--re)",fontWeight:600,marginBottom:4}}>Failed</div><div style={{fontSize:11,color:"var(--t3)"}}>{result.notes}</div></div>}
        </div>
      </div>
    </Modal>
  );
}

//  VOICE MODAL 
function VoiceModal({ onClose, addToast, token, apiBase }) {
  const [rec,    setRec]    = useState(false);
  const [trans,  setTrans]  = useState("");
  const [loading,setLoading]= useState(false);
  const [result, setResult] = useState(null);
  const [saved,  setSaved]  = useState(false);
  const recRef = useRef();

  const startRec = () => {
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    if (!SR) { addToast("Speech API not supported","error"); return; }
    const r = new SR();
    r.continuous=false; r.interimResults=true; r.lang="en-IN";
    r.onresult=e=>setTrans(Array.from(e.results).map(x=>x[0].transcript).join(" "));
    r.onend=()=>setRec(false);
    r.start(); recRef.current=r; setRec(true); setResult(null); setSaved(false);
  };
  const stopRec = () => { recRef.current?.stop(); setRec(false); };

  const process = async () => {
    if (!trans.trim()) return;
    setLoading(true); setResult(null);
    try {
      const parsed = await apiJson(`${apiBase}/api/ai/voice-parse`, {
        token,
        method: "POST",
        body: { text: trans },
      });
      setResult(parsed);
    } catch (e) {
      const local = parsePaymentTranscript(trans);
      if (local) setResult(local);
      else setResult({error:true,summary:normalizeMessage(e)||"Could not parse. Try speaking more clearly.",confidence:"low"});
    }
    setLoading(false);
  };

  const save = async () => {
    if (!result || result.error) return;
    setLoading(true);
    try {
      const out = await apiJson(`${apiBase}/api/ai/voice-save`, {
        token,
        method: "POST",
        body: { text: trans, parsed: result },
      });
      setSaved(true);
      const bits = [];
      if (out?.already_recorded) bits.push("Already recorded today");
      if (out?.borrower_created) bits.push("Borrower created");
      if (out?.loan_created) bits.push("Loan created");
      bits.push(`Payment ${out?.already_recorded ? "linked" : "saved"} (${out.payment_id || "ok"})`);
      addToast(bits.join(" â€¢ "), "success");
      emitDataChanged();
    } catch (e) {
      addToast(e, "error");
    } finally {
      setLoading(false);
    }
  };

  const SAMPLES = ["Rajan paid 590 rupees today","Sunita partial 800 rupees","Ramesh not paid today","Collected 1550 from Kavita Singh"];
  const cc = {high:"var(--gr)",medium:"var(--am)",low:"var(--re)"};
  const ptColor = t=>t==="paid"?"var(--gr)":t==="unpaid"?"var(--re)":"var(--am)";

  return (
    <Modal title="  VOICE  PAYMENT ENTRY" onClose={onClose} large
      footer={<>
        <button className="btn btn-g" onClick={onClose}>Close</button>
        {result&&!result.error&&!saved&&<button className="btn btn-p" onClick={save}> Record Payment</button>}
        {saved&&<span className="badge bg" style={{padding:"5px 10px",fontSize:9}}> SAVED</span>}
      </>}>
      <div className="g2" style={{gap:14}}>
        <div>
          <div style={{textAlign:"center",padding:"14px 0"}}>
            <div className={`v-viz${rec?" v-on":""}`} style={{marginBottom:14}}>
              {[40,60,80,100,80,60,40].map((h,i)=><div key={i} className="v-bar" style={{height:rec?h:7}}/>)}
            </div>
            <button className={`btn ${rec?"btn-g":"btn-p"}`} style={{borderRadius:20,height:40,width:110,justifyContent:"center",fontSize:12}} onClick={rec?stopRec:startRec}>
              {rec?" Stop":" Speak"}
            </button>
            <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--t3)",marginTop:6,letterSpacing:1}}>{rec?"- RECORDING":"CLICK MIC TO START"}</div>
          </div>
          <div className="fg">
            <label className="fl">Transcript / Type Manually</label>
            <textarea className="fi" rows={3} placeholder='Try: "Arun paid 590 today"' value={trans} onChange={e=>setTrans(e.target.value)} style={{resize:"vertical",lineHeight:1.6}}/>
          </div>
          <button className="btn btn-p" style={{width:"100%",justifyContent:"center"}} onClick={process} disabled={!trans.trim()||loading}>
            {loading?<><span className="spin"></span> Processing...</>:"Extract Payment Data"}
          </button>
          <div style={{marginTop:11}}>
            <div className="fl mb8">SAMPLE PHRASES</div>
            {SAMPLES.map(p=>(
              <div key={p} onClick={()=>setTrans(p)} style={{display:"flex",marginBottom:4,cursor:"pointer",background:"var(--bg3)",border:"1px solid var(--bd)",borderRadius:4,padding:"4px 8px",fontSize:10,color:"var(--t3)",fontFamily:"var(--mono)"}}>
                "{p}"
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="fl mb8">PARSED PAYMENT</div>
          {!result&&!loading&&<div className="ai-p ai-empty"><div style={{fontSize:26,marginBottom:7}}>-</div><div style={{fontSize:11,color:"var(--t3)"}}>Speak or type a payment and AI extracts structured data</div></div>}
          {loading&&<div className="ai-p ai-empty"><div className="spin" style={{fontSize:22}}></div><div style={{fontSize:11,color:"var(--t3)",marginTop:7}}>Parsing...</div></div>}
          {result&&!result.error&&(
            <div className="ai-p">
              <div className="bw mb8">
                <span className="fl" style={{margin:0}}>CONFIDENCE</span>
                <span style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:600,color:cc[result.confidence]}}>- {(result.confidence||"").toUpperCase()}</span>
              </div>
              {[["Borrower",result.borrowerName,null],["Amount",result.amount?fmt(result.amount):null,null],["Type",result.paymentType,ptColor(result.paymentType)],["Date",result.date,null]].map(([l,v,c])=>v?(
                <div className="ai-fld" key={l}><span className="ai-fl">{l}</span><span className="ai-fv" style={c?{color:c}:{}}>{v}</span></div>
              ):null)}
              {result.notes&&<div style={{marginTop:7,padding:"5px 7px",background:"var(--bg)",borderRadius:3,fontSize:11,color:"var(--t3)"}}>{result.notes}</div>}
              {result.summary&&<div style={{marginTop:9,padding:"7px 9px",background:"rgba(245,166,35,.07)",borderRadius:4,fontSize:11,color:"var(--am)",fontStyle:"italic"}}>"{result.summary}"</div>}
            </div>
          )}
          {result?.error&&<div className="ai-p" style={{borderColor:"rgba(231,76,60,.3)"}}><div style={{color:"var(--re)",fontWeight:600,marginBottom:4}}>Parse Failed</div><div style={{fontSize:11,color:"var(--t3)"}}>{result.summary}</div></div>}
        </div>
      </div>
    </Modal>
  );
}

//  DASHBOARD 
function Dashboard({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const [reloadKey, setReloadKey] = useState(0);
  const [showImg,   setShowImg]   = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [exporting, setExporting] = useState(false);

  const [loading, setLoading]   = useState(true);
  const [stats, setStats]       = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    const onChanged = () => setReloadKey(k => k + 1);
    window.addEventListener("lt:dataChanged", onChanged);
    return () => window.removeEventListener("lt:dataChanged", onChanged);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const today = localISODate();
        const [st, pay] = await Promise.all([
          apiJson(`${apiBase}/api/dashboard/stats`, { token }),
          apiJson(`${apiBase}/api/payments?payment_date=${today}&limit=200`, { token }),
        ]);
        setStats(st);
        setPayments(Array.isArray(pay) ? pay : []);
      } catch (e) {
        addToast(e.message || "Could not load dashboard","error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token, addToast, reloadKey]);

  const totalToday = stats?.today_collected ?? 0;
  const pieData = stats?.payment_status_pie || [];

  const weeklyRaw = stats?.weekly_data || [];
  const weeklyTarget = weeklyRaw.length ? Math.round(weeklyRaw.reduce((s, d) => s + (d.collected || 0), 0) / weeklyRaw.length) : 0;
  const weeklyData = weeklyRaw.map(d => ({ day: d.day, collected: d.collected || 0, target: weeklyTarget }));

  const exportExcel = async () => {
    if (!isAdmin) return addToast("Only admin can export Excel", "error");
    setExporting(true);
    try {
      const dTo = localISODate();
      const dt = new Date();
      dt.setDate(dt.getDate() - 29);
      const dFrom = localISODate(dt);
      const blob = await apiBlob(`${apiBase}/api/dashboard/export/excel?date_from=${dFrom}&date_to=${dTo}`, { token });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jayfin_report_${dFrom}_${dTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addToast("Excel downloaded", "success");
    } catch (e) {
      addToast(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  const perfRaw = stats?.collector_perf || [];
  const maxCollected = perfRaw.reduce((m, c) => Math.max(m, c.collected || 0), 0) || 1;
  const collData = perfRaw.map(c => ({
    id: c.id,
    name: (c.name || c.id || "").split(" ")[0],
    fullName: c.name,
    zone: c.zone,
    assigned: c.assigned || 0,
    active: !!c.active,
    collected: c.collected || 0,
    target: maxCollected,
  }));

  return (
    <div className="page">
      <div className="ph">
        <div>
          <div className="pt"><Bi en="DASHBOARD"/></div>
          <div className="ps">{localISODate()} - LIVE</div>
        </div>
        <div className="row">
          <button className="btn btn-pu" onClick={()=>setShowVoice(true)}><Bi en="Voice Entry"/></button>
          <button className="btn btn-su" onClick={()=>setShowImg(true)}><Bi en="Scan Image"/></button>
          <button className="btn btn-p btn-sm" onClick={exportExcel} disabled={exporting || loading}><Bi en={exporting ? "Exporting..." : "Export"} /></button>
        </div>
      </div>

      <div className="sg">
        {[
          {l:"Today's Collection", v:fmt(totalToday), s:`${payments.length} entries`, c:"Live", up:true, ac:"var(--am)"},
          {l:"Active Loans",       v:String(stats?.active_loans ?? "-"), s:`${fmt(stats?.total_outstanding ?? 0)} outstanding`, c:"Live", up:true, ac:"var(--bl)"},
          {l:"Collectors Active",  v:`${stats?.active_collectors ?? "-"}/${stats?.total_collectors ?? "-"}`, s:"Status", c:"Live", up:true, ac:"var(--gr)"},
          {l:"Overdue Accounts",   v:String(stats?.overdue_accounts ?? "-"), s:`${fmt(stats?.overdue_amount ?? 0)} due today`, c:"Live", up:false, ac:"var(--re)"},
        ].map(s=>(
          <div className="sc" key={s.l} style={{"--accent":s.ac}}>
            <div className="sl"><Bi en={s.l}/></div>
            <div className="sv">{s.v}</div>
            <div className="ss">{s.s}</div>
            <div className={`schange ${s.up?"up":"down"}`}>{s.c}</div>
          </div>
        ))}
      </div>

      {/*  Chart row */}
      <div className="g2 mt14">
        {/* Weekly collection chart */}
        <div className="card">
          <div className="ch">
            <span className="ct"><Bi en="Weekly Collections"/></span>
            <div className="row">
              {["bar","line"].map(v=>(
                <button key={v} className={`btn btn-sm ${chartType===v?"btn-p":"btn-g"}`} onClick={()=>setChartType(v)}>
                  {v==="bar"?"":""}
                </button>
              ))}
            </div>
          </div>
          <div className="cb" style={{paddingTop:6}}>
            <ResponsiveContainer width="100%" height={150}>
              {chartType==="bar"
                ? <BarChart data={weeklyData} margin={{top:4,right:2,left:-22,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false}/>
                    <XAxis dataKey="day" tick={{fill:"var(--t3)",fontSize:8,fontFamily:"var(--mono)"}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fill:"var(--t3)",fontSize:8}} axisLine={false} tickLine={false} tickFormatter={v=>`${v/1000}k`}/>
                    <Tooltip content={<CT/>} cursor={{fill:"rgba(255,255,255,.03)"}}/>
                    <Bar dataKey="collected" fill="var(--am)"  name="Collected" radius={[3,3,0,0]} maxBarSize={24}/>
                    <Bar dataKey="target"    fill="var(--bg3)" name="Target"    radius={[3,3,0,0]} maxBarSize={24}/>
                  </BarChart>
                : <LineChart data={weeklyData} margin={{top:4,right:2,left:-22,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)"/>
                    <XAxis dataKey="day" tick={{fill:"var(--t3)",fontSize:8,fontFamily:"var(--mono)"}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fill:"var(--t3)",fontSize:8}} axisLine={false} tickLine={false} tickFormatter={v=>`${v/1000}k`}/>
                    <Tooltip content={<CT/>}/>
                    <Line type="monotone" dataKey="collected" stroke="var(--am)"  strokeWidth={2} dot={{fill:"var(--am)",r:3}} name="Collected"/>
                    <Line type="monotone" dataKey="target"    stroke="var(--t3)"  strokeWidth={1} strokeDasharray="4 4" dot={false} name="Target"/>
                  </LineChart>
              }
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie + collector chart stacked */}
        <div style={{display:"flex",flexDirection:"column",gap:11}}>
          {/* Pie */}
          <div className="card">
        <div className="ch"><span className="ct"><Bi en="Today's Status"/></span></div>
            <div className="cb" style={{display:"flex",alignItems:"center",gap:8,paddingTop:6,paddingBottom:8}}>
              <PieChart width={80} height={80}>
                <Pie data={pieData} cx={36} cy={36} innerRadius={22} outerRadius={38} dataKey="value" paddingAngle={3}>
                  {pieData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i]}/>)}
                </Pie>
              </PieChart>
              <div style={{flex:1}}>
                {pieData.map((d,i)=>(
                  <div key={d.name} className="bw" style={{marginBottom:4}}>
                    <div className="row" style={{gap:5}}>
                      <div style={{width:6,height:6,borderRadius:1,background:PIE_COLORS[i],flexShrink:0}}/>
                      <span style={{fontSize:11,color:"var(--t2)"}}>{d.name}</span>
                    </div>
                    <span style={{fontFamily:"var(--mono)",fontSize:11,color:PIE_COLORS[i],fontWeight:600}}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*  Collector performance (compact scrollable) + horizontal bar chart */}
      <div className="g2 mt14">
        <div className="card">
          <div className="ch"><span className="ct"><Bi en="Collector Performance"/></span><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>Today</span></div>
          <div className="cb" style={{paddingTop:6,paddingBottom:6}}>
            <div className="perf-list">
              {collData.map(c=>{
                const pct=Math.round((c.collected/c.target)*100);
                return (
                  <div className="perf-row" key={c.id}>
                    <div className="av" style={{width:24,height:24,fontSize:8,flexShrink:0}}>{initials(splitBilingual(c.fullName||c.name).en)}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div className="perf-name"><BiName value={c.fullName||c.name}/></div>
                      <div className="perf-zone">{c.zone||`Assigned: ${c.assigned}`}</div>
                      <div className="prog prog-sm" style={{marginTop:4}}>
                        <div className="pf" style={{width:`${pct}%`,background:c.active?"var(--am)":"var(--t3)"}}/>
                      </div>
                    </div>
                    <div style={{flexShrink:0,textAlign:"right"}}>
                      <div className="perf-amt">{fmt(c.collected)}</div>
                      <span className={`badge ${c.active?"bg":"bgr"}`} style={{marginTop:2}}>{c.active?"ON":"OFF"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Horizontal bar: collected vs target */}
        <div className="card">
          <div className="ch"><span className="ct"><Bi en="Collected vs Top"/></span></div>
          <div className="cb" style={{paddingTop:6}}>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={collData} layout="vertical" margin={{top:0,right:8,left:8,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" horizontal={false}/>
                <XAxis type="number" tick={{fill:"var(--t3)",fontSize:7}} tickFormatter={v=>`${v/1000}k`} axisLine={false} tickLine={false}/>
                <YAxis type="category" dataKey="name" tick={{fill:"var(--t2)",fontSize:9,fontFamily:"var(--mono)"}} axisLine={false} tickLine={false} width={38}/>
                <Tooltip content={<CT/>} cursor={{fill:"rgba(255,255,255,.03)"}}/>
                <Bar dataKey="target"    fill="var(--bg3)" name="Target"    radius={[0,3,3,0]}/>
                <Bar dataKey="collected" fill="var(--am)"  name="Collected" radius={[0,3,3,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/*  Today's payments log */}
      <div className="card mt14">
        <div className="ch"><span className="ct">Today's Payment Log</span><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>{payments.length} entries</span></div>
        <div className="tw">
          <table>
            <thead><tr><th>Time</th><th>Borrower</th><th>Collector</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td className="mono" colSpan={5}>Loading</td></tr>
              ) : payments.map(p=>{
                const b=p.borrower;
                const c=p.collector;
                const st=upper(p.payment_type);
                return (
                  <tr key={p.id}>
                    <td className="mono">{p.payment_time || ""}</td>
                    <td style={{fontWeight:700}}>{b?.name ? <BiName value={b.name}/> : ""}</td>
                    <td style={{color:"var(--t3)"}}>{c?.name ? <BiName value={c.name}/> : ""}</td>
                    <td className="mono am">{fmt(p.amount)}</td>
                    <td><span className={`badge ${st==="PAID"?"bg":st==="PARTIAL"?"bo":"br"}`}><Bi en={st}/></span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showImg   && <ImageScanModal onClose={()=>setShowImg(false)}   addToast={addToast} token={token} apiBase={apiBase}/>}
      {showVoice && <VoiceModal     onClose={()=>setShowVoice(false)} addToast={addToast} token={token} apiBase={apiBase}/>}
    </div>
  );
}

//  COLLECTORS PAGE 
function CollectorsPage({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [collectors, setColl] = useState([]);
  const [sort, setSort]       = useState({ key: "name", dir: "asc" });
  const [f, setF]             = useState({name:"",nameTa:"",phone:"",zone:"",password:""});
  const [edit, setEdit]       = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const suggestTamil = useTamilSuggestor({ apiBase, token });

  const loadCollectors = useCallback(async () => {
    try {
      const data = await apiJson(`${apiBase}/api/collectors`, { token });
      setColl(Array.isArray(data) ? data : []);
    } catch (e) {
      addToast(e, "error");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token, addToast]);

  // Load collectors from backend
  useEffect(() => {
    setLoading(true);
    loadCollectors();
  }, [loadCollectors]);

  const save = async () => {
    if (!isAdmin) return addToast("Admin access required","error");
    if (!f.name || !f.phone || !f.password) {
      return addToast("Name, phone & password required","error");
    }
    setSaving(true);
    try {
      const phoneDigits = String(f.phone || "").replace(/\D/g, "");
      const created = await apiJson(`${apiBase}/api/collectors`, {
        token,
        method: "POST",
        body: {
          name: f.nameTa ? `${f.name} | ${f.nameTa}` : f.name,
          email: `collector-${phoneDigits}@loantrack.app`,
          phone: phoneDigits || f.phone,
          zone: f.zone,
          password: f.password,
        },
      });
      setShow(false);
      setF({name:"",nameTa:"",phone:"",zone:"",password:""});
      addToast(`${created?.name || f.name} created`, "success");
      await loadCollectors();
    } catch (_) {
      addToast(_, "error");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c) => {
    const n = splitBilingual(c?.name);
    setEdit({
      id: c.id,
      name: n.en || "",
      nameTa: n.ta || "",
      phone: c.phone || "",
      zone: c.zone || "",
      is_active: !!c.is_active,
    });
  };

  const saveEdit = async () => {
    if (!isAdmin) return addToast("Admin access required", "error");
    if (!edit?.id) return;
    if (!edit.name || !edit.phone) return addToast("Name and phone required", "error");
    setEditSaving(true);
    try {
      const phoneDigits = String(edit.phone || "").replace(/\D/g, "");
      await apiJson(`${apiBase}/api/collectors/${edit.id}`, {
        token,
        method: "PUT",
        body: {
          name: edit.nameTa ? `${edit.name} | ${edit.nameTa}` : edit.name,
          phone: phoneDigits || edit.phone,
          zone: edit.zone,
          is_active: !!edit.is_active,
        },
      });
      addToast("Collector updated", "success");
      setEdit(null);
      await loadCollectors();
    } catch (e) {
      addToast(e, "error");
    } finally {
      setEditSaving(false);
    }
  };

  // For now we don't have per-collector totals from backend; keep 0s so charts still render.
  const collData = collectors.map(c=>({
    name: String(c?.name || "").split(" ")[0] || "Collector",
    collected: 0,
    target: 0,
  }));

  const sortedCollectors = useMemo(() => {
    const typeByKey = {
      phone: "number",
      assigned: "number",
      collected: "number",
      status: "bool",
    };

    const getValue = (c, key) => {
      if (key === "name") return splitBilingual(c?.name).en || "";
      if (key === "phone") return c.phone;
      if (key === "zone") return c.zone;
      if (key === "assigned") return c.assigned_borrowers ?? 0;
      if (key === "collected") return c.total_collected ?? 0;
      if (key === "status") return !!c.is_active;
      return "";
    };

    return sortRows(collectors, sort, getValue, typeByKey);
  }, [collectors, sort]);

  const collectorsColumns = useMemo(
    () => [
      { key: "name" },
      { key: "phone" },
      { key: "zone" },
      { key: "assigned" },
      { key: "collected" },
      { key: "status" },
      { key: null },
    ],
    []
  );
  const collectorsTableRef = useRef(null);
  const onCollectorsTableClick = useSortableHeader({
    tableRef: collectorsTableRef,
    columns: collectorsColumns,
    sort,
    setSort,
  });

  return (
    <div className="page">
      <div className="ph">
          <div>
          <div className="pt"><Bi en="COLLECTORS"/></div>
          <div className="ps"><Bi en="FIELD AGENTS"/></div>
        </div>
        {isAdmin && <button className="btn btn-p" onClick={()=>setShow(true)}><Bi en="+ Add Collector"/></button>}
      </div>
      <div className="sg" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
        <div className="sc"><div className="sl"><Bi en="Total"/></div><div className="sv">{collectors.length}</div></div>
        <div className="sc" style={{"--accent":"var(--gr)"}}><div className="sl"><Bi en="Active"/></div><div className="sv">{collectors.filter(c=>c.is_active).length}</div></div>
        <div className="sc" style={{"--accent":"var(--re)"}}><div className="sl"><Bi en="Inactive"/></div><div className="sv">{collectors.filter(c=>!c.is_active).length}</div></div>
      </div>
      <div className="g2 mt14">
        <div className="card" style={{gridColumn:"1/-1"}}>
          <div className="ch"><span className="ct"><Bi en="Collector Comparison"/></span></div>
          <div className="cb" style={{paddingTop:6}}>
            <ResponsiveContainer width="100%" height={130}>
              <BarChart data={collData} margin={{top:4,right:4,left:-22,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false}/>
                <XAxis dataKey="name" tick={{fill:"var(--t3)",fontSize:9,fontFamily:"var(--mono)"}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:"var(--t3)",fontSize:8}} axisLine={false} tickLine={false} tickFormatter={v=>`${v/1000}k`}/>
                <Tooltip content={<CT/>} cursor={{fill:"rgba(255,255,255,.03)"}}/>
                <Bar dataKey="target"    fill="var(--bg3)" name="Target"    radius={[3,3,0,0]} maxBarSize={32}/>
                <Bar dataKey="collected" fill="var(--am)"  name="Collected" radius={[3,3,0,0]} maxBarSize={32}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="card mt14">
        <div className="ch"><span className="ct"><Bi en="All Collectors"/></span></div>
        <div className="tw">
          <table ref={collectorsTableRef} className="sort-table" onClick={onCollectorsTableClick}>
            <thead><tr><th><Bi en="Name"/></th><th><Bi en="Phone"/></th><th><Bi en="Zone"/></th><th><Bi en="Assigned"/></th><th><Bi en="Collected"/></th><th><Bi en="Status"/></th><th></th></tr></thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{textAlign:"center",padding:20,fontSize:12,color:"var(--t3)"}}>Loading collectors</td></tr>
              )}
              {!loading && sortedCollectors.map(c=>(
                <tr key={c.id}>
                  <td>
                    <div className="row">
                      <div className="av">{initials(splitBilingual(c.name).en)}</div>
                      <div style={{fontWeight:700}}><BiName value={c.name}/></div>
                    </div>
                  </td>
                  <td className="mono">{c.phone}</td>
                  <td>{c.zone}</td>
                  <td className="mono">{c.assigned_borrowers ?? 0}</td>
                  <td className="mono am">{fmt(c.total_collected ?? 0)}</td>
                  <td><span className={`badge ${c.is_active?"bg":"br"}`}>{c.is_active?"Active":"Inactive"}</span></td>
                  <td><button className="btn btn-g btn-sm" onClick={()=>openEdit(c)}><Bi en="Edit"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {show&&<Modal title="ADD COLLECTOR" onClose={()=>setShow(false)} footer={<><button className="btn btn-g" onClick={()=>setShow(false)}><Bi en="Cancel"/></button><button className="btn btn-p" onClick={save} disabled={saving}>{saving?"Creating":"Create"}</button></>}>
        <div className="fgrid">
          {[["name","Full Name","text"],["nameTa","Tamil Name","text"],["phone","Phone","tel"],["zone","Zone","text"],["password","Password","password"]].map(([k,l,t])=>(
            <div className="fg" key={k}>
              <label className="fl">{l}</label>
              <input
                className="fi"
                type={t}
                placeholder={l}
                value={f[k]}
                onChange={e=>{
                  const v = e.target.value;
                  if (k === "name") {
                    setF(p=>({...p,name:v}));
                    suggestTamil("collector:add:name", v, {
                      forPlace: false,
                      apply: (ta) => setF(p => (p.name === v ? { ...p, nameTa: ta } : p)),
                    });
                    return;
                  }
                  if (k === "nameTa") return setF(p=>({...p,nameTa:v,name: v ? tamilToLatin(v) : ""}));
                  return setF(p=>({...p,[k]:v}));
                }}
              />
            </div>
          ))}
        </div>
      </Modal>}

      {edit&&<Modal title="EDIT COLLECTOR" onClose={()=>setEdit(null)} footer={<><button className="btn btn-g" onClick={()=>setEdit(null)}><Bi en="Cancel"/></button><button className="btn btn-p" onClick={saveEdit} disabled={editSaving}>{editSaving?"Saving":"Save"}</button></>}>
        <div className="fgrid">
          <div className="fg"><label className="fl">Full Name</label><input className="fi" placeholder="Full Name" value={edit.name} onChange={e=>{const v=e.target.value; setEdit(p=>({...p,name:v})); suggestTamil("collector:edit:name", v, { forPlace:false, apply:(ta)=>setEdit(p=>(p && p.name===v ? ({...p,nameTa:ta}) : p)) });}}/></div>
          <div className="fg"><label className="fl">Tamil Name</label><input className="fi" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD \\u0BAA\\u0BC6\\u0BAF\\u0BB0\\u0BCD" value={edit.nameTa} onChange={e=>{const v=e.target.value; setEdit(p=>({...p,nameTa:v,name: v ? tamilToLatin(v) : ""}));}}/></div>
          <div className="fg"><label className="fl">Phone</label><input className="fi" placeholder="10-digit" value={edit.phone} onChange={e=>setEdit(p=>({...p,phone:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Zone</label><input className="fi" placeholder="Zone" value={edit.zone} onChange={e=>setEdit(p=>({...p,zone:e.target.value}))}/></div>
        </div>
        <div className="row" style={{marginTop:10,justifyContent:"space-between"}}>
          <div className="fl" style={{margin:0}}><Bi en="Active" /></div>
          <input type="checkbox" checked={!!edit.is_active} onChange={e=>setEdit(p=>({...p,is_active:e.target.checked}))}/>
        </div>
      </Modal>}
    </div>
  );
}

//  BORROWERS PAGE (disabled: borrower creation moved to Loans)
// eslint-disable-next-line no-unused-vars
function BorrowersPage({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const [reloadKey, setReloadKey] = useState(0);
  const [q, setQ]               = useState("");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [borrowers, setBorr]    = useState([]);
  const [collectors, setColl]   = useState([]);
  const [loans, setLoans]       = useState([]);
  const [sort, setSort]         = useState({ key: "name", dir: "asc" });
  const suggestTamil = useTamilSuggestor({ apiBase, token });

  const emptyBorrowerForm = useMemo(
    () => ({ id:"", name:"", nameTa:"", phone:"", address:"", addressTa:"", collectorId:"" }),
    []
  );
  const emptyLoanForm = useMemo(
    () => ({
      id:"",
      borrowerId:"",
      principal:"",
      interestRate:"",
      frequency:"DAILY",
      startDate: localISODate(),
      installmentAmt:"",
      totalInstallments:"100",
      status:"ACTIVE",
      notes:"",
    }),
    []
  );

  const [selectedBorrowerId, setSelectedBorrowerId] = useState("");
  const [bf, setBf] = useState(emptyBorrowerForm);
  const [lf, setLf] = useState(emptyLoanForm);

  useEffect(() => {
    const onChanged = () => setReloadKey(k => k + 1);
    window.addEventListener("lt:dataChanged", onChanged);
    return () => window.removeEventListener("lt:dataChanged", onChanged);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [b, c, l] = await Promise.all([
          apiJson(`${apiBase}/api/borrowers?limit=200`, { token }),
          apiJson(`${apiBase}/api/collectors`, { token }),
          apiJson(`${apiBase}/api/loans?limit=500`, { token }),
        ]);
        setBorr(Array.isArray(b) ? b : []);
        setColl(Array.isArray(c) ? c : []);
        setLoans(Array.isArray(l) ? l : []);
      } catch (e) {
        addToast(e.message || "Could not load borrowers","error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token, addToast, reloadKey]);

  const collectorById = useMemo(() => {
    const m = new Map();
    collectors.forEach(c => m.set(c.id, c));
    return m;
  }, [collectors]);

  const borrowerById = useMemo(() => {
    const m = new Map();
    borrowers.forEach(b => {
      if (b?.id) m.set(b.id, b);
    });
    return m;
  }, [borrowers]);

  const loanByBorrowerId = useMemo(() => {
    const m = new Map();
    loans.forEach(l => {
      const bid = l?.borrower_id;
      if (!bid) return;
      const prev = m.get(bid);
      if (!prev) {
        m.set(bid, l);
        return;
      }
      if (prev?.status !== "ACTIVE" && l?.status === "ACTIVE") {
        m.set(bid, l);
        return;
      }
      const prevDate = String(prev?.start_date || "");
      const curDate = String(l?.start_date || "");
      if (curDate && curDate > prevDate) m.set(bid, l);
    });
    return m;
  }, [loans]);

  const filtered = borrowers.filter(b=>b.name.toLowerCase().includes(q.toLowerCase())||b.phone.includes(q));

  const sortedBorrowers = useMemo(() => {
    const typeByKey = { phone: "number", principal: "number", installment: "number", remaining: "number" };
    const getValue = (b, key) => {
      if (key === "name") return splitBilingual(b?.name).en || "";
      if (key === "phone") return b?.phone;
      if (key === "principal") return loanByBorrowerId.get(b?.id)?.principal ?? null;
      if (key === "installment") return loanByBorrowerId.get(b?.id)?.installment_amt ?? null;
      if (key === "freq") return loanByBorrowerId.get(b?.id)?.frequency ?? "";
      if (key === "remaining") return loanByBorrowerId.get(b?.id)?.remaining_balance ?? null;
      if (key === "collector") return splitBilingual(collectorById.get(b?.collector_id)?.name).en || "";
      if (key === "address") return b?.address;
      if (key === "status") return b?.status;
      return "";
    };
    return sortRows(filtered, sort, getValue, typeByKey);
  }, [collectorById, filtered, loanByBorrowerId, sort]);

  const borrowersColumns = useMemo(
    () => [
      { key: "name" },
      { key: "phone" },
      { key: "principal" },
      { key: "installment" },
      { key: "freq" },
      { key: "remaining" },
      { key: "collector" },
      { key: "address" },
      { key: "status" },
    ],
    []
  );
  const borrowersTableRef = useRef(null);
  const onBorrowersTableClick = useSortableHeader({
    tableRef: borrowersTableRef,
    columns: borrowersColumns,
    sort,
    setSort,
  });

  const selectBorrower = (id) => {
    const bid = String(id || "");
    setSelectedBorrowerId(bid);
    if (!bid) {
      setBf(emptyBorrowerForm);
      setLf(emptyLoanForm);
      return;
    }
    const b = borrowerById.get(bid);
    const l = loanByBorrowerId.get(bid);
    const bn = splitBilingual(b?.name);
    const an = splitBilingual(b?.address);
    setBf({
      id: bid,
      name: bn.en || "",
      nameTa: bn.ta || "",
      phone: b?.phone || "",
      address: an.en || "",
      addressTa: an.ta || "",
      collectorId: b?.collector_id || "",
    });
    setLf({
      id: l?.id || "",
      borrowerId: bid,
      principal: l?.total_payable != null ? String(l.total_payable) : (l?.principal != null ? String(l.principal) : ""),
      interestRate: l?.interest_amount != null
        ? String(l.interest_amount)
        : (l?.total_payable != null && l?.principal != null ? String(Math.round((parseFloat(l.total_payable) - parseFloat(l.principal)) * 100) / 100) : ""),
      frequency: String(l?.frequency || "DAILY"),
      startDate: l?.start_date ? String(l.start_date).slice(0,10) : localISODate(),
      installmentAmt: l?.installment_amt != null ? String(l.installment_amt) : "",
      totalInstallments: l?.total_installments != null ? String(l.total_installments) : "100",
      status: l?.status || "ACTIVE",
      notes: l?.notes || "",
    });
  };

  useEffect(() => {
    if (!selectedBorrowerId) return;
    if (!borrowerById.get(selectedBorrowerId)) {
      selectBorrower("");
      return;
    }
    // keep in sync when data reloads
    selectBorrower(selectedBorrowerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const tp = lf.principal ? Math.round(parseFloat(lf.principal)) : 0;

  const saveAll = async () => {
    if (!isAdmin) return addToast("Only admin can add/edit borrower + loan","error");
    if (!bf.name || !bf.phone) return addToast("Borrower name and phone required","error");
    if (!lf.principal || !lf.installmentAmt || !lf.totalInstallments) {
      return addToast("Loan principal, installment and total installments required","error");
    }

    setSaving(true);
    try {
      const borrowerBody = {
        name: bf.nameTa ? `${bf.name} | ${bf.nameTa}` : bf.name,
        phone: bf.phone,
        address: bf.addressTa ? `${bf.address} | ${bf.addressTa}` : bf.address,
        collector_id: bf.collectorId || null,
      };

      let borrowerId = selectedBorrowerId;
      let savedBorrower = null;
      if (borrowerId) {
        savedBorrower = await apiJson(`${apiBase}/api/borrowers/${borrowerId}`, {
          token,
          method: "PUT",
          body: borrowerBody,
        });
        setBorr(p => p.map(x => x.id === savedBorrower.id ? savedBorrower : x));
      } else {
        savedBorrower = await apiJson(`${apiBase}/api/borrowers`, {
          token,
          method: "POST",
          body: borrowerBody,
        });
        borrowerId = savedBorrower?.id || "";
        if (!borrowerId) throw new Error("Borrower created but id missing");
        setBorr(p => [...p, savedBorrower]);
      }

      const loanBody = {
        borrower_id: borrowerId,
        principal: parseFloat(lf.principal),
        interest_amount: parseFloat(lf.interestRate || "0"),
        interest_rate: 0,
        installment_amt: parseFloat(lf.installmentAmt),
        frequency: upper(lf.frequency || "DAILY"),
        start_date: lf.startDate || null,
        total_installments: parseInt(lf.totalInstallments, 10),
        status: lf.status || "ACTIVE",
        notes: lf.notes || null,
      };

      let savedLoan = null;
      if (lf.id) {
        savedLoan = await apiJson(`${apiBase}/api/loans/${lf.id}`, {
          token,
          method: "PUT",
          body: loanBody,
        });
        setLoans(p => p.map(x => x.id === savedLoan.id ? savedLoan : x));
      } else {
        savedLoan = await apiJson(`${apiBase}/api/loans`, {
          token,
          method: "POST",
          body: loanBody,
        });
        setLoans(p => [savedLoan, ...p]);
      }

      setSelectedBorrowerId(borrowerId);
      addToast(selectedBorrowerId ? "Borrower + loan updated" : "Borrower + loan created","success");
      emitDataChanged();

      const bn = splitBilingual(savedBorrower?.name);
      const an = splitBilingual(savedBorrower?.address);
      setBf({
        id: borrowerId,
        name: bn.en || "",
        nameTa: bn.ta || "",
        phone: savedBorrower?.phone || "",
        address: an.en || "",
        addressTa: an.ta || "",
        collectorId: savedBorrower?.collector_id || "",
      });
      setLf({
        id: savedLoan?.id || "",
        borrowerId,
        principal: savedLoan?.total_payable != null ? String(savedLoan.total_payable) : (savedLoan?.principal != null ? String(savedLoan.principal) : ""),
        interestRate: savedLoan?.interest_amount != null
          ? String(savedLoan.interest_amount)
          : (savedLoan?.total_payable != null && savedLoan?.principal != null ? String(Math.round((parseFloat(savedLoan.total_payable) - parseFloat(savedLoan.principal)) * 100) / 100) : ""),
        frequency: String(savedLoan?.frequency || "DAILY"),
        startDate: savedLoan?.start_date ? String(savedLoan.start_date).slice(0,10) : localISODate(),
        installmentAmt: savedLoan?.installment_amt != null ? String(savedLoan.installment_amt) : "",
        totalInstallments: savedLoan?.total_installments != null ? String(savedLoan.total_installments) : "100",
        status: savedLoan?.status || "ACTIVE",
        notes: savedLoan?.notes || "",
      });
    } catch (e) {
      addToast(e.message || "Failed to save borrower + loan","error");
    } finally {
      setSaving(false);
    }
  };

  const clearForm = () => {
    setSelectedBorrowerId("");
    setBf(emptyBorrowerForm);
    setLf(emptyLoanForm);
  };
  return (
    <div className="page">
      <div className="ph">
        <div>
          <div className="pt"><Bi en="BORROWERS"/></div>
          <div className="ps"><Bi en="BORROWER + LOAN (Single Page)"/></div>
        </div>
      </div>

      {isAdmin && (
        <div className="card mt14">
          <div className="ch">
            <span className="ct"><Bi en={selectedBorrowerId ? "EDIT BORROWER + LOAN" : "ADD BORROWER + LOAN"} /></span>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-g btn-sm" onClick={clearForm} disabled={saving}><Bi en="Clear" /></button>
              <button className="btn btn-p btn-sm" onClick={saveAll} disabled={saving || loading}>
                {saving ? "Saving..." : (selectedBorrowerId ? "Update" : "Add")}
              </button>
            </div>
          </div>
          <div className="cb">
            <div className="fg">
              <label className="fl"><Bi en="Borrower" /></label>
              <select className="fi" value={selectedBorrowerId} onChange={e=>selectBorrower(e.target.value)} disabled={saving}>
                <option value="">New borrower</option>
                {sortedBorrowers.map(b=>{
                  const bn = splitBilingual(b.name);
                  return <option key={b.id} value={b.id}>{bn.en}{bn.ta ? ` (${bn.ta})` : ""}  {b.phone ? `- ${b.phone}` : ""}</option>;
                })}
              </select>
            </div>

            <div className="fgrid">
              <div className="fg"><label className="fl">Full Name</label><input className="fi" placeholder="Full Name" value={bf.name} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,name:v})); suggestTamil("borrower:name", v, { forPlace:false, apply:(ta)=>setBf(p=>(p.name===v ? ({...p,nameTa:ta}) : p)) });}}/></div>
              <div className="fg"><label className="fl">Phone</label><input className="fi" placeholder="10-digit" value={bf.phone} disabled={saving} onChange={e=>setBf(p=>({...p,phone:e.target.value}))}/></div>
            </div>
            <div className="fg"><label className="fl">Tamil Name</label><input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD \\u0BAA\\u0BC6\\u0BAF\\u0BB0\\u0BCD" value={bf.nameTa} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,nameTa:v,name: v ? tamilToLatin(v) : ""}));}}/></div>
            <div className="fgrid">
              <div className="fg"><label className="fl">Address (English)</label><input className="fi" placeholder="Full address" value={bf.address} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,address:v})); suggestTamil("borrower:address", v, { forPlace:true, apply:(ta)=>setBf(p=>(p.address===v ? ({...p,addressTa:ta}) : p)) });}}/></div>
              <div className="fg"><label className="fl">Address (Tamil)</label><input className="fi ta-input" placeholder="\\u0BAE\\u0BC1\\u0B95\\u0BB5\\u0BB0\\u0BBF" value={bf.addressTa} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,addressTa:v,address: v ? tamilToLatin(v) : ""}));}}/></div>
            </div>
            <div className="fg">
              <label className="fl">Assign Collector</label>
              <select className="fi" value={bf.collectorId} disabled={saving} onChange={e=>setBf(p=>({...p,collectorId:e.target.value}))}>
                <option value="">Select Collector</option>
                {collectors.map(c=>{
                  const cn = splitBilingual(c.name);
                  return <option key={c.id} value={c.id}>{cn.en}{cn.ta ? ` (${cn.ta})` : ""}  {c.zone || ""}</option>;
                })}
              </select>
            </div>

            <div className="hr" />

            <div className="fgrid">
              <div className="fg"><label className="fl">Start Date</label><input className="fi" type="date" value={lf.startDate} disabled={saving} onChange={e=>setLf(p=>({...p,startDate:e.target.value}))}/></div>
              <div className="fg"><label className="fl">Frequency</label>
                <select className="fi" value={lf.frequency} disabled={saving} onChange={e=>setLf(p=>({...p,frequency:e.target.value}))}>
                  <option value="DAILY">Daily</option><option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option>
                </select>
              </div>
            </div>
            <div className="fgrid">
              <div className="fg"><label className="fl">Principal (Rs)</label><input className="fi" type="number" value={lf.principal} disabled={saving} onChange={e=>setLf(p=>({...p,principal:e.target.value}))}/></div>
              <div className="fg"><label className="fl">Interest (Rs)</label><input className="fi" type="number" value={lf.interestRate} disabled={saving} onChange={e=>setLf(p=>({...p,interestRate:e.target.value}))}/></div>
              <div className="fg"><label className="fl">Installment (Rs)</label><input className="fi" type="number" value={lf.installmentAmt} disabled={saving} onChange={e=>setLf(p=>({...p,installmentAmt:e.target.value}))}/></div>
              <div className="fg"><label className="fl">Total Installments</label><input className="fi" type="number" value={lf.totalInstallments} disabled={saving} onChange={e=>setLf(p=>({...p,totalInstallments:e.target.value}))}/></div>
            </div>
            {selectedBorrowerId && (
              <div className="fgrid">
                <div className="fg"><label className="fl">Loan Status</label>
                  <select className="fi" value={lf.status} disabled={saving} onChange={e=>setLf(p=>({...p,status:e.target.value}))}>
                    <option value="ACTIVE">Active</option><option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div className="fg"><label className="fl">Notes</label><input className="fi" placeholder="Notes" value={lf.notes} disabled={saving} onChange={e=>setLf(p=>({...p,notes:e.target.value}))}/></div>
              </div>
            )}
            {tp > 0 && (
              <div style={{background:"rgba(245,166,35,.08)",border:"1px solid rgba(245,166,35,.2)",borderRadius:5,padding:"8px 11px"}}>
                <div className="bw"><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>TOTAL PAYABLE</span><span style={{fontFamily:"var(--mono)",fontSize:14,color:"var(--am)",fontWeight:600}}>{fmt(tp)}</span></div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedBorrowerId && (
        <div className="card mt14">
          <div className="ch"><span className="ct"><Bi en="DETAILS" /></span></div>
          <div className="cb">
            {(() => {
              const b = borrowerById.get(selectedBorrowerId);
              const l = loanByBorrowerId.get(selectedBorrowerId);
              if (!b) return <div className="mono">No borrower selected</div>;
              const bn = splitBilingual(b.name);
              const an = splitBilingual(b.address);
              const c = collectorById.get(b.collector_id);
              return (
                <div className="fgrid">
                  <div className="fg">
                    <label className="fl"><Bi en="Borrower" /></label>
                    <div style={{fontWeight:800}}><Bi en={bn.en} ta={bn.ta} /></div>
                    <div className="mono" style={{color:"var(--t3)",marginTop:4}}>{b.phone}</div>
                    {an.en && <div style={{marginTop:6,color:"var(--t2)",fontSize:12}}><Bi en={an.en} ta={an.ta} /></div>}
                    {c?.name && <div style={{marginTop:6,fontSize:12}}><span style={{color:"var(--t3)"}}>Collector: </span><BiName value={c.name}/></div>}
                  </div>
                  <div className="fg">
                    <label className="fl"><Bi en="Loan" /></label>
                    {l ? (
                      <div className="mono" style={{display:"grid",gridTemplateColumns:"120px 1fr",rowGap:6,columnGap:10}}>
                        <span style={{color:"var(--t3)"}}>Principal</span><span>{fmt(l.principal)}</span>
                        <span style={{color:"var(--t3)"}}>Installment</span><span>{fmt(l.installment_amt)}</span>
                        <span style={{color:"var(--t3)"}}>Frequency</span><span>{l.frequency}</span>
                        <span style={{color:"var(--t3)"}}>Start Date</span><span>{String(l.start_date || "").slice(0, 10) || "-"}</span>
                        <span style={{color:"var(--t3)"}}>Paid</span><span>{fmt(l.amount_paid || 0)}</span>
                        <span style={{color:"var(--t3)"}}>Remaining</span><span>{fmt(l.remaining_balance || 0)}</span>
                        <span style={{color:"var(--t3)"}}>Status</span><span>{l.status}</span>
                      </div>
                    ) : (
                      <div className="mono" style={{color:"var(--t3)"}}>No loan found</div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <input className="fi mb12 mt14" style={{maxWidth:270}} placeholder="Search name or phone" value={q} onChange={e=>setQ(e.target.value)}/>
      <div className="card">
        <div className="ch"><span className="ct"><Bi en="All Borrowers"/></span><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>{filtered.length}</span></div>
        <div className="tw">
          <table ref={borrowersTableRef} className="sort-table borrowers-table" onClick={onBorrowersTableClick}>
            <thead>
              <tr>
                <th><Bi en="Name" /></th>
                <th><Bi en="Phone" /></th>
                <th><Bi en="Principal" /></th>
                <th><Bi en="Installment" /></th>
                <th><Bi en="Freq" /></th>
                <th><Bi en="Remaining" /></th>
                <th><Bi en="Collector" /></th>
                <th><Bi en="Address" /></th>
                <th><Bi en="Status" /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="mono" colSpan={9}>Loading</td></tr>
              ) : sortedBorrowers.map(b=>{
                const c = collectorById.get(b.collector_id);
                const l = loanByBorrowerId.get(b.id);
                return (
                  <tr key={b.id} onClick={()=>selectBorrower(b.id)} style={{cursor:"pointer"}}>
                    <td>
                      {(() => {
                        const bn = splitBilingual(b.name);
                        return (
                          <div className="row">
                            <div className="av" style={{background:"linear-gradient(135deg,#1a2840,#2c4a7c)"}}>{initials(bn.en)}</div>
                            <div>
                              <div style={{fontWeight:700}}><Bi en={bn.en} ta={bn.ta}/></div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="mono">{b.phone}</td>
                    <td className="mono am">{l?fmt(l.principal):""}</td>
                    <td className="mono">{l?fmt(l.installment_amt):""}</td>
                    <td className="mono">{l?l.frequency:""}</td>
                    <td className="mono">{l?fmt(l.remaining_balance || 0):""}</td>
                    <td style={{fontSize:11}}>{c?.name ? <BiName value={c.name}/> : ""}</td>
                    <td style={{maxWidth:170,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--t3)",fontSize:10}}>
                      {(() => { const ad = splitBilingual(b.address); return ad.en ? <Bi en={ad.en} ta={ad.ta}/> : ""; })()}
                    </td>
                    <td><span className={`badge ${b.status==="ACTIVE"?"bg":b.status==="OVERDUE"?"br":"bgr"}`}>{b.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

//  LOANS PAGE
function LoansPage({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const [reloadKey, setReloadKey] = useState(0);
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [loans, setLoans]     = useState([]);
  const [borrowers, setBorr]  = useState([]);
  const [collectors, setColl] = useState([]);
  const [sort, setSort]       = useState({ key: "borrower", dir: "asc" });
  const [nameFilter, setNameFilter] = useState("");
  const [micListening, setMicListening] = useState(false);
  const micRef = useRef(null);
  const suggestTamil = useTamilSuggestor({ apiBase, token });
  const [bf, setBf] = useState({name:"",nameTa:"",phone:"",address:"",addressTa:"",collectorId:""});
  const [f, setF] = useState({principal:"",interestRate:"",frequency:"DAILY",startDate: localISODate(),installmentAmt:"",totalInstallments:"100"});
  const [ef, setEf] = useState({
    id: "",
    borrowerId: "",
    borrowerName: "",
    borrowerNameTa: "",
    borrowerPhone: "",
    borrowerAddress: "",
    borrowerAddressTa: "",
    principal: "",
    interestRate: "",
    frequency: "DAILY",
    startDate: "",
    installmentAmt: "",
    totalInstallments: "",
    status: "ACTIVE",
    notes: "",
  });

  useEffect(() => {
    const onChanged = () => setReloadKey(k => k + 1);
    window.addEventListener("lt:dataChanged", onChanged);
    return () => window.removeEventListener("lt:dataChanged", onChanged);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [l, b, c] = await Promise.all([
          apiJson(`${apiBase}/api/loans?limit=200`, { token }),
          apiJson(`${apiBase}/api/borrowers?limit=200`, { token }),
          apiJson(`${apiBase}/api/collectors`, { token }),
        ]);
        setLoans(Array.isArray(l) ? l : []);
        setBorr(Array.isArray(b) ? b : []);
        setColl(Array.isArray(c) ? c : []);
      } catch (e) {
        addToast(e.message || "Could not load loans","error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token, addToast, reloadKey]);

  const borrowerById = useMemo(() => {
    const m = new Map();
    borrowers.forEach(b => m.set(b.id, b));
    return m;
  }, [borrowers]);

  const collectorById = useMemo(() => {
    const m = new Map();
    collectors.forEach(c => m.set(c.id, c));
    return m;
  }, [collectors]);

  const sortedLoans = useMemo(() => {
    const typeByKey = {
      principal: "number",
      interest: "number",
      installment: "number",
      remaining: "number",
      progress: "number",
    };
    const getValue = (l, key) => {
      if (key === "borrower") return splitBilingual(borrowerById.get(l?.borrower_id)?.name).en || "";
      if (key === "principal") return l?.principal ?? 0;
      if (key === "interest") {
        if (l?.interest_amount != null) return l.interest_amount;
        return Math.max(0, (l?.total_payable || 0) - (l?.principal || 0));
      }
      if (key === "installment") return l?.installment_amt ?? 0;
      if (key === "freq") return l?.frequency;
      if (key === "remaining") return l?.remaining_balance ?? 0;
      if (key === "progress") return Math.round(((l?.amount_paid || 0) / (l?.total_payable || 1)) * 100);
      if (key === "status") return l?.status;
      return "";
    };
    return sortRows(loans, sort, getValue, typeByKey);
  }, [borrowerById, loans, sort]);

  const loansColumns = useMemo(
    () => [
      { key: "borrower" },
      { key: "principal" },
      { key: "interest" },
      { key: "installment" },
      { key: "freq" },
      { key: "remaining" },
      { key: "progress" },
      { key: "status" },
    ],
    []
  );
  const loansTableRef = useRef(null);
  const onLoansTableClick = useSortableHeader({
    tableRef: loansTableRef,
    columns: loansColumns,
    sort,
    setSort,
  });

  const toggleMic = () => {
    if (micListening) {
      try { micRef.current?.stop?.(); } catch (_) {}
      setMicListening(false);
      return;
    }

    setMicListening(true);
    micRef.current = startVoiceCapture({
      lang: "ta-IN",
      fallbackLabel: "Type borrower name",
      onText: (t) => setNameFilter(t),
      onError: (ev) => {
        const code = ev?.error || ev?.code;
        if (String(code) === "aborted") return;
        const msg = code ? `Voice input failed (${code})` : (ev?.message || "Voice input failed");
        addToast(msg, "error");
      },
      onEnd: () => setMicListening(false),
    });
  };

  useEffect(() => {
    return () => {
      try { micRef.current?.stop?.(); } catch (_) {}
    };
  }, []);

  const visibleLoans = useMemo(() => {
    const q = String(nameFilter || "").trim();
    const rows = sortedLoans
      .map((l, i) => {
        const b = borrowerById.get(l?.borrower_id);
        const score = nameMatchScore(q, b?.name || "");
        return { l, b, i, score };
      })
      .filter(x => x.score.ok);

    if (!q) return rows;
    return rows.sort((a, b) => a.score.score - b.score.score || a.i - b.i);
  }, [sortedLoans, borrowerById, nameFilter]);

  const nameFilterMeta = useMemo(() => {
    const q = String(nameFilter || "").trim();
    if (!q) return null;
    const anyExact = visibleLoans.some(v => v.score?.exact);
    return { q, count: visibleLoans.length, anyExact };
  }, [nameFilter, visibleLoans]);

  const tp = f.principal ? Math.round(parseFloat(f.principal)) : 0;

  const save = async () => {
    if (!isAdmin) return addToast("Only admin can create loans","error");
    if (!bf.name || !bf.phone) return addToast("Borrower name and phone required","error");
    if (!f.principal || !f.installmentAmt || !f.totalInstallments) {
      return addToast("Principal, installment and total installments required","error");
    }
    setSaving(true);
    let createdBorrowerId = "";
    try {
      const borrowerBody = {
        name: bf.nameTa ? `${bf.name} | ${bf.nameTa}` : bf.name,
        phone: bf.phone,
        address: bf.addressTa ? `${bf.address} | ${bf.addressTa}` : bf.address,
        collector_id: bf.collectorId || null,
      };
      const savedBorrower = await apiJson(`${apiBase}/api/borrowers`, {
        token,
        method: "POST",
        body: borrowerBody,
      });
      const borrowerId = savedBorrower?.id || "";
      if (!borrowerId) throw new Error("Borrower created but id missing");
      createdBorrowerId = borrowerId;

      const created = await apiJson(`${apiBase}/api/loans`, {
        token,
        method: "POST",
        body: {
          borrower_id: borrowerId,
          principal: parseFloat(f.principal),
          interest_amount: parseFloat(f.interestRate || "0"),
          interest_rate: 0,
          installment_amt: parseFloat(f.installmentAmt),
          frequency: upper(f.frequency),
          start_date: f.startDate || null,
          total_installments: parseInt(f.totalInstallments, 10),
        },
      });
      setLoans(p => [created, ...p]);
      setBorr(p => [...p, savedBorrower]);
      setShow(false);
      setBf({name:"",nameTa:"",phone:"",address:"",addressTa:"",collectorId:""});
      setF({principal:"",interestRate:"",frequency:"DAILY",startDate: localISODate(),installmentAmt:"",totalInstallments:"100"});
      addToast("Borrower + loan created","success");
      emitDataChanged();
    } catch (e) {
      // Best-effort rollback: if borrower was created but loan failed, remove borrower.
      if (createdBorrowerId) {
        try {
          await apiJson(`${apiBase}/api/borrowers/${createdBorrowerId}`, { token, method: "DELETE" });
        } catch (_) {}
      }
      addToast(e.message || "Failed to create loan","error");
    } finally {
      setSaving(false);
    }
  };

  const openEditLoan = (l) => {
    if (!isAdmin) return;
    const b = borrowerById.get(l?.borrower_id || "");
    const bn = splitBilingual(b?.name || "");
    const ba = splitBilingual(b?.address || "");
    setEf({
      id: l?.id || "",
      borrowerId: l?.borrower_id || "",
      borrowerName: bn.en || "",
      borrowerNameTa: bn.ta || "",
      borrowerPhone: b?.phone || "",
      borrowerAddress: ba.en || "",
      borrowerAddressTa: ba.ta || "",
      principal: String(l?.total_payable ?? l?.principal ?? ""),
      interestRate: l?.interest_amount != null
        ? String(l.interest_amount)
        : (l?.total_payable != null && l?.principal != null ? String(Math.round((parseFloat(l.total_payable) - parseFloat(l.principal)) * 100) / 100) : ""),
      frequency: String(l?.frequency ?? "DAILY"),
      startDate: l?.start_date ? String(l.start_date).slice(0,10) : "",
      installmentAmt: String(l?.installment_amt ?? ""),
      totalInstallments: String(l?.total_installments ?? ""),
      status: l?.status || "ACTIVE",
      notes: l?.notes || "",
    });
    setEditOpen(true);
  };

  const saveEditLoan = async () => {
    if (!isAdmin) return addToast("Only admin can edit loans","error");
    if (!ef.id) return;
    if (!String(ef.borrowerName || "").trim()) return addToast("Borrower name required","error");
    if (!String(ef.borrowerPhone || "").trim()) return addToast("Borrower phone required","error");
    if (!ef.principal || !ef.installmentAmt || !ef.totalInstallments) return addToast("Principal, installment and total installments required","error");
    setEditSaving(true);
    try {
      // Update borrower details (name + location) from the Edit Loan modal
      if (ef.borrowerId) {
        const borrowerBody = {
          name: ef.borrowerNameTa ? `${ef.borrowerName} | ${ef.borrowerNameTa}` : ef.borrowerName,
          phone: ef.borrowerPhone,
        };
        if (String(ef.borrowerAddress || "").trim() || String(ef.borrowerAddressTa || "").trim()) {
          borrowerBody.address = ef.borrowerAddressTa
            ? `${ef.borrowerAddress} | ${ef.borrowerAddressTa}`
            : ef.borrowerAddress;
        }
        const savedBorrower = await apiJson(`${apiBase}/api/borrowers/${ef.borrowerId}`, {
          token,
          method: "PUT",
          body: borrowerBody,
        });
        setBorr(p => p.map(x => x.id === savedBorrower.id ? savedBorrower : x));
      }

      const updated = await apiJson(`${apiBase}/api/loans/${ef.id}`, {
        token,
        method: "PUT",
        body: {
          principal: parseFloat(ef.principal),
          interest_amount: parseFloat(ef.interestRate || "0"),
          interest_rate: 0,
          frequency: upper(ef.frequency || "DAILY"),
          start_date: ef.startDate || null,
          installment_amt: parseFloat(ef.installmentAmt),
          total_installments: parseInt(ef.totalInstallments, 10),
          status: ef.status,
          notes: ef.notes || null,
        },
      });
      setLoans(p => p.map(x => x.id === updated.id ? updated : x));
      setEditOpen(false);
      addToast("Loan updated","success");
      emitDataChanged();
    } catch (e) {
      addToast(e.message || "Failed to update loan","error");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="ph">
        <div>
          <div className="pt"><Bi en="LOANS"/></div>
          <div className="ps"><Bi en="LOAN MANAGEMENT"/></div>
        </div>
        <div style={{display:"inline-flex",gap:8,alignItems:"center"}}>
          <button
            type="button"
            className={`mic-btn${micListening ? " on" : ""}`}
            onClick={toggleMic}
            title="Voice filter by name"
            aria-pressed={micListening}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z"/>
              <path d="M19 11a7 7 0 0 1-14 0"/>
              <path d="M12 18v3"/>
              <path d="M8 21h8"/>
            </svg>
          </button>
          {isAdmin && <button className="btn btn-p" onClick={()=>setShow(true)}><Bi en="+ Create Loan"/></button>}
        </div>
      </div>
      <div className="sg sg-5">
        <div className="sc"><div className="sl"><Bi en="Total"/></div><div className="sv">{loans.length}</div></div>
        <div className="sc" style={{"--accent":"var(--gr)"}}><div className="sl"><Bi en="Active"/></div><div className="sv">{loans.filter(l=>l.status==="ACTIVE").length}</div></div>
        <div className="sc" style={{"--accent":"var(--am)"}}><div className="sl"><Bi en="Principal"/></div><div className="sv" style={{fontSize:20}}>{fmt(loans.reduce((s,l)=>s+l.principal,0))}</div></div>
        <div className="sc" style={{"--accent":"var(--pu)"}}><div className="sl"><Bi en="Interest"/></div><div className="sv" style={{fontSize:20}}>{fmt(loans.reduce((s,l)=>s+(l.interest_amount!=null?l.interest_amount:Math.max(0,(l.total_payable||0)-(l.principal||0))),0))}</div></div>
        <div className="sc" style={{"--accent":"var(--bl)"}}><div className="sl"><Bi en="Outstanding"/></div><div className="sv" style={{fontSize:20}}>{fmt(loans.reduce((s,l)=>s+(l.status==="ACTIVE"?(l.remaining_balance||0):0),0))}</div></div>
      </div>
      {!!nameFilterMeta && (
        <div className="vchip" style={{marginTop:-6,marginBottom:10}}>
          <div className="vtag" title={nameFilterMeta.q}>
                Voice: {nameFilterMeta.q} â€¢ {nameFilterMeta.count} match{nameFilterMeta.count===1?"":"es"}{nameFilterMeta.anyExact ? "" : " (approx)"}
          </div>
          <button type="button" className="btn btn-g btn-sm" onClick={()=>setNameFilter("")}>Clear</button>
        </div>
      )}
      <div className="card mt14">
        <div className="ch"><span className="ct"><Bi en="All Loans"/></span></div>
        <div className="tw">
          <table ref={loansTableRef} className="sort-table" onClick={onLoansTableClick}>
            <thead><tr><th><Bi en="Borrower"/></th><th><Bi en="Principal"/></th><th><Bi en="Interest"/></th><th><Bi en="Installment"/></th><th><Bi en="Freq"/></th><th><Bi en="Remaining"/></th><th><Bi en="Progress"/></th><th><Bi en="Status"/></th>{isAdmin && <th style={{width:86}}><Bi en="Edit"/></th>}</tr></thead>
            <tbody>
              {loading ? (
                <tr><td className="mono" colSpan={isAdmin ? 9 : 8}>Loading</td></tr>
              ) : visibleLoans.map(({ l, b })=>{
                const pct=Math.round(((l.amount_paid||0)/(l.total_payable||1))*100);
                const interestAmt = l?.interest_amount != null ? l.interest_amount : Math.max(0, (l?.total_payable || 0) - (l?.principal || 0));
                return (
                  <tr key={l.id}>
                    <td style={{fontWeight:700}}>{b?.name ? <BiName value={b.name}/> : ""}</td>
                    <td className="mono am">{fmt(l.principal)}</td>
                    <td className="mono">{fmt(interestAmt)}</td>
                    <td className="mono">{fmt(l.installment_amt)}</td>
                    <td><span className={`badge ${l.frequency==="DAILY"?"ba":l.frequency==="WEEKLY"?"bb":"bgr"}`}>{l.frequency}</span></td>
                    <td className="mono">{fmt(l.remaining_balance)}</td>
                    <td style={{minWidth:85}}><div className="prog" style={{marginBottom:3}}><div className="pf" style={{width:`${pct}%`,background:pct===100?"var(--gr)":"var(--am)"}}/></div><span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--t3)"}}>{l.paid_installments}/{l.total_installments}  {pct}%</span></td>
                    <td><span className={`badge ${l.status==="COMPLETED"?"bg":"ba"}`}>{l.status==="COMPLETED"?"Done":"Active"}</span></td>
                    {isAdmin && (
                      <td>
                        <button className="btn btn-g btn-sm" onClick={(e)=>{e.stopPropagation(); openEditLoan(l);}}><Bi en="Edit"/></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {show&&<Modal title="CREATE LOAN" onClose={()=>setShow(false)} footer={<><button className="btn btn-g" onClick={()=>setShow(false)}><Bi en="Cancel"/></button><button className="btn btn-p" onClick={save} disabled={saving}>{saving?"Creating":"Create Loan"}</button></>}>
        <div className="fgrid">
          <div className="fg"><label className="fl">Full Name</label><input className="fi" placeholder="Name Surname" value={bf.name} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,name:v})); suggestTamil("loan:create:borrowerName", v, { forPlace:false, apply:(ta)=>setBf(p=>(p.name===v ? ({...p,nameTa:ta}) : p)) });}}/></div>
          <div className="fg"><label className="fl">Phone</label><input className="fi" placeholder="10-digit" value={bf.phone} disabled={saving} onChange={e=>setBf(p=>({...p,phone:e.target.value}))}/></div>
        </div>
        <div className="fg"><label className="fl">Tamil Name</label><input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD \\u0BAA\\u0BC6\\u0BAF\\u0BB0\\u0BCD" value={bf.nameTa} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,nameTa:v,name: v ? tamilToLatin(v) : ""}));}}/></div>
        <div className="fgrid">
          <div className="fg"><label className="fl">Address (English)</label><input className="fi" placeholder="Full address" value={bf.address} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,address:v})); suggestTamil("loan:create:borrowerAddress", v, { forPlace:true, apply:(ta)=>setBf(p=>(p.address===v ? ({...p,addressTa:ta}) : p)) });}}/></div>
          <div className="fg"><label className="fl">Address (Tamil)</label><input className="fi ta-input" placeholder="\\u0BAE\\u0BC1\\u0B95\\u0BB5\\u0BB0\\u0BBF" value={bf.addressTa} disabled={saving} onChange={e=>{const v=e.target.value; setBf(p=>({...p,addressTa:v,address: v ? tamilToLatin(v) : ""}));}}/></div>
        </div>
        {/* Removed accidentally inserted Edit-Loan fields
          <div className="fg">
            <label className="fl">Full Name</label>
            <input className="fi" placeholder="Full Name" value={ef.borrowerName} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerName:v})); suggestTamil("loan:edit:borrowerName", v, { forPlace:false, apply:(ta)=>setEf(p=>(p.borrowerName===v ? ({...p,borrowerNameTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Phone</label>
            <input className="fi" placeholder="10-digit" value={ef.borrowerPhone} onChange={e=>setEf(p=>({...p,borrowerPhone:e.target.value}))}/>
          </div>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label className="fl">Borrower</label>
            <input className="fi" placeholder="Full Name" value={ef.borrowerName} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerName:v})); suggestTamil("loan:edit:borrowerName", v, { forPlace:false, apply:(ta)=>setEf(p=>(p.borrowerName===v ? ({...p,borrowerNameTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Phone</label>
            <input className="fi" placeholder="10-digit" value={ef.borrowerPhone} onChange={e=>setEf(p=>({...p,borrowerPhone:e.target.value}))}/>
          </div>
        </div>
        <div className="fg">
          <label className="fl">Tamil Name</label>
          <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerNameTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerNameTa:v,borrowerName: v ? tamilToLatin(v) : p.borrowerName}));}}/>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label className="fl">Address (English)</label>
            <input className="fi" placeholder="Location / Address" value={ef.borrowerAddress} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddress:v})); suggestTamil("loan:edit:borrowerAddress", v, { forPlace:true, apply:(ta)=>setEf(p=>(p.borrowerAddress===v ? ({...p,borrowerAddressTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Address (Tamil)</label>
            <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerAddressTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddressTa:v,borrowerAddress: v ? tamilToLatin(v) : p.borrowerAddress}));}}/>
          </div>
        </div>
        <div className="fg">
          <label className="fl">Tamil Name</label>
          <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerNameTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerNameTa:v,borrowerName: v ? tamilToLatin(v) : p.borrowerName}));}}/>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label className="fl">Location (English)</label>
            <input className="fi" placeholder="Location / Address" value={ef.borrowerAddress} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddress:v})); suggestTamil("loan:edit:borrowerAddress", v, { forPlace:true, apply:(ta)=>setEf(p=>(p.borrowerAddress===v ? ({...p,borrowerAddressTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Location (Tamil)</label>
            <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerAddressTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddressTa:v,borrowerAddress: v ? tamilToLatin(v) : p.borrowerAddress}));}}/>
          </div>
        */}
        <div className="fg">
          <label className="fl">Assign Collector</label>
          <select className="fi" value={bf.collectorId} disabled={saving} onChange={e=>setBf(p=>({...p,collectorId:e.target.value}))}>
            <option value="">Select Collector</option>
            {collectors.map(c=>{
              const cn = splitBilingual(c.name);
              return <option key={c.id} value={c.id}>{cn.en}{cn.ta ? ` (${cn.ta})` : ""}  {c.zone || ""}</option>;
            })}
          </select>
        </div>
        {bf.collectorId && (
          <div className="mono" style={{marginTop:-6,fontSize:10,color:"var(--t3)"}}>
            Collector: {collectorById.get(bf.collectorId)?.name ? <BiName value={collectorById.get(bf.collectorId).name}/> : bf.collectorId}
          </div>
        )}
        <div className="hr"/>
        <div className="fg"><label className="fl">Start Date</label><input className="fi" type="date" value={f.startDate} onChange={e=>setF(p=>({...p,startDate:e.target.value}))}/></div>
        <div className="fgrid">
          {[["principal","Principal (Rs)","number"],["interestRate","Interest (Rs)","number"],["installmentAmt","Installment (Rs)","number"],["totalInstallments","Total Installments","number"]].map(([k,l,t])=>(
            <div className="fg" key={k}><label className="fl">{l}</label><input className="fi" type={t} placeholder={l} value={f[k]} onChange={e=>setF(p=>({...p,[k]:e.target.value}))}/></div>
          ))}
          <div className="fg"><label className="fl">Frequency</label>
            <select className="fi" value={f.frequency} onChange={e=>setF(p=>({...p,frequency:e.target.value}))}>
              <option value="DAILY">Daily</option><option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option>
            </select>
          </div>
        </div>
        {tp>0&&<div style={{background:"rgba(245,166,35,.08)",border:"1px solid rgba(245,166,35,.2)",borderRadius:5,padding:"8px 11px"}}>
          <div className="bw"><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>TOTAL PAYABLE</span><span style={{fontFamily:"var(--mono)",fontSize:14,color:"var(--am)",fontWeight:600}}>{fmt(tp)}</span></div>
        </div>}
      </Modal>}
      {editOpen&&<Modal title="EDIT LOAN" onClose={()=>setEditOpen(false)} footer={<><button className="btn btn-g" onClick={()=>setEditOpen(false)}><Bi en="Cancel"/></button><button className="btn btn-p" onClick={saveEditLoan} disabled={editSaving}>{editSaving?"Saving":"Save"}</button></>}>
        <div className="fg" style={{display:"none"}}>
          <label className="fl">Borrower</label>
          <div className="fi" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <span style={{fontWeight:700}}>
              {(() => { const b = borrowerById.get(ef.borrowerId); return b?.name ? <BiName value={b.name}/> : "-"; })()}
            </span>
          </div>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label className="fl">Borrower</label>
            <input className="fi" placeholder="Full Name" value={ef.borrowerName} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerName:v})); suggestTamil("loan:edit:borrowerName", v, { forPlace:false, apply:(ta)=>setEf(p=>(p.borrowerName===v ? ({...p,borrowerNameTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Phone</label>
            <input className="fi" placeholder="10-digit" value={ef.borrowerPhone} onChange={e=>setEf(p=>({...p,borrowerPhone:e.target.value}))}/>
          </div>
        </div>
        <div className="fg">
          <label className="fl">Tamil Name</label>
          <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerNameTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerNameTa:v,borrowerName: v ? tamilToLatin(v) : p.borrowerName}));}}/>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label className="fl">Address (English)</label>
            <input className="fi" placeholder="Location / Address" value={ef.borrowerAddress} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddress:v})); suggestTamil("loan:edit:borrowerAddress", v, { forPlace:true, apply:(ta)=>setEf(p=>(p.borrowerAddress===v ? ({...p,borrowerAddressTa:ta}) : p)) });}}/>
          </div>
          <div className="fg">
            <label className="fl">Address (Tamil)</label>
            <input className="fi ta-input" placeholder="\\u0BA4\\u0BAE\\u0BBF\\u0BB4\\u0BCD" value={ef.borrowerAddressTa} onChange={e=>{const v=e.target.value; setEf(p=>({...p,borrowerAddressTa:v,borrowerAddress: v ? tamilToLatin(v) : p.borrowerAddress}));}}/>
          </div>
        </div>
        <div className="fg"><label className="fl">Start Date</label><input className="fi" type="date" value={ef.startDate} onChange={e=>setEf(p=>({...p,startDate:e.target.value}))}/></div>
        <div className="fgrid">
          <div className="fg"><label className="fl">Principal (Rs)</label><input className="fi" type="number" value={ef.principal} onChange={e=>setEf(p=>({...p,principal:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Interest (Rs)</label><input className="fi" type="number" value={ef.interestRate} onChange={e=>setEf(p=>({...p,interestRate:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Frequency</label>
            <select className="fi" value={ef.frequency} onChange={e=>setEf(p=>({...p,frequency:e.target.value}))}>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </div>
        </div>
        <div className="fgrid">
          <div className="fg"><label className="fl">Installment (Rs)</label><input className="fi" type="number" value={ef.installmentAmt} onChange={e=>setEf(p=>({...p,installmentAmt:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Total Installments</label><input className="fi" type="number" value={ef.totalInstallments} onChange={e=>setEf(p=>({...p,totalInstallments:e.target.value}))}/></div>
          <div className="fg"><label className="fl">Status</label>
            <select className="fi" value={ef.status} onChange={e=>setEf(p=>({...p,status:e.target.value}))}>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </div>
        <div className="fg"><label className="fl">Notes</label><input className="fi" placeholder="Notes" value={ef.notes} onChange={e=>setEf(p=>({...p,notes:e.target.value}))}/></div>
      </Modal>}
    </div>
  );
}

//  REPORTS PAGE 
function ReportsPage({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reportDate, setReportDate] = useState(() => localISODate());
  const [daily, setDaily]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [overdue, setOverdue]     = useState([]);
  const [loans, setLoans]         = useState([]);
  const [payments, setPayments]   = useState([]);
  const [logSort, setLogSort]     = useState({ key: "date", dir: "desc" });

  const summaryFromPayments = useCallback((list) => {
    const rows = Array.isArray(list) ? list : [];
    const paid = rows.filter(p => upper(p.payment_type) === "PAID");
    const partial = rows.filter(p => upper(p.payment_type) === "PARTIAL");
    const unpaid = rows.filter(p => upper(p.payment_type) === "UNPAID");
    const totalCollected = rows
      .filter(p => upper(p.payment_type) !== "UNPAID")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const collectors = new Set(rows.map(p => p.collector_id).filter(Boolean));
    return {
      total_collected: Math.round(totalCollected * 100) / 100,
      paid_count: paid.length,
      partial_count: partial.length,
      unpaid_count: unpaid.length,
      collector_count: collectors.size,
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const dTo = localISODate();
        const dt = new Date();
        dt.setDate(dt.getDate() - 29);
        const dFrom = localISODate(dt);
        const [d, ob, al, p] = await Promise.all([
          apiJson(`${apiBase}/api/dashboard/daily-summary?date_from=${dFrom}&date_to=${dTo}`, { token }),
          apiJson(`${apiBase}/api/borrowers?status=OVERDUE&limit=50`, { token }),
          apiJson(`${apiBase}/api/loans?status=ACTIVE&limit=200`, { token }),
          apiJson(`${apiBase}/api/payments?payment_date=${reportDate}&limit=500`, { token }),
        ]);
        setDaily(Array.isArray(d) ? d : []);
        setOverdue(Array.isArray(ob) ? ob : []);
        setLoans(Array.isArray(al) ? al : []);
        setPayments(Array.isArray(p) ? p : []);
        setSummary(summaryFromPayments(p));
      } catch (e) {
        addToast(e.message || "Could not load reports","error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token, addToast, reportDate, summaryFromPayments]);

  const exportExcel = async () => {
    if (!isAdmin) return addToast("Only admin can export Excel","error");
    setExporting(true);
    try {
      const dFrom = reportDate || localISODate();
      const dTo = reportDate || localISODate();
      const blob = await apiBlob(`${apiBase}/api/dashboard/export/excel?date_from=${dFrom}&date_to=${dTo}`, { token });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jayfin_report_${dFrom}_${dTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addToast("Excel downloaded","success");
    } catch (e) {
      addToast(e.message || "Export failed","error");
    } finally {
      setExporting(false);
    }
  };

  const paymentsForLog = useMemo(() => {
    const typeByKey = { date: "date", amount: "number", time: "time" };
    const getValue = (p, key) => {
      if (key === "date") return p?.payment_date;
      if (key === "borrower") return splitBilingual(p?.borrower?.name).en || "";
      if (key === "collector") return splitBilingual(p?.collector?.name).en || "";
      if (key === "amount") return p?.amount ?? 0;
      if (key === "type") return upper(p?.payment_type);
      if (key === "time") return p?.payment_time;
      return "";
    };
    return sortRows(payments, logSort, getValue, typeByKey);
  }, [logSort, payments]);

  const logColumns = useMemo(
    () => [
      { key: "date" },
      { key: "borrower" },
      { key: "collector" },
      { key: "amount" },
      { key: "type" },
      { key: "time" },
    ],
    []
  );
  const logTableRef = useRef(null);
  const onLogTableClick = useSortableHeader({
    tableRef: logTableRef,
    columns: logColumns,
    sort: logSort,
    setSort: setLogSort,
  });

  const chartData = daily.map(d => ({
    date: (d.date || "").slice(5),
    collected: d.collected || 0,
  }));

  return (
    <div className="page">
      <div className="ph">
        <div>
          <div className="pt"><Bi en="REPORTS"/></div>
          <div className="ps"><Bi en="COLLECTION ANALYTICS"/></div>
        </div>
        <div className="row" style={{gap:10,alignItems:"flex-end"}}>
          <div className="fg" style={{margin:0,minWidth:170}}>
            <label className="fl"><Bi en="Report Date" /></label>
            <input className="fi" type="date" value={reportDate} onChange={e=>setReportDate(e.target.value)} />
          </div>
          <button className="btn btn-p" onClick={exportExcel} disabled={exporting || loading}>
            <Bi en={exporting?"Exporting...":"Export Excel"} />
          </button>
        </div>
      </div>

      <div className="card mb12">
        <div className="ch"><span className="ct"><Bi en="Collection Trend (Last 30 Days)"/></span></div>
        <div className="cb" style={{paddingTop:6}}>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={chartData} margin={{top:4,right:4,left:-22,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)"/>
              <XAxis dataKey="date" tick={{fill:"var(--t3)",fontSize:8,fontFamily:"var(--mono)"}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:"var(--t3)",fontSize:8}} axisLine={false} tickLine={false} tickFormatter={v=>`${v/1000}k`}/>
              <Tooltip content={<CT/>}/>
              <Line type="monotone" dataKey="collected" stroke="var(--am)" strokeWidth={2.5} dot={{fill:"var(--am)",r:3}} name="Collected"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="g2">
        <div className="card">
          <div className="ch"><span className="ct"><Bi en="Summary"/></span><span className="mono mu">{reportDate}</span></div>
          <div className="cb">
            {loading ? (
              <div className="mono mu">Loading</div>
            ) : (
              <>
                <div className="bw mt8" style={{paddingBottom:8,borderBottom:"1px solid var(--bd)"}}>
                  <span style={{color:"var(--t3)",fontSize:12}}><Bi en="Total Collected"/></span>
                  <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--gr)"}}>{fmt(summary?.total_collected ?? 0)}</span>
                </div>
                <div className="bw mt8" style={{paddingBottom:8,borderBottom:"1px solid var(--bd)"}}>
                  <span style={{color:"var(--t3)",fontSize:12}}><Bi en="Paid / Partial / Unpaid"/></span>
                  <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--t2)"}}>{summary?.paid_count ?? 0} / {summary?.partial_count ?? 0} / {summary?.unpaid_count ?? 0}</span>
                </div>
                <div className="bw mt8" style={{paddingBottom:8}}>
                  <span style={{color:"var(--t3)",fontSize:12}}><Bi en="Collectors Active"/></span>
                  <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--am)"}}>{summary?.collector_count ?? 0}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="ch"><span className="ct"><Bi en="Overdue Accounts"/></span></div>
          <div className="cb">
            {loading ? (
              <div className="mono mu">Loading</div>
            ) : overdue.length ? overdue.map(b=>{
              const l=loans.find(x=>x.borrower_id===b.id && x.status==="ACTIVE");
              return (
                <div key={b.id} className="bw mt8" style={{paddingBottom:8,borderBottom:"1px solid var(--bd)"}}>
                  <div><div style={{fontWeight:600,fontSize:12}}>{b.name}</div><div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>{b.phone}</div></div>
                  <span style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--re)"}}>{l?fmt(l.installment_amt):""}</span>
                </div>
              );
            }) : <div className="mono mu">No overdue borrowers</div>}
          </div>
        </div>
      </div>

      <div className="card mt14">
        <div className="ch"><span className="ct"><Bi en="Full Collection Log"/></span><span className="mono mu">{reportDate}</span></div>
        <div className="tw">
          <table ref={logTableRef} className="sort-table" onClick={onLogTableClick}>
            <thead><tr><th>Date</th><th>Borrower</th><th>Collector</th><th>Amount</th><th>Type</th><th>Time</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td className="mono" colSpan={isAdmin ? 7 : 6}>Loading</td></tr>
              ) : paymentsForLog.map(p=>{
                const b=p.borrower;
                const c=p.collector;
                const st=upper(p.payment_type);
                return (
                  <tr key={p.id}>
                    <td className="mono">{String(p.payment_date || "").slice(0,10)}</td>
                    <td style={{fontWeight:700}}>{b?.name ? <BiName value={b.name}/> : ""}</td>
                    <td style={{color:"var(--t3)"}}>{c?.name ? <BiName value={c.name}/> : ""}</td>
                    <td className="mono am">{fmt(p.amount)}</td>
                    <td><span className={`badge ${st==="PAID"?"bg":st==="PARTIAL"?"bo":"br"}`}><Bi en={st}/></span></td>
                    <td className="mono mu">{p.payment_time || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

//  STATEMENT / CUSTOMER INFO PAGE
function StatementPage({ addToast, token, apiBase, me }) {
  const isAdmin = isAdminRole(me?.role);
  const { showTamil } = useUI();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [nameFilter, setNameFilter] = useState("");
  const [micListening, setMicListening] = useState(false);
  const micRef = useRef(null);
  const [borrowers, setBorrowers] = useState([]);
  const [borrowerId, setBorrowerId] = useState("");
  const [detail, setDetail] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateTo, setDateTo] = useState(() => localISODate());
  const [dateFrom, setDateFrom] = useState(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 30);
    return localISODate(dt);
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const b = await apiJson(`${apiBase}/api/borrowers?limit=200`, { token });
        const list = Array.isArray(b) ? b : [];
        setBorrowers(list);
        if (!borrowerId && list.length) setBorrowerId(list[0].id);
      } catch (e) {
        addToast(e.message || "Could not load borrowers", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, token, addToast]);

  useEffect(() => {
    if (!borrowerId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [d, p] = await Promise.all([
          apiJson(`${apiBase}/api/borrowers/${borrowerId}`, { token }),
          apiJson(`${apiBase}/api/payments?borrower_id=${borrowerId}&date_from=${dateFrom}&date_to=${dateTo}&limit=500`, { token }),
        ]);
        setDetail(d || null);
        setPayments(Array.isArray(p) ? p : []);
      } catch (e) {
        addToast(e.message || "Could not load statement", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token, addToast, borrowerId, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const paid = payments.filter(x => upper(x.payment_type) !== "UNPAID");
    const total = paid.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    return {
      total: Math.round(total * 100) / 100,
      count: payments.length,
      paidCount: paid.length,
      unpaidCount: payments.length - paid.length,
    };
  }, [payments]);

  const toggleMic = () => {
    if (micListening) {
      try { micRef.current?.stop?.(); } catch (_) {}
      setMicListening(false);
      return;
    }

    setMicListening(true);
    micRef.current = startVoiceCapture({
      lang: "ta-IN",
      fallbackLabel: "Type borrower name",
      onText: (t) => setNameFilter(t),
      onError: (ev) => {
        const code = ev?.error || ev?.code;
        if (String(code) === "aborted") return;
        const msg = code ? `Voice input failed (${code})` : (ev?.message || "Voice input failed");
        addToast(msg, "error");
      },
      onEnd: () => setMicListening(false),
    });
  };

  useEffect(() => {
    return () => {
      try { micRef.current?.stop?.(); } catch (_) {}
    };
  }, []);

  const visibleBorrowers = useMemo(() => {
    const q = String(nameFilter || "").trim();
    const rows = borrowers
      .map((b, i) => ({ b, i, score: nameMatchScore(q, b?.name || "") }))
      .filter(x => x.score.ok);
    if (!q) return rows.map(x => x.b);
    return rows.sort((a, b) => a.score.score - b.score.score || a.i - b.i).map(x => x.b);
  }, [borrowers, nameFilter]);

  const nameFilterMeta = useMemo(() => {
    const q = String(nameFilter || "").trim();
    if (!q) return null;
    const anyExact = borrowers.some(b => nameMatchScore(q, b?.name || "")?.exact);
    return { q, count: visibleBorrowers.length, anyExact };
  }, [nameFilter, visibleBorrowers, borrowers]);

  useEffect(() => {
    if (!nameFilterMeta) return;
    if (!visibleBorrowers.length) return;
    const exists = visibleBorrowers.some(b => b?.id === borrowerId);
    if (!exists) setBorrowerId(visibleBorrowers[0].id);
  }, [nameFilterMeta, visibleBorrowers, borrowerId]);

  const shareText = useMemo(() => {
    const b = detail || {};
    const nameBi = b?.name ? splitBilingual(b.name) : { en: "", ta: "" };
    const addrBi = b?.address ? splitBilingual(b.address) : { en: "", ta: "" };
    const phone = b?.phone || "";

    const fmtDMY = (isoLike) => {
      const s = String(isoLike || "").slice(0, 10);
      const [y, m, d] = s.split("-");
      if (!y || !m || !d) return s;
      return `${d}-${m}-${y}`;
    };

    const header = [
      `Customer Statement | \u0BB5\u0BBE\u0B9F\u0BBF\u0B95\u0BCD\u0B95\u0BC8\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD \u0B85\u0BB1\u0BBF\u0B95\u0BCD\u0B95\u0BC8`,
      nameBi.en ? `Name: ${nameBi.en}${nameBi.ta ? ` | ${nameBi.ta}` : ""}` : null,
      phone ? `Phone: ${phone}` : null,
      addrBi.en ? `Address: ${addrBi.en}${addrBi.ta ? ` | ${addrBi.ta}` : ""}` : null,
      `Period: ${dateFrom} to ${dateTo}`,
      `Total: ${fmt(totals.total)} (entries: ${totals.count})`,
      "",
      `Payments: | \u0BAA\u0BA3\u0BAE\u0BCD:`,
    ].filter(Boolean).join("\n");

    const lines = payments
      .slice()
      .sort((a, b) => String(a.payment_date).localeCompare(String(b.payment_date)))
      .map(p => `${fmtDMY(p.payment_date)} - ${fmt(p.amount)}`);

    return `${header}\n${lines.join("\n")}`.trim();
  }, [detail, payments, dateFrom, dateTo, totals.total, totals.count]);

  const shareTextFixed = useMemo(() => repairUtf8Mojibake(shareText), [shareText]);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareTextFixed);
      addToast("Copied", "success");
    } catch (_) {
      addToast("Copy not supported on this device", "error");
    }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareTextFixed)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="page">
      <div className="ph">
        <div>
          <div className="pt"><Bi en="Statement"/></div>
          <div className="ps"><Bi en="Customer info & payments"/></div>
        </div>
        <button
          type="button"
          className={`mic-btn${micListening ? " on" : ""}`}
          onClick={toggleMic}
          title="Voice filter by name"
          aria-pressed={micListening}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z"/>
            <path d="M19 11a7 7 0 0 1-14 0"/>
            <path d="M12 18v3"/>
            <path d="M8 21h8"/>
          </svg>
        </button>
      </div>

      <div className="card">
        <div className="cb">
          <div className="fgrid fgrid-3">
            <div className="fg">
              <label className="fl"><Bi en="Borrower" /></label>
              <select className="fi" value={borrowerId} onChange={e => setBorrowerId(e.target.value)}>
                {visibleBorrowers.map(b => {
                  const bn = splitBilingual(b.name);
                  return <option key={b.id} value={b.id}>{bn.en}{bn.ta ? ` (${bn.ta})` : ""}</option>;
                })}
              </select>
            </div>
            <div className="fg">
              <label className="fl"><Bi en="From" /></label>
              <input className="fi" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="fg">
              <label className="fl"><Bi en="To" /></label>
              <input className="fi" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
          <div className="bw mt8" style={{ paddingBottom: 0 }}>
            <span className="mu" style={{ fontSize: 11 }}><Bi en="Total" /></span>
            <span className="mono am" style={{ fontSize: 12 }}>{fmt(totals.total)}</span>
          </div>
          {!!nameFilterMeta && (
            <div className="vchip" style={{marginTop:10}}>
              <div className="vtag" title={nameFilterMeta.q}>
                Voice: {nameFilterMeta.q} â€¢ {nameFilterMeta.count} match{nameFilterMeta.count===1?"":"es"}{nameFilterMeta.anyExact ? "" : " (approx)"}
              </div>
              <button type="button" className="btn btn-g btn-sm" onClick={()=>setNameFilter("")}>Clear</button>
            </div>
          )}
        </div>
      </div>

      <div className="g2 mt14" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
        <div className="card stmt-cust">
          <div className="ch"><span className="ct">CUSTOMER DETAILS</span></div>
          <div className="cb">
            {loading ? (
              <div className="mono mu">Loading</div>
            ) : !detail ? (
              <div className="mono mu">No borrower selected</div>
            ) : (
              <>
                {(() => {
                  const nameBi = splitBilingual(detail.name);
                  const addrBi = splitBilingual(detail.address);
                  const collBi = splitBilingual(detail.collector?.name);
                  const loan = detail.active_loan || null;
                  const freqKey = upper(loan?.frequency || "");
                  const freqEn = freqKey === "DAILY" ? "Daily" : freqKey === "WEEKLY" ? "Weekly" : freqKey === "MONTHLY" ? "Monthly" : (freqKey || "-");
                  const freqTa = freqKey === "DAILY" ? "\u0BA4\u0BBF\u0BA9\u0BAE\u0BC1\u0BAE\u0BCD" : freqKey === "WEEKLY" ? "\u0BB5\u0BBE\u0BB0\u0BAE\u0BCD" : freqKey === "MONTHLY" ? "\u0BAE\u0BBE\u0BA4\u0BAE\u0BCD" : "";
                  const freqTaFixed = repairUtf8Mojibake(freqTa);

                  return (
                    <>
                      <div className="stmt-top">
                        <div className="stmt-av">{initials(detail.name)}</div>
                        <div className="stmt-name">
                          <div className="en">{nameBi.en || detail.name}</div>
                          {showTamil && nameBi.ta && <div className="ta">{nameBi.ta}</div>}
                        </div>
                      </div>

                      <div className="stmt-sep" />

                      {!!detail.phone && (
                        <div className="stmt-row">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.54 2.36a2 2 0 0 1-.45 2.11L9.1 10.1a16 16 0 0 0 4.8 4.8l.91-.99a2 2 0 0 1 2.11-.45c.76.24 1.55.42 2.36.54A2 2 0 0 1 22 16.92z"/>
                          </svg>
                          <div>
                            <div className="stmt-row-main">{detail.phone}</div>
                          </div>
                        </div>
                      )}

                      {!!detail.address && (
                        <div className="stmt-row">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 21s7-4.35 7-11a7 7 0 0 0-14 0c0 6.65 7 11 7 11z"/>
                            <path d="M12 10a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
                          </svg>
                          <div>
                            <div className="stmt-row-main">{addrBi.en || detail.address}</div>
                            {showTamil && addrBi.ta && <div className="stmt-row-sub">{addrBi.ta}</div>}
                          </div>
                        </div>
                      )}

                      {!!detail.collector?.name && (
                        <div className="stmt-row">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20 21a8 8 0 0 0-16 0"/>
                            <path d="M12 13a4 4 0 1 0-4-4 4 4 0 0 0 4 4z"/>
                          </svg>
                          <div>
                            <div className="stmt-row-main">
                              <span className="stmt-row-k">Collector:</span> {collBi.en || detail.collector.name}
                            </div>
                            {showTamil && collBi.ta && <div className="stmt-row-sub">{collBi.ta}</div>}
                          </div>
                        </div>
                      )}

                      {loan && (
                        <>
                          <div className="stmt-sep" />
                          <div className="stmt-stats">
                            <div className="stmt-stat">
                              <div className="stmt-stat-l">INSTALLMENT</div>
                              {showTamil && <div className="stmt-stat-ta">{"\u0BA4\u0BB5\u0BA3\u0BC8"}</div>}
                              <div className="stmt-stat-v">{fmt(loan.installment_amt)}</div>
                            </div>
                            <div className="stmt-stat">
                              <div className="stmt-stat-l">REMAINING</div>
                              {showTamil && <div className="stmt-stat-ta">{"\u0BAE\u0BC0\u0BA4\u0BAE\u0BCD"}</div>}
                              <div className="stmt-stat-v">{fmt(loan.remaining_balance)}</div>
                            </div>
                            <div className="stmt-stat">
                              <div className="stmt-stat-l">FREQUENCY</div>
                              {showTamil && <div className="stmt-stat-ta">{"\u0B85\u0B9F\u0BBF\u0B95\u0BCD\u0B95\u0B9F\u0BBF"}</div>}
                              <div className="stmt-stat-v" style={{color:"var(--bl)"}}>{freqEn}</div>
                              {showTamil && freqTaFixed && <div className="stmt-stat-v2">{freqTaFixed}</div>}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Payments panel hidden for now (payments still load for totals + WhatsApp share) */}
      </div>

      <div className="card mt14" style={{ borderStyle: "dashed", borderColor: "var(--bd2)" }}>
        <div className="ch">
          <span className="ct">
            SHARE{" "}
            <span style={{ fontFamily: "var(--tamil)", fontSize: 11, letterSpacing: 0, textTransform: "none", color: "var(--t2)" }}>
              {"\u0BAA\u0B95\u0BBF\u0BB0\u0BCD"}
            </span>
          </span>
        </div>
        <div className="cb">
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "var(--mono)", fontSize: 10, color: "var(--t2)", background: "#f6f7ff", border: "1px solid var(--bd2)", borderRadius: 14, padding: 12, maxHeight: 220, overflow: "auto" }}>
            {shareTextFixed}
          </pre>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn btn-g" onClick={copyText}><Bi en="Copy Text" /></button>
            <button className="btn btn-p" onClick={openWhatsApp}><Bi en="Open WhatsApp" /></button>
            {!isAdmin && <span className="mu" style={{ fontSize: 10 }}>(Collectors see only their own payments)</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

//  MOBILE VIEW 
function MobileView({ addToast, onBack, token, apiBase, me, meIsAdmin }) {
  const { showTamil } = useUI();
  const isAdmin = !!meIsAdmin || isAdminRole(me?.role);
  const isCollector = isCollectorRole(me?.role);
  const canUseCollectorMode = isCollector || isAdmin;
  const ACTING_COLLECTOR_KEY = "LT:actingCollectorId";
  const [actingCollectorId, setActingCollectorId] = useState(() => {
    if (isCollector) return me?.id || "";
    if (isAdmin) {
      try { return localStorage.getItem(ACTING_COLLECTOR_KEY) || ""; } catch (_) { return ""; }
    }
    return "";
  });
  const [actingCollectors, setActingCollectors] = useState([]);
  const [loadingCollectors, setLoadingCollectors] = useState(false);
  const [loading, setLoading] = useState(true);
  const [route,  setRoute]    = useState([]);
  const [screen, setScreen]   = useState("route");
  const [pTarget,setPTarget]  = useState(null);
  const [pAmt,   setPAmt]     = useState("");
  const [eTarget, setETarget] = useState(null);
  const [eAmt, setEAmt]       = useState("");
  const [addrSel, setAddrSel] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [micListening, setMicListening] = useState(false);
  const micRef = useRef(null);

  const collectorId = isAdmin ? actingCollectorId : (me?.id || "");
  const collectorName = useMemo(() => {
    if (isAdmin) {
      return actingCollectors.find(c => c?.id === actingCollectorId)?.name || "Collector";
    }
    return me?.name || "Collector";
  }, [isAdmin, actingCollectors, actingCollectorId, me?.name]);
  const collectorNameBi = useMemo(() => splitBilingual(collectorName), [collectorName]);

  const goHome = () => {
    setScreen("route");
    setPTarget(null);
    setPAmt("");
    setETarget(null);
    setEAmt("");
  };

  const toggleMic = () => {
    if (micListening) {
      try { micRef.current?.stop?.(); } catch (_) {}
      setMicListening(false);
      return;
    }

    setMicListening(true);
    micRef.current = startVoiceCapture({
      lang: "ta-IN",
      fallbackLabel: "Type customer name",
      onText: (t) => setNameFilter(t),
      onError: (ev) => {
        const code = ev?.error || ev?.code;
        if (String(code) === "aborted") return;
        const msg = code ? `Voice input failed (${code})` : (ev?.message || "Voice input failed");
        addToast(msg, "error");
      },
      onEnd: () => setMicListening(false),
    });
  };

  useEffect(() => {
    if (screen === "route") return;
    if (!micListening) return;
    try { micRef.current?.stop?.(); } catch (_) {}
    setMicListening(false);
  }, [screen, micListening]);

  useEffect(() => {
    return () => {
      try { micRef.current?.stop?.(); } catch (_) {}
    };
  }, []);

  useEffect(() => {
    if (!isCollector) return;
    if (me?.id && actingCollectorId !== me.id) setActingCollectorId(me.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollector, me?.id]);

  useEffect(() => {
    if (!isAdmin) return;
    try { localStorage.setItem(ACTING_COLLECTOR_KEY, actingCollectorId || ""); } catch (_) {}
  }, [isAdmin, actingCollectorId]);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    (async () => {
      setLoadingCollectors(true);
      try {
        const data = await apiJson(`${apiBase}/api/collectors?active_only=true`, { token });
        const list = Array.isArray(data) ? data : [];
        if (!alive) return;
        setActingCollectors(list);
        writeJsonSafe(OFFLINE_COLLECTORS_CACHE_KEY, { savedAt: Date.now(), list });
        if (!actingCollectorId && list[0]?.id) setActingCollectorId(list[0].id);
      } catch (e) {
        if (!alive) return;
        const cached = readJsonSafe(OFFLINE_COLLECTORS_CACHE_KEY, null);
        const list = Array.isArray(cached?.list) ? cached.list : [];
        if (list.length) {
          setActingCollectors(list);
          if (!actingCollectorId && list[0]?.id) setActingCollectorId(list[0].id);
          addToast("Offline: using saved collectors list", "info");
        } else {
          addToast(e.message || "Could not load collectors","error");
        }
      } finally {
        if (alive) setLoadingCollectors(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, apiBase, token]);

  const loadRoute = async () => {
    if (!canUseCollectorMode || !collectorId) return;
    const dateISO = localISODate();
    setLoading(true);
    try {
      const data = await apiJson(`${apiBase}/api/collectors/${collectorId}/route`, { token });
      const mapped = (Array.isArray(data) ? data : []).map(r => ({
        borrowerId: r.borrower_id,
        loanId: r.loan_id,
        name: r.borrower_name,
        phone: r.phone || "",
        address: r.address || "",
        amount: r.installment_amt,
        balance: r.remaining_balance,
        status: r.today_status ? String(r.today_status).toLowerCase() : "pending",
        time: r.today_time || null,
        todayPaymentId: r.today_payment_id || null,
        todayAmount: r.today_amount ?? null,
      }));
      setRoute(mapped);
      cacheRoute(collectorId, dateISO, mapped);
      return mapped;
    } catch (e) {
      if (isLikelyNetworkError(e)) {
        const cached = readCachedRoute(collectorId, dateISO);
        if (cached) {
          setRoute(cached);
          addToast("Offline: showing last saved route", "info");
          return cached;
        }
      }
      addToast(e.message || "Could not load today's route","error");
      setRoute([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canUseCollectorMode || !collectorId) {
      setLoading(false);
      setRoute([]);
      return;
    }
    loadRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, token, collectorId, canUseCollectorMode]);

  const todayISO = localISODate();
  const totalDue  = route.reduce((s,r)=>s+(r.amount||0),0);
  const totalPaid = route.reduce((s,r)=>{
    if (r.status === "paid" || r.status === "partial") return s + (r.todayAmount ?? 0);
    return s;
  },0);
  const done      = route.filter(r=>r.status!=="pending").length;
  const addrSummary = useMemo(() => {
    const map = new Map(); // keyLower -> { label, key, count }
    for (const r of route) {
      const full = String(r?.address || "").trim();
      if (!full) continue;
      const firstPart = full.split(",")[0].trim();
      const label = firstPart || full;
      const key = label.toLowerCase();
      const prev = map.get(key);
      if (prev) prev.count += 1;
      else map.set(key, { label, key, count: 1 });
    }
    return Array.from(map.values()).sort((a,b)=>a.label.localeCompare(b.label));
  }, [route]);

  const visible = route
    .map((r, i) => {
      const score = nameMatchScore(nameFilter, r?.name || "");
      return { r, i, score };
    })
    .filter(({ r, score }) => {
      const addrQ = String(addrSel || "").trim().toLowerCase();
      const addrOk = !addrQ || String(r.address || "").toLowerCase().includes(addrQ);
      if (!addrOk) return false;
      return score.ok;
    })
    .sort((a, b) => {
      const q = String(nameFilter || "").trim();
      if (!q) return a.i - b.i;
      return a.score.score - b.score.score || a.i - b.i;
    });

  const nameFilterMeta = useMemo(() => {
    const q = String(nameFilter || "").trim();
    if (!q) return null;
    const anyExact = visible.some(v => v.score?.exact);
    return { q, count: visible.length, anyExact };
  }, [nameFilter, visible]);

  const record = async (r, payment_type, amount) => {
    if (!collectorId) throw new Error("Select a collector");

    const body = {
      loan_id: r.loanId,
      borrower_id: r.borrowerId,
      ...(isAdmin ? { collector_id: collectorId } : {}),
      amount,
      payment_type: upper(payment_type),
      payment_date: todayISO,
      payment_time: localTimeHHMM(),
    };

    // If offline or fetch fails, queue locally and sync later.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      enqueueOfflineAction({ id: `q_${Date.now()}`, url: "/api/payments", method: "POST", body, createdAt: Date.now() });
      return { queued: true };
    }

    try {
      await apiJson(`${apiBase}/api/payments`, { token, method: "POST", body });
      return { queued: false };
    } catch (e) {
      if (isLikelyNetworkError(e)) {
        enqueueOfflineAction({ id: `q_${Date.now()}`, url: "/api/payments", method: "POST", body, createdAt: Date.now() });
        return { queued: true };
      }
      throw e;
    }
  };

  const mark = async (idx, st) => {
    const r = route[idx];
    if (!r) return;
    if (st === "partial") {
      setPTarget(idx);
      setPAmt("");
      setScreen("partial");
      return;
    }
    try {
      const amt = st === "unpaid" ? 0 : (r.amount || 0);
      const res = await record(r, st, amt);
      if (res?.queued) {
        addToast("Saved offline. Will sync when internet is back.", "info");
        const updated = route.map((x, j) => j !== idx ? x : ({
          ...x,
          status: st,
          todayAmount: st === "paid" ? amt : 0,
          time: localTimeHHMM(),
          todayPaymentId: null,
        }));
        setRoute(updated);
        cacheRoute(collectorId, todayISO, updated);
      } else {
        addToast(st==="paid"?"Payment recorded":st==="unpaid"?"Marked unpaid":"Updated", st==="paid"?"success":st==="unpaid"?"error":"info");
        await loadRoute();
      }
    } catch (e) {
      addToast(e.message || "Payment failed","error");
    }
  };

  const confirmP = async () => {
    if (pTarget === null) return;
    const r = route[pTarget];
    const amt = parseFloat(pAmt);
    if (!r || !pAmt || isNaN(amt) || amt <= 0) return;
    try {
      const res = await record(r, "partial", amt);
      if (res?.queued) {
        addToast("Saved offline. Will sync when internet is back.", "info");
        const updated = route.map((x, j) => j !== pTarget ? x : ({
          ...x,
          status: "partial",
          todayAmount: amt,
          time: localTimeHHMM(),
          todayPaymentId: null,
        }));
        setRoute(updated);
        cacheRoute(collectorId, todayISO, updated);
        setScreen("route");
        setPTarget(null);
        setPAmt("");
      } else {
        addToast(`Partial ${amt} recorded`,"info");
        setScreen("route");
        setPTarget(null);
        setPAmt("");
        await loadRoute();
      }
    } catch (e) {
      addToast(e.message || "Partial payment failed","error");
    }
  };

  const openEdit = (idx) => {
    const r = route[idx];
    if (!r?.todayPaymentId) return addToast("No payment to edit","error");
    setETarget(idx);
    setEAmt(String(r.todayAmount ?? r.amount ?? ""));
    setScreen("edit");
  };

  const confirmEdit = async () => {
    if (eTarget === null) return;
    const r = route[eTarget];
    const amt = parseFloat(eAmt);
    if (!r?.todayPaymentId) return addToast("No payment to edit","error");
    if (!eAmt || isNaN(amt) || amt < 0) return;
    try {
      const body = { amount: amt };
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        enqueueOfflineAction({ id: `q_${Date.now()}`, url: `/api/payments/${r.todayPaymentId}`, method: "PUT", body, createdAt: Date.now() });
        const updated = route.map((x, j) => j !== eTarget ? x : ({ ...x, todayAmount: amt }));
        setRoute(updated);
        cacheRoute(collectorId, todayISO, updated);
        addToast("Saved offline. Will sync when internet is back.", "info");
        setScreen("route");
        setETarget(null);
        setEAmt("");
        return;
      }

      await apiJson(`${apiBase}/api/payments/${r.todayPaymentId}`, { token, method: "PUT", body });
      addToast("Amount updated","success");
      setScreen("route");
      setETarget(null);
      setEAmt("");
      await loadRoute();
    } catch (e) {
      if (isLikelyNetworkError(e)) {
        enqueueOfflineAction({ id: `q_${Date.now()}`, url: `/api/payments/${r.todayPaymentId}`, method: "PUT", body: { amount: amt }, createdAt: Date.now() });
        const updated = route.map((x, j) => j !== eTarget ? x : ({ ...x, todayAmount: amt }));
        setRoute(updated);
        cacheRoute(collectorId, todayISO, updated);
        addToast("Saved offline. Will sync when internet is back.", "info");
        setScreen("route");
        setETarget(null);
        setEAmt("");
        return;
      }
      addToast(e.message || "Update failed","error");
    }
  };

  const renderCard = (r, i) => {
    if (!r) return null;
    const tel = String(r.phone || "").replace(/[^0-9+]/g, "");
    return (
      <div className="rc" key={r.borrowerId} style={{borderColor:r.status==="paid"?"rgba(46,204,113,.3)":r.status==="unpaid"?"rgba(231,76,60,.3)":r.status==="partial"?"rgba(245,166,35,.3)":"var(--bd)"}}>
        <div className="rch">
          <div className="bav">{initials(r.name)}</div>
          <div style={{flex:1}}>
            <div className="rn"><BiName value={r.name}/></div>
          </div>
          {!!tel && (
            <a
              className="call-pill"
              href={`tel:${tel}`}
              onClick={(e) => e.stopPropagation()}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.54 2.36a2 2 0 0 1-.45 2.11L9.1 10.1a16 16 0 0 0 4.8 4.8l.91-.99a2 2 0 0 1 2.11-.45c.76.24 1.55.42 2.36.54A2 2 0 0 1 22 16.92z"/>
                <path d="M14.5 5.5a4 4 0 0 1 4 4"/>
                <path d="M14.5 1.5a8 8 0 0 1 8 8"/>
              </svg>
              <span>Call</span>
            </a>
          )}
          {/* Hide the small status badge (PAID/PARTIAL/NOT PAID) in card header */}
        </div>
        {r.status==="pending"
          ? <div className="ract">
              <button type="button" className="act act-paid" onClick={()=>mark(i,"paid")}>
                <span className="act-ico" aria-hidden="true"><StatusIcon type="paid" /></span>
                <Bi en="PAID" className="act-bi" />
              </button>
              <button type="button" className="act act-partial" onClick={()=>mark(i,"partial")}>
                <span className="act-ico" aria-hidden="true"><StatusIcon type="partial" /></span>
                <Bi en="PARTIAL" className="act-bi" />
              </button>
              <button type="button" className="act act-unpaid" onClick={()=>mark(i,"unpaid")}>
                <span className="act-ico" aria-hidden="true"><StatusIcon type="unpaid" /></span>
                <Bi en="NOT PAID" className="act-bi" />
              </button>
            </div>
          : (() => {
              const isPaid = r.status === "paid";
              const isPartial = r.status === "partial";
              const accent = isPaid ? "var(--gr)" : isPartial ? "var(--or2)" : "var(--re)";
              const stEn = isPaid ? "OK" : isPartial ? "PARTIAL" : "NOT PAID";
              const stTa = isPaid ? (TA2.Paid || "Paid") : isPartial ? (TA2.Partial || "Partial") : (TA2["NOT PAID"] || "NOT PAID");
              const amount = isPaid ? (r.todayAmount ?? r.amount ?? 0) : isPartial ? (r.todayAmount ?? 0) : 0;
              const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
              return (
                <div className="rd" style={{ "--rdAccent": accent }}>
                  <div className="rd-left">
                    <div className="rd-amt">
                      <span className="rd-st">{stEn}</span>
                      {(isPaid || isPartial) && <span className="rd-val">{inr(amount)}</span>}
                    </div>
                    <div className="rd-sub">
                      {showTamil && <span className="rd-ta">{stTa}</span>}
                    </div>
                  </div>
                  {(r.todayPaymentId && (r.status==="paid" || r.status==="partial" || r.status==="unpaid")) && (
                    <button className="edit-pill" onClick={(e)=>{e.stopPropagation(); openEdit(i);}}>
                      <Bi en="Edit" />
                    </button>
                  )}
                </div>
              );
            })()
        }
      </div>
    );
  };

  return (
    <div className="mob-w">
      <div style={{width:"100%"}}>
        <div className="row mb12"><button className="btn btn-g btn-sm" onClick={onBack}>&lt;- Admin</button><span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}><Bi en="MOBILE PREVIEW"/></span></div>
        <div className="mob-f">
          <div className="mob-st">
            <span>9:41</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
              {canUseCollectorMode && screen === "route" && (
                <button
                  type="button"
                  className={`mic-btn${micListening ? " on" : ""}`}
                  onClick={toggleMic}
                  title="Voice filter by name"
                  aria-pressed={micListening}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z"/>
                    <path d="M19 11a7 7 0 0 1-14 0"/>
                    <path d="M12 18v3"/>
                    <path d="M8 21h8"/>
                  </svg>
                </button>
              )}
              {canUseCollectorMode && screen !== "route" && (
                <button className="btn btn-g btn-sm" onClick={goHome} style={{padding:"3px 10px"}}><Bi en="Home"/></button>
              )}
            </span>
          </div>
          {!canUseCollectorMode ? (
            <div style={{padding:"18px 14px"}}>
              <div style={{fontFamily:"var(--disp)",fontSize:17,letterSpacing:1}}>COLLECTOR MODE</div>
              <div style={{marginTop:6,fontSize:11,color:"var(--t3)"}}>Login with a collector account to use the mobile route and record payments.</div>
            </div>
          ) : isAdmin && !collectorId ? (
            <div style={{padding:"18px 14px"}}>
              <div style={{fontFamily:"var(--disp)",fontSize:17,letterSpacing:1}}>COLLECTOR MODE</div>
              <div style={{marginTop:6,fontSize:11,color:"var(--t3)"}}>Select a collector to view today&apos;s route and record payments on their behalf.</div>
              <div className="fg" style={{marginTop:12}}>
                <label className="fl"><Bi en="Collector"/></label>
                <select
                  className="fi"
                  value={actingCollectorId}
                  onChange={e => { goHome(); setActingCollectorId(e.target.value); }}
                  disabled={loadingCollectors}
                >
                  <option value="">{loadingCollectors ? "Loading..." : "Select a collector"}</option>
                  {actingCollectors.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : screen==="route" ? (
            <>
              <div className="mob-tb">
                <div className="bw">
                  <div>
                    <div className="rt-en">TODAY&apos;S ROUTE</div>
                    {showTamil && TA2["TODAY'S ROUTE"] && <div className="rt-ta">{TA2["TODAY'S ROUTE"]}</div>}
                    <div className="rt-name">{collectorNameBi.en || collectorName || "Collector"}</div>
                    <div className="rt-meta">
                      {showTamil && collectorNameBi.ta && <span className="rt-ta-sm">{collectorNameBi.ta}</span>}
                      <span>{nameFilterMeta ? (<>{nameFilterMeta.count} match{nameFilterMeta.count===1?"":"es"}{nameFilterMeta.anyExact ? "" : " (approx)"}</>) : ""}</span>
                      <span>{localISODate()}</span>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}><div style={{fontFamily:"var(--mono)",fontSize:14,color:"var(--am)",fontWeight:600}}>{fmt(totalPaid)}</div><div style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--t3)"}}>OF {fmt(totalDue)}</div></div>
                </div>
                <div style={{marginTop:7}}><div className="prog"><div className="pf" style={{width:`${route.length ? (done/route.length)*100 : 0}%`,background:"var(--am)"}}/></div>
                  <div className="bw" style={{marginTop:3}}><span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--t3)"}}>{done}/{route.length} done</span><span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--t3)"}}>{route.length-done} pending</span></div>
                </div>
                {!!nameFilterMeta && (
                  <div className="vchip">
                    <div className="vtag" title={nameFilterMeta.q}>
                Voice: {nameFilterMeta.q} â€¢ {nameFilterMeta.count} match{nameFilterMeta.count===1?"":"es"}{nameFilterMeta.anyExact ? "" : " (approx)"}
                    </div>
                    <button type="button" className="btn btn-g btn-sm" onClick={()=>setNameFilter("")}>Clear</button>
                  </div>
                )}
                {isAdmin && (
                  <div className="fg" style={{marginTop:10, marginBottom:0}}>
                    <label className="fl">COLLECTOR</label>
                    <select
                      className="fi"
                      value={actingCollectorId}
                      onChange={e => { goHome(); setActingCollectorId(e.target.value); }}
                      disabled={loadingCollectors}
                    >
                      <option value="">{loadingCollectors ? "Loading..." : "Select a collector"}</option>
                      {actingCollectors.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="fg" style={{marginTop:10, marginBottom:0}}>
                  <label className="fl">ADDRESS</label>
                  <select className="fi" value={addrSel} onChange={e=>setAddrSel(e.target.value)}>
                    <option value="">{showTamil ? (TA2["All addresses"] || "All addresses") : "All addresses"}</option>
                    {addrSummary.map(a => (
                      <option key={a.key} value={a.label}>{a.label} ({a.count})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mob-ct" style={{padding:"5px 0 18px"}}>
                {loading ? (
                  <div className="mono mu" style={{padding:"14px 12px"}}>Loading...</div>
                ) : route.length ? (
                  visible.length ? (
                    visible.map(({ r, i }) => renderCard(r, i))
                  ) : (
                    <div className="mono mu" style={{padding:"14px 12px"}}>No matches</div>
                  )
                ) : (
                  <div className="mono mu" style={{padding:"14px 12px"}}>No borrowers assigned</div>
                )}
              </div>
            </>
          ) : screen==="partial" ? (
            <>
              <div className="mob-tb">
                <button className="btn btn-g btn-sm" onClick={goHome} style={{marginBottom:5}}>&lt;- Back</button>
                <div style={{fontFamily:"var(--disp)",fontSize:17,letterSpacing:1}}>PARTIAL PAYMENT</div>
                <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)",marginTop:1}}>{pTarget!==null?route[pTarget].name:""}</div>
              </div>
              <div style={{padding:"14px 12px"}}>
                <div style={{background:"rgba(245,166,35,.08)",border:"1px solid rgba(245,166,35,.2)",borderRadius:9,padding:12,marginBottom:14,textAlign:"center"}}>
                  <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>INSTALLMENT DUE</div>
                  <div style={{fontFamily:"var(--disp)",fontSize:30,color:"var(--am)"}}>{pTarget!==null?fmt(route[pTarget].amount):""}</div>
                </div>
                <div className="fg"><label className="fl">Amount Collected (Rs)</label>
                  <input className="fi" type="number" placeholder="Enter amount" value={pAmt} onChange={e=>setPAmt(e.target.value)} style={{fontSize:20,textAlign:"center",height:46}}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginBottom:14}}>
                  {[100,200,300,400,500,1000].map(a=><button key={a} className="btn btn-g" style={{justifyContent:"center",fontSize:10}} onClick={()=>setPAmt(String(a))}>Rs {a}</button>)}
                </div>
                <button className="btn btn-p" style={{width:"100%",justifyContent:"center",height:44,fontSize:13}} onClick={confirmP}>Confirm Rs {pAmt||0}</button>
              </div>
            </>
          ) : (
            <>
              <div className="mob-tb">
                <button
                  className="btn btn-g btn-sm"
                  onClick={goHome}
                  style={{marginBottom:5}}
                >
                  &lt;- Back
                </button>
                <div style={{fontFamily:"var(--disp)",fontSize:17,letterSpacing:1}}>EDIT AMOUNT</div>
                <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)",marginTop:1}}>{eTarget!==null?route[eTarget]?.name:""}</div>
              </div>
              <div style={{padding:"14px 12px"}}>
                <div style={{background:"rgba(79,124,255,.08)",border:"1px solid rgba(79,124,255,.2)",borderRadius:9,padding:12,marginBottom:14,textAlign:"center"}}>
                  <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--t3)"}}>INSTALLMENT DUE</div>
                  <div style={{fontFamily:"var(--disp)",fontSize:30,color:"var(--am)"}}>{eTarget!==null?fmt(route[eTarget]?.amount):""}</div>
                </div>
                <div className="fg"><label className="fl">Recorded Amount (Rs)</label>
                  <input className="fi" type="number" placeholder="Enter amount" value={eAmt} onChange={e=>setEAmt(e.target.value)} style={{fontSize:20,textAlign:"center",height:46}}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginBottom:14}}>
                  {[100,200,300,400,500,1000].map(a=><button key={a} className="btn btn-g" style={{justifyContent:"center",fontSize:10}} onClick={()=>setEAmt(String(a))}>Rs {a}</button>)}
                </div>
                <button className="btn btn-p" style={{width:"100%",justifyContent:"center",height:44,fontSize:13}} onClick={confirmEdit}>Save Rs {eAmt||0}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

//  LOGIN 
function LoginPage({ onLogin, apiBase }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Login failed. Check email and password.");
        setLoading(false);
        return;
      }
      onLogin({
        token: data.access_token,
        refreshToken: data.refresh_token,
        user: { id: data.user_id, company_id: data.company_id, name: data.name, role: data.role },
      });
    } catch (err) {
      setError("Cannot reach server. Check your connection and API URL.");
      setLoading(false);
    }
  };

  return (
    <div className="lp">
      <div className="lc">
        <div className="lb">
          <img className="brandLogo" src={BRAND_LOGO} alt="Jayfin" />
          <div className="ls">PRO - MULTI-TENANT SAAS - MVP</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="fg"><label className="fl">Email</label><input className="fi" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" required/></div>
          <div className="fg"><label className="fl">Password</label><input className="fi" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="********" autoComplete="current-password" required/></div>
          {error && <div style={{fontSize:11,color:"var(--re)",marginBottom:8}}>{error}</div>}
          <button type="submit" className="btn btn-p" style={{width:"100%",justifyContent:"center",height:40,fontSize:12,marginTop:5}} disabled={loading}>{loading ? "Signing in..." : "Sign In ->"}</button>
        </form>
      </div>
    </div>
  );
}

//  SIDEBAR 
const NAV = [
  {sec:"Overview",   items:[{k:"dashboard",  l:"Dashboard",    i:"D"}]},
  {sec:"Management", items:[{k:"collectors", l:"Collectors",   i:"C"},{k:"loans",l:"Loans",i:"L"},{k:"statement",l:"Statement",i:"T"}]},
  {sec:"Mobile",     items:[{k:"mobile",     l:"Collector App",i:"M"}]},
];

function Sidebar({ active, onNav, user, onLogout, isAdmin }) {
  const nav = isAdmin ? NAV : NAV.filter(s => s.sec === "Overview" || s.sec === "Mobile");
  const { showTamil, setShowTamil } = useUI();
  return (
    <div className="sidebar">
      <div className="sb-brand">
        <img className="brandLogo" src={BRAND_LOGO} alt="Jayfin" style={{ width: 170, marginBottom: 6 }} />
        <div className="sb-sub">PRO - v1.0 MVP</div>
      </div>
      <div className="sb-nav">
        {nav.map(s=>(
          <div key={s.sec}>
            <div className="nav-sec"><Bi en={s.sec}/></div>
            {s.items.map(it=>(
              <div key={it.k} className={`nav-item ${active===it.k?"active":""}`} onClick={()=>onNav(it.k)}>
                <span className="nav-ico">{it.i}</span><Bi en={it.l}/>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="sb-foot">
        <div className="u-pill">
          <div className="av">{initials(user.name)}</div>
          <div style={{flex:1,overflow:"hidden"}}><div className="u-name">{user.name}</div><div className="u-role">{user.role}</div></div>
        </div>
        <button type="button" className="btn btn-g btn-sm" style={{marginTop:6,width:"100%",justifyContent:"center"}} onClick={()=>setShowTamil(v=>!v)}>
          {showTamil ? "Tamil subtitles: ON" : "Tamil subtitles: OFF"}
        </button>
        <button type="button" className="btn btn-g btn-sm" style={{marginTop:6,width:"100%"}} onClick={onLogout}><Bi en="Sign out"/></button>
      </div>
    </div>
  );
}

function MobileTopBar({ user, onMenu, showHome, onHome }) {
  return (
    <div className="mtop">
      <button
        type="button"
        className="mtop-back"
        onClick={onHome}
        disabled={!showHome}
        aria-label="Home"
        title="Home"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14.7 5.3a1 1 0 010 1.4L10.4 11H21a1 1 0 010 2H10.4l4.3 4.3a1 1 0 11-1.4 1.4l-6-6a1 1 0 010-1.4l6-6a1 1 0 011.4 0z"/>
        </svg>
      </button>
      <img className="brandLogo" src={BRAND_LOGO} alt="Jayfin" />
      <button type="button" className="mtop-btn" onClick={onMenu}>
        <div className="av">{initials(user.name)}</div>
        <div className="mtop-t">{user.role}</div>
      </button>
    </div>
  );
}

function HomePage({ onNav, isAdmin, onOpenSettings }) {
  const sections = isAdmin ? NAV : NAV.filter(s => s.sec === "Overview" || s.sec === "Mobile");
  const items = sections.flatMap(s => s.items);
  const tiles = [
    { k: "dashboard", l: "Dashboard", i: "D" },
    ...(isAdmin ? [{ k: "collectors", l: "Collectors", i: "C" }] : []),
    ...(isAdmin ? [{ k: "loans", l: "Loans", i: "L" }] : []),
    ...(isAdmin ? [{ k: "statement", l: "Statement", i: "T" }] : []),
    { k: "mobile", l: "Collector App", i: "M" },
    { k: "settings", l: "Settings", i: "S" },
  ].filter(t => t.k === "settings" || items.some(x => x.k === t.k));

  return (
    <div className="page">
      <div className="homegrid">
        {tiles.map(t => (
          <button
            key={t.k}
            type="button"
            className="htile"
            onClick={() => (t.k === "settings" ? onOpenSettings() : onNav(t.k))}
          >
            <div className="ht-ico">{t.i}</div>
            <div className="ht-l"><Bi en={t.l} /></div>
          </button>
        ))}
      </div>
    </div>
  );
}

//  ROOT 
function loadStoredAuth() {
  try {
    const t = localStorage.getItem(AUTH_KEY);
    const r = localStorage.getItem(REFRESH_KEY);
    const u = localStorage.getItem(USER_KEY);
    if (t && u) return { token: t, refreshToken: r || null, user: JSON.parse(u) };
  } catch (_) {}
  return null;
}

export default function App() {
  const [auth, setAuth] = useState(loadStoredAuth);
  const [page, setPage] = useState("dashboard");
  const [toasts, setToasts] = useState([]);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showTamil, setShowTamil] = useState(() => {
    try { return localStorage.getItem(TAMIL_KEY) === "1"; } catch (_) { return false; }
  });
  const ui = useMemo(() => ({ showTamil, setShowTamil }), [showTamil]);
  const isMobile = useMediaQuery("(max-width: 900px)");
  const mobileHomeInit = useRef(false);

  useEffect(() => {
    if (!auth) return;
    if (!isMobile) {
      mobileHomeInit.current = false;
      if (page === "home") setPage("dashboard");
      return;
    }
    const isCollector = isCollectorRole(auth.user.role);
    if (mobileHomeInit.current) return;
    mobileHomeInit.current = true;
    if (!isCollector && page === "dashboard") setPage("home");
  }, [auth, isMobile, page]);

  useEffect(() => {
    try { localStorage.setItem(TAMIL_KEY, showTamil ? "1" : "0"); } catch (_) {}
  }, [showTamil]);

  useEffect(() => {
    const onChanged = () => setAuth(loadStoredAuth());
    window.addEventListener("lt:authChanged", onChanged);
    return () => window.removeEventListener("lt:authChanged", onChanged);
  }, []);

  const addToast = useCallback((msg, type="info") => {
    const id = Date.now();
    setToasts(p=>[...p,{id,msg: normalizeMessage(msg),type}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3000);
  }, []);

  useEffect(() => {
    const token = auth?.token;
    if (!token) return;
    let syncing = false;
    const run = async () => {
      if (syncing) return;
      syncing = true;
      try {
        const res = await syncOfflineQueue({ token, apiBase: API_BASE, addToast });
        if (res.synced > 0) {
          addToast(`Synced ${res.synced} offline item(s)`, "success");
          emitDataChanged();
        }
      } finally {
        syncing = false;
      }
    };
    run();
    window.addEventListener("online", run);
    const id = setInterval(() => {
      if (typeof navigator !== "undefined" && navigator.onLine) run();
    }, 15000);
    return () => {
      window.removeEventListener("online", run);
      clearInterval(id);
    };
  }, [auth?.token, addToast]);

  const onLogin = ({ token, refreshToken, user }) => {
    localStorage.setItem(AUTH_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setAuth({ token, refreshToken: refreshToken || auth?.refreshToken || null, user });
    setPage(isCollectorRole(user.role) ? "mobile" : "dashboard");
  };

  const onLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setAuth(null);
  };

  if (!auth) return <><style>{S}</style><LoginPage onLogin={onLogin} apiBase={API_BASE}/></>;

  const isAdmin = isAdminRole(auth.user.role);
  const user = { name: auth.user.name, role: isAdmin ? "Admin - Owner" : "Field Collector" };
  const pages = {
    home: <HomePage onNav={setPage} isAdmin={isAdmin} onOpenSettings={() => setMobileMenu(true)} />,
    dashboard: <Dashboard addToast={addToast} token={auth.token} apiBase={API_BASE} me={auth.user} />,
    collectors: <CollectorsPage addToast={addToast} token={auth.token} apiBase={API_BASE} me={auth.user} />,
    loans: <LoansPage addToast={addToast} token={auth.token} apiBase={API_BASE} me={auth.user} />,
    statement: <StatementPage addToast={addToast} token={auth.token} apiBase={API_BASE} me={auth.user} />,
    // Keep route available (hidden from nav) to avoid linting unused component warnings.
    reports: <ReportsPage addToast={addToast} token={auth.token} apiBase={API_BASE} me={auth.user} />,
    mobile: <MobileView addToast={addToast} onBack={()=>setPage("dashboard")} token={auth.token} apiBase={API_BASE} me={auth.user} meIsAdmin={isAdmin} />,
  };

  return (
    <UIContext.Provider value={ui}>
      <>
        <style>{S}</style>
        <div className="app">
          {!isMobile && <Sidebar active={page} onNav={setPage} user={user} onLogout={onLogout} isAdmin={isAdmin}/>}
          <div className={`main ${isMobile ? "mob" : ""}`}>
            {isMobile && (
              <MobileTopBar
                user={user}
                onMenu={() => setMobileMenu(true)}
                showHome={page !== (isCollectorRole(auth.user.role) ? "mobile" : "home")}
                onHome={() => setPage(isCollectorRole(auth.user.role) ? "mobile" : "home")}
              />
            )}
            {pages[page] || pages.dashboard}
          </div>
        </div>
        {isMobile && mobileMenu && (
          <Modal
            title="Menu"
            onClose={() => setMobileMenu(false)}
            footer={
              <>
                <button className="btn btn-g" onClick={() => setMobileMenu(false)}>Close</button>
                <button className="btn btn-p" onClick={() => { setMobileMenu(false); onLogout(); }}>Sign out</button>
              </>
            }
          >
            <div className="u-pill">
              <div className="av">{initials(user.name)}</div>
              <div style={{flex:1,overflow:"hidden"}}>
                <div className="u-name">{user.name}</div>
                <div className="u-role">{user.role}</div>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-g"
              style={{marginTop:10,width:"100%",justifyContent:"center"}}
              onClick={() => setShowTamil(v => !v)}
            >
              {showTamil ? "Tamil subtitles: ON" : "Tamil subtitles: OFF"}
            </button>
            <div style={{marginTop:10,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                { k: "home", l: "Home" },
                ...(isAdmin ? NAV.flatMap(s => s.items) : NAV.filter(s => s.sec === "Overview" || s.sec === "Mobile").flatMap(s => s.items)),
              ].map(it => (
                <button
                  key={it.k}
                  type="button"
                  className="btn btn-g btn-sm"
                  style={{justifyContent:"center"}}
                  onClick={() => { setMobileMenu(false); setPage(it.k); }}
                >
                  <Bi en={it.l} />
                </button>
              ))}
            </div>
          </Modal>
        )}
        <Toast toasts={toasts}/>
      </>
    </UIContext.Provider>
  );
}


